import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Send, User, Bot, FileText, CheckCircle, AlertCircle, AlertTriangle, Loader, Workflow, Clock, DollarSign, Settings, RefreshCw, Code, Zap, Copy, Download, Database, Table, Plus, Trash2, Edit3, Eye, ArrowRight, ArrowLeft, ArrowDownCircle, ArrowUpCircle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ClipboardList, ClipboardCheck, Sliders, Presentation, Rocket, GitBranch, Square, Moon, Sun, Check, X, Menu, MessageSquare, PanelLeftClose, PanelLeft, Search, Play, Sparkles, BookOpen, Pencil, Fuel, LogOut } from 'lucide-react';
import FlowVisualization from './FlowVisualization';
import DataFlowVisualization from './DataFlowVisualization';
import PrototypeView from './PrototypeView';
import DocumentationView from './DocumentationView';
import MapDataView from './MapDataView';
import PromptsView from './PromptsView';
import QuestionnaireView from './QuestionnaireView';
import WorkflowView from './WorkflowView';
import ExecuteView from './ExecuteView';
import Orb from './reactbits/Orb';
import { API_BASE } from './config';

// Custom scrollbar styles and animations
const customStyles = `
  /* Modern thin scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 10px;
    transition: background 0.2s ease;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.25);
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  /* Firefox scrollbar */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
  }

  @keyframes skeleton-pulse {
    0%, 100% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.8;
    }
  }

  @keyframes fuel-urgent {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @keyframes switchProgress {
    0% { width: 0%; }
    100% { width: 100%; }
  }

  @keyframes executionPulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.8; }
  }

  .skeleton-pulse {
    animation: skeleton-pulse 1.5s ease-in-out infinite;
  }

  @keyframes snap-glow {
    0%, 100% {
      opacity: 0.6;
      box-shadow: 0 0 10px rgba(59, 130, 246, 0.4);
    }
    50% {
      opacity: 1;
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.8);
    }
  }

  .snap-indicator {
    animation: snap-glow 0.8s ease-in-out infinite;
  }

  @keyframes edge-hint-pulse {
    0%, 100% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.8;
    }
  }

  .edge-hint {
    animation: edge-hint-pulse 2s ease-in-out infinite;
  }

  /* Magnetic pull animation - mutual attraction effect */
  @keyframes magnetic-pull {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.005);
    }
  }

  .magnetic-pull {
    animation: magnetic-pull 0.6s ease-in-out infinite;
  }

  /* Architect breathing glow - always present, subtle */
  @keyframes architect-breathing {
    0%, 100% {
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.15), 0 0 40px rgba(14, 165, 233, 0.1);
    }
    50% {
      box-shadow: 0 0 25px rgba(59, 130, 246, 0.25), 0 0 50px rgba(14, 165, 233, 0.15);
    }
  }

  .architect-breathing {
    animation: architect-breathing 4s ease-in-out infinite;
  }

  /* Architect active - intensified breathing when processing */
  @keyframes architect-active {
    0%, 100% {
      box-shadow: 0 0 25px rgba(59, 130, 246, 0.3), 0 0 50px rgba(14, 165, 233, 0.2), 0 0 80px rgba(6, 182, 212, 0.1);
    }
    50% {
      box-shadow: 0 0 35px rgba(59, 130, 246, 0.5), 0 0 70px rgba(14, 165, 233, 0.3), 0 0 100px rgba(6, 182, 212, 0.2);
    }
  }

  .architect-active {
    animation: architect-active 2s ease-in-out infinite;
  }

  /* Node execution glow animation */
  @keyframes node-executing-glow {
    0%, 100% {
      box-shadow: 0 0 8px 2px rgba(6, 182, 212, 0.4), 0 0 16px 4px rgba(6, 182, 212, 0.2);
    }
    50% {
      box-shadow: 0 0 16px 4px rgba(6, 182, 212, 0.7), 0 0 32px 8px rgba(6, 182, 212, 0.4);
    }
  }

  .node-executing {
    animation: node-executing-glow 1.5s ease-in-out infinite;
  }

  /* Execution section glow - pseudo-element border overlay */
  .execution-glow {
    position: relative !important;
  }

  .execution-glow::before,
  .sticky-glow::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    border: 3px solid rgba(6, 182, 212, 0.7);
    box-shadow: inset 0 0 24px 6px rgba(6, 182, 212, 0.15), 0 0 12px 2px rgba(6, 182, 212, 0.25);
    animation: execution-section-glow 2s ease-in-out infinite;
    pointer-events: none;
    z-index: 10;
  }

  @keyframes execution-section-glow {
    0%, 100% {
      border-color: rgba(6, 182, 212, 0.5);
      box-shadow: inset 0 0 20px 4px rgba(6, 182, 212, 0.12), 0 0 8px 2px rgba(6, 182, 212, 0.2);
      opacity: 0.85;
    }
    50% {
      border-color: rgba(6, 182, 212, 1);
      box-shadow: inset 0 0 32px 8px rgba(6, 182, 212, 0.25), 0 0 18px 4px rgba(6, 182, 212, 0.4);
      opacity: 1;
    }
  }

  @keyframes node-completed-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }

  .node-completed-pulse {
    animation: node-completed-pulse 0.6s ease-out;
  }

  /* Flowing gradient for header during processing */
  @keyframes gradient-flow {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  .gradient-flow {
    background: linear-gradient(90deg, #3b82f6, #0ea5e9, #06b6d4, #0ea5e9, #3b82f6);
    background-size: 200% 100%;
    animation: gradient-flow 3s ease-in-out infinite;
  }

  /* Logo shimmer effect */
  @keyframes logo-shimmer {
    0%, 100% {
      filter: brightness(1) drop-shadow(0 0 8px rgba(59, 130, 246, 0.3));
    }
    50% {
      filter: brightness(1.1) drop-shadow(0 0 12px rgba(59, 130, 246, 0.5));
    }
  }

  .logo-shimmer {
    animation: logo-shimmer 4s ease-in-out infinite;
  }

  /* Logo active - faster shimmer when processing */
  @keyframes logo-active {
    0%, 100% {
      filter: brightness(1) drop-shadow(0 0 12px rgba(6, 182, 212, 0.5));
      transform: scale(1);
    }
    50% {
      filter: brightness(1.3) drop-shadow(0 0 25px rgba(6, 182, 212, 0.9));
      transform: scale(1.08);
    }
  }

  .logo-active {
    animation: logo-active 1.2s ease-in-out infinite;
  }

  /* Header flowing liquid effect when thinking */
  @keyframes header-liquid-flow {
    0% {
      background-position: -100% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  /* Aurora + Heartbeat: color shift with pulse */
  @keyframes aurora-heartbeat {
    0% {
      background: linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%);
      filter: brightness(1);
    }
    15% {
      background: linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%);
      filter: brightness(1.3);
    }
    25% {
      background: linear-gradient(135deg, #0284c7 0%, #3b82f6 50%, #60a5fa 100%);
      filter: brightness(1);
    }
    40% {
      background: linear-gradient(135deg, #0284c7 0%, #3b82f6 50%, #60a5fa 100%);
      filter: brightness(1.3);
    }
    50% {
      background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #a78bfa 100%);
      filter: brightness(1);
    }
    65% {
      background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #a78bfa 100%);
      filter: brightness(1.3);
    }
    75% {
      background: linear-gradient(135deg, #0284c7 0%, #3b82f6 50%, #60a5fa 100%);
      filter: brightness(1);
    }
    90% {
      background: linear-gradient(135deg, #0284c7 0%, #3b82f6 50%, #60a5fa 100%);
      filter: brightness(1.3);
    }
    100% {
      background: linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%);
      filter: brightness(1);
    }
  }

  .header-alive {
    animation: aurora-heartbeat 4s ease-in-out infinite !important;
  }

  /* Dark mode aurora */
  @keyframes aurora-heartbeat-dark {
    0% {
      background: rgba(6, 182, 212, 0.3);
      filter: brightness(1);
    }
    15% {
      background: rgba(6, 182, 212, 0.5);
      filter: brightness(1.2);
    }
    25% {
      background: rgba(59, 130, 246, 0.3);
      filter: brightness(1);
    }
    40% {
      background: rgba(59, 130, 246, 0.5);
      filter: brightness(1.2);
    }
    50% {
      background: rgba(139, 92, 246, 0.3);
      filter: brightness(1);
    }
    65% {
      background: rgba(139, 92, 246, 0.5);
      filter: brightness(1.2);
    }
    75% {
      background: rgba(59, 130, 246, 0.3);
      filter: brightness(1);
    }
    90% {
      background: rgba(59, 130, 246, 0.5);
      filter: brightness(1.2);
    }
    100% {
      background: rgba(6, 182, 212, 0.3);
      filter: brightness(1);
    }
  }

  .header-alive-dark {
    animation: aurora-heartbeat-dark 4s ease-in-out infinite !important;
  }

  /* Breadcrumb border flow animation */
  @keyframes border-flow {
    0% {
      background-position: 0% 50%;
    }
    100% {
      background-position: 200% 50%;
    }
  }

  .border-flow {
    background-size: 200% 100%;
    animation: border-flow 2s linear infinite;
  }

  /* Celebration pulse for stage completion */
  @keyframes celebration-pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.7);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 20px 10px rgba(52, 211, 153, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(52, 211, 153, 0);
    }
  }

  .celebration-pulse {
    animation: celebration-pulse 0.6s ease-out;
  }

  /* Status indicator pulse */
  @keyframes status-pulse {
    0%, 100% {
      opacity: 0.8;
      transform: scale(1);
      box-shadow: 0 0 8px rgba(6, 182, 212, 0.3);
    }
    50% {
      opacity: 1;
      transform: scale(1.02);
      box-shadow: 0 0 12px rgba(6, 182, 212, 0.5);
    }
  }

  .status-pulse {
    animation: status-pulse 1.5s ease-in-out infinite;
  }

  /* Custom tooltip styles - theme aware */
  [data-tooltip] {
    position: relative;
  }

  [data-tooltip]::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, visibility 0.15s ease;
    z-index: 1000;
    /* Default dark tooltip styling */
    background: #1f2937 !important;
    color: #f3f4f6 !important;
    border: 1px solid #374151;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  [data-tooltip]:hover::after {
    opacity: 1;
    visibility: visible;
  }

  /* Dark mode tooltip */
  .dark-tooltip[data-tooltip]::after {
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  /* Light mode tooltip */
  .light-tooltip[data-tooltip]::after {
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  /* Tooltip arrow */
  [data-tooltip]::before {
    content: '';
    position: absolute;
    bottom: calc(100% + 2px);
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #1f2937;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, visibility 0.15s ease;
    z-index: 1000;
  }

  [data-tooltip]:hover::before {
    opacity: 1;
    visibility: visible;
  }

  /* Bottom tooltip variant */
  .tooltip-bottom[data-tooltip]::after {
    bottom: auto;
    top: calc(100% + 8px);
  }

  .tooltip-bottom[data-tooltip]::before {
    bottom: auto;
    top: calc(100% + 2px);
    border-top-color: transparent;
    border-bottom-color: #1f2937;
  }

  /* Liquid glass ambient orb animations */
  @keyframes orb-float-1 {
    0%, 100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(30px, -20px) scale(1.05);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.95);
    }
  }

  @keyframes orb-float-2 {
    0%, 100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(-40px, 30px) scale(1.1);
    }
    66% {
      transform: translate(30px, -10px) scale(0.9);
    }
  }

  @keyframes orb-float-3 {
    0%, 100% {
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      transform: translate(-50%, -50%) scale(1.2);
    }
  }

  .orb-1 {
    animation: orb-float-1 20s ease-in-out infinite;
  }

  .orb-2 {
    animation: orb-float-2 25s ease-in-out infinite;
  }

  .orb-3 {
    animation: orb-float-3 15s ease-in-out infinite;
  }

  /* Liquid glass hover effects */
  @keyframes liquid-shimmer {
    0% {
      transform: translateX(-100%) skewX(-15deg);
    }
    100% {
      transform: translateX(200%) skewX(-15deg);
    }
  }

  .liquid-glass {
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
  }

  .liquid-glass::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transform: translateX(-100%) skewX(-15deg);
    transition: none;
    pointer-events: none;
  }

  .liquid-glass:hover::before {
    animation: liquid-shimmer 0.8s ease-out;
  }

  .liquid-glass:hover {
    background: rgba(6, 182, 212, 0.08);
    border-color: rgba(6, 182, 212, 0.2);
    box-shadow:
      0 4px 16px rgba(6, 182, 212, 0.1),
      inset 0 0 8px rgba(6, 182, 212, 0.05);
  }

  /* Chat header specific hover - theme-aware */
  .chat-header-hover-light:hover {
    background: linear-gradient(135deg, #0e7490 0%, #0891b2 50%, #06b6d4 100%) !important;
  }
  .chat-header-hover-light:hover,
  .chat-header-hover-light:hover * {
    color: #fff !important;
  }
  .chat-header-hover-dark:hover {
    background: rgba(6, 182, 212, 0.2);
  }
  .chat-header-hover-dark:hover,
  .chat-header-hover-dark:hover * {
    color: #fff !important;
  }

  /* Liquid glow effect for interactive elements */
  .liquid-glow {
    transition: all 0.3s ease;
  }

  .liquid-glow:hover {
    box-shadow:
      0 0 20px rgba(59, 130, 246, 0.4),
      0 0 40px rgba(59, 130, 246, 0.2),
      inset 0 0 15px rgba(255, 255, 255, 0.1);
    transform: translateY(-1px);
  }

  /* Ripple effect on click */
  @keyframes liquid-ripple {
    0% {
      transform: scale(0);
      opacity: 0.5;
    }
    100% {
      transform: scale(4);
      opacity: 0;
    }
  }

  .liquid-ripple {
    position: relative;
    overflow: hidden;
  }

  .liquid-ripple::after {
    content: '';
    position: absolute;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%);
    border-radius: 50%;
    transform: scale(0);
    pointer-events: none;
  }

  .liquid-ripple:active::after {
    animation: liquid-ripple 0.6s ease-out;
  }

  /* Hover-to-expand for long table cell values */
  .cell-truncatable {
    max-height: 2.8em;
    overflow: hidden;
    position: relative;
    transition: max-height 0.3s ease;
    cursor: pointer;
  }
  .cell-truncatable.cell-expanded {
    max-height: 500px;
  }
  .cell-truncatable .cell-fade {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1.4em;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }
  .cell-truncatable.cell-expanded .cell-fade {
    opacity: 0;
  }

  /* Slide-up entrance for minimized chat bottom bar */
  @keyframes slide-up-bar {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  .slide-up-bar {
    animation: slide-up-bar 0.3s ease-out;
  }

  /* Magical shimmer for "Let Byte handle it" button */
  @keyframes byte-shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes byte-glow-pulse {
    0%, 100% { box-shadow: 0 0 8px rgba(6, 182, 212, 0.3), 0 0 20px rgba(6, 182, 212, 0.1); }
    50% { box-shadow: 0 0 16px rgba(6, 182, 212, 0.5), 0 0 40px rgba(6, 182, 212, 0.2); }
  }
  .byte-magic-btn {
    background: linear-gradient(135deg, #0891b2, #06b6d4, #22d3ee, #06b6d4, #0891b2);
    background-size: 200% 100%;
    animation: byte-shimmer 3s ease-in-out infinite, byte-glow-pulse 2s ease-in-out infinite;
    color: white;
    font-weight: 600;
    border: none;
    position: relative;
    overflow: hidden;
  }
  .byte-magic-btn::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255,255,255,0.15) 50%,
      transparent 70%
    );
    animation: byte-shimmer 3s ease-in-out infinite;
  }
  .byte-magic-btn:hover {
    transform: scale(1.03);
    box-shadow: 0 0 24px rgba(6, 182, 212, 0.6), 0 0 48px rgba(6, 182, 212, 0.3) !important;
  }
  .byte-magic-btn:disabled {
    animation: none;
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none !important;
    transform: none !important;
  }

  /* Screen transition countdown */
  @keyframes countdown-pop {
    0% { transform: scale(0.5); opacity: 0; }
    40% { transform: scale(1.15); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes countdown-ring {
    0% { stroke-dashoffset: 283; }
    100% { stroke-dashoffset: 0; }
  }

  /* Map-data skeleton animation */
  @keyframes mapdata-pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  .mapdata-skeleton-bar {
    animation: mapdata-pulse 1.5s ease-in-out infinite;
  }
  @keyframes testdata-highlight {
    0% { box-shadow: none; }
    20% { box-shadow: 0 0 0 2px rgba(6,182,212,0.5); }
    80% { box-shadow: 0 0 0 2px rgba(6,182,212,0.5); }
    100% { box-shadow: none; }
  }
  .testdata-highlight {
    animation: testdata-highlight 1.5s ease-in-out;
  }
`;

// Skeleton component for questionnaire loading
const QuestionnaireSkeleton = ({ thinkingMessage, isDarkMode }) => (
  <div className="space-y-6 p-6">
    {/* Thinking message banner - sticky */}
    {thinkingMessage && (
      <div className={`sticky top-0 z-10 rounded-lg p-3 flex items-center space-x-3 backdrop-blur-sm shadow-lg ${isDarkMode
        ? 'bg-gradient-to-r from-cyan-900/80 to-blue-900/80 border border-cyan-700'
        : 'bg-[#0077A8] border border-[#006090]'
        }`}>
        <div className={`w-2 h-2 rounded-full animate-pulse ${isDarkMode ? 'bg-cyan-400' : 'bg-white'}`}></div>
        <span className={`text-sm font-medium ${isDarkMode ? 'text-cyan-300' : 'text-white'}`}>{thinkingMessage}</span>
      </div>
    )}

    {/* Header skeleton */}
    <div className="space-y-2">
      <div className={`h-7 w-64 rounded skeleton-pulse ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}></div>
      <div className={`h-4 w-96 rounded skeleton-pulse ${isDarkMode ? 'bg-gray-700/60' : 'bg-gray-100/60'}`} style={{ animationDelay: '0.1s' }}></div>
    </div>

    {/* Question card skeletons */}
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className={`rounded-lg p-4 space-y-3 ${isDarkMode ? 'border border-gray-700' : 'border border-gray-200'}`} style={{ animationDelay: `${i * 0.15}s` }}>
        <div className="flex items-center space-x-2">
          <div className="h-5 w-5 bg-[#0077A8]/40 rounded skeleton-pulse"></div>
          <div className={`h-5 w-3/4 rounded skeleton-pulse ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}></div>
        </div>
        <div className={`h-10 w-full rounded-md skeleton-pulse ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-300'}`}></div>
        <div className={`h-3 w-1/3 rounded skeleton-pulse ${isDarkMode ? 'bg-gray-700/40' : 'bg-gray-100/40'}`}></div>
      </div>
    ))}

    {/* Submit button skeleton */}
    <div className={`pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      <div className="h-10 w-full bg-[#0077A8]/30 rounded-md skeleton-pulse"></div>
    </div>
  </div>
);

// Skeleton component for workflow/decomposition loading
const WorkflowSkeleton = ({ thinkingMessage, isDarkMode }) => (
  <div className="space-y-6 p-6">
    {/* Thinking message banner - sticky */}
    {thinkingMessage && (
      <div className={`sticky top-0 z-10 rounded-lg p-3 flex items-center space-x-3 backdrop-blur-sm shadow-lg ${isDarkMode
        ? 'bg-gradient-to-r from-cyan-900/80 to-blue-900/80 border border-cyan-700'
        : 'bg-[#0077A8] border border-[#006090]'
        }`}>
        <div className={`w-2 h-2 rounded-full animate-pulse ${isDarkMode ? 'bg-cyan-400' : 'bg-white'}`}></div>
        <span className={`text-sm font-medium ${isDarkMode ? 'text-cyan-300' : 'text-white'}`}>{thinkingMessage}</span>
      </div>
    )}

    {/* Summary card skeleton */}
    <div className={`rounded-lg p-4 space-y-3 ${isDarkMode ? 'border border-cyan-800 bg-cyan-900/30' : 'border border-cyan-200 bg-cyan-50'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="h-5 w-5 bg-cyan-600/40 rounded skeleton-pulse"></div>
          <div className={`h-5 w-48 rounded skeleton-pulse ${isDarkMode ? 'bg-cyan-600/40' : 'bg-cyan-700/40'}`}></div>
        </div>
        <div className="flex space-x-4">
          <div className={`h-4 w-20 rounded skeleton-pulse ${isDarkMode ? 'bg-cyan-600/30' : 'bg-cyan-700/30'}`}></div>
          <div className={`h-4 w-20 rounded skeleton-pulse ${isDarkMode ? 'bg-cyan-600/30' : 'bg-cyan-700/30'}`}></div>
        </div>
      </div>
      <div className={`h-4 w-full rounded skeleton-pulse ${isDarkMode ? 'bg-cyan-600/20' : 'bg-cyan-700/20'}`} style={{ animationDelay: '0.1s' }}></div>
      <div className={`h-4 w-2/3 rounded skeleton-pulse ${isDarkMode ? 'bg-cyan-600/20' : 'bg-cyan-700/20'}`} style={{ animationDelay: '0.2s' }}></div>
    </div>

    {/* Flow visualization skeleton */}
    <div className={`rounded-lg p-4 ${isDarkMode ? 'border border-gray-700' : 'border border-gray-200'}`}>
      <div className="flex items-center space-x-2 mb-4">
        <div className="h-5 w-5 bg-[#0077A8]/40 rounded skeleton-pulse"></div>
        <div className={`h-5 w-40 rounded skeleton-pulse ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}></div>
      </div>

      {/* Flow nodes skeleton */}
      <div className="flex items-center justify-center space-x-4 py-6">
        {[1, 2, 3, 4].map((i) => (
          <React.Fragment key={i}>
            <div
              className="flex flex-col items-center space-y-2"
              style={{ animationDelay: `${i * 0.2}s` }}
            >
              <div className={`w-32 h-24 border-2 border-blue-500/30 rounded-xl skeleton-pulse flex flex-col items-center justify-center p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="w-6 h-6 bg-[#0077A8]/40 rounded-full mb-2 skeleton-pulse"></div>
                <div className={`h-3 w-20 rounded skeleton-pulse ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}></div>
                <div className={`h-2 w-16 rounded mt-1 skeleton-pulse ${isDarkMode ? 'bg-gray-700/60' : 'bg-gray-100/60'}`}></div>
              </div>
            </div>
            {i < 4 && (
              <div className="h-1 w-6 bg-blue-500/30 rounded skeleton-pulse"></div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>

    {/* Node details skeleton */}
    <div className={`rounded-lg p-4 space-y-3 ${isDarkMode ? 'border border-gray-700' : 'border border-gray-200'}`}>
      <div className={`h-5 w-32 rounded skeleton-pulse ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className={`h-4 w-24 rounded skeleton-pulse ${isDarkMode ? 'bg-gray-700/60' : 'bg-gray-100/60'}`}></div>
          <div className={`h-4 w-full rounded skeleton-pulse ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}></div>
        </div>
        <div className="space-y-2">
          <div className={`h-4 w-24 rounded skeleton-pulse ${isDarkMode ? 'bg-gray-700/60' : 'bg-gray-100/60'}`}></div>
          <div className={`h-4 w-full rounded skeleton-pulse ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}></div>
        </div>
      </div>
    </div>
  </div>
);

// Random message variants for user actions
const UI_MESSAGES = {
  round1_submit: [
    "Here are my answers",
    "Submitting my responses",
    "Answers ready",
    "Here's the information you asked for",
    "My responses to your questions"
  ],
  decomposition_request: [
    "Ready to generate the AI agent",
    "Let's create the AI agent now",
    "Generate the AI agent with this context",
    "Build the agent based on my requirements"
  ],
  map_variables_start: [
    "Mapping variables and data flow...",
    "Analyzing data connections between nodes...",
    "Setting up variable mappings...",
    "Configuring data flow for your agent..."
  ],
  map_variables_complete: [
    "Variable mapping complete! Your agent's data flow is now configured.",
    "All set! Variables have been mapped between nodes.",
    "Data flow configured successfully!",
    "Variable mappings are ready. Your agent knows how data flows between steps."
  ]
};

const getRandomMessage = (type) => {
  const messages = UI_MESSAGES[type] || [type];
  return messages[Math.floor(Math.random() * messages.length)];
};

// Read URL params on initial render
function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    session: params.get('session'),
    agent: params.get('agent'),
    view: params.get('view'),
    mode: params.get('mode'),
    sid: params.get('sid'),
    sidebar: params.get('sidebar'),
  };
}

// Update URL without page reload
function updateUrl(params) {
  const url = new URL(window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
    else url.searchParams.delete(key);
  });
  window.history.replaceState({}, '', url.toString());
}

const ContextGeneratorUI = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: 'Hey! I\'m Byte, your AI Architect. I\'ll guide you through building your perfect AI solution. Tell me what problem you\'re trying to solve, and let\'s create something great together!',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzingQuestionnaire, setIsAnalyzingQuestionnaire] = useState(false);
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState(null);
  const [formData, setFormData] = useState({});
  const [otherModeFields, setOtherModeFields] = useState({}); // Track fields where "Other" is selected
  const urlParams = getUrlParams();
  const [sessionId, setSessionId] = useState(urlParams.session || null);
  const [currentRound, setCurrentRound] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [workflowResult, setWorkflowResult] = useState(null);
  const [currentView, setCurrentView] = useState(urlParams.view || 'guide');
  const isShareMode = urlParams.mode === 'share';

  // Store questionnaire and form data for navigation
  const [round1Questionnaire, setRound1Questionnaire] = useState(null);
  const [round1FormData, setRound1FormData] = useState({});
  const [llmProvider, setLlmProvider] = useState('openai');
  const [generatedPrompts, setGeneratedPrompts] = useState(null);
  const [isGeneratingPrompts, setIsGeneratingPrompts] = useState(false);
  const [generatedSchema, setGeneratedSchema] = useState(null);
  const [generatedData, setGeneratedData] = useState(null);
  const [isGeneratingData, setIsGeneratingData] = useState(false);

  const [previewData, setPreviewData] = useState(null);
  const [editedSamples, setEditedSamples] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewStep, setPreviewStep] = useState(0);
  const [learnedPatterns, setLearnedPatterns] = useState(null);

  const [dataGenerationSettings, setDataGenerationSettings] = useState({
    numRows: 10,
    numSamples: 3,
    purpose: null
  });

  // Confirmation dialog for going back to questionnaire
  const [showBackConfirmDialog, setShowBackConfirmDialog] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isViewingAnswers, setIsViewingAnswers] = useState(false); // True when viewing answers without edits
  const [originalFormData, setOriginalFormData] = useState({}); // To track if form was edited

  // Low clarity mode - when user needs to provide more details
  const [lowClarityMode, setLowClarityMode] = useState(false); // false | true | 'capped'
  const [clarificationRound, setClarificationRound] = useState(0); // tracks how many times clarity was insufficient
  const [pendingBuildMessage, setPendingBuildMessage] = useState(null); // Holds message for "build agent?" CTA
  const [missingContextItems, setMissingContextItems] = useState([]);
  const [additionalDetails, setAdditionalDetails] = useState('');

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  const [insufficientCreditsDialog, setInsufficientCreditsDialog] = useState(false);

  // Flow tabs state - 3 ranked flows
  const [currentFlowIndex, setCurrentFlowIndex] = useState(0);
  const [currentNodeIndex, setCurrentNodeIndex] = useState(0);
  const [selectedPromptNodeIndex, setSelectedPromptNodeIndex] = useState(0);
  const [isPromptFlowExpanded, setIsPromptFlowExpanded] = useState(false);
  const [isPromptFlowHovered, setIsPromptFlowHovered] = useState(false);
  const [selectedSampleIndex, setSelectedSampleIndex] = useState(0);

  // Prompt testing state - stored per node to persist when switching tabs
  const [promptTestResults, setPromptTestResults] = useState({}); // { [nodeIndex]: { input, output } }
  const [isRunningPromptTest, setIsRunningPromptTest] = useState(false);
  const [requestJsonOutput, setRequestJsonOutput] = useState(false); // Toggle for JSON output format

  // Execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [executionStatus, setExecutionStatus] = useState(null); // 'running' | 'completed' | 'failed'
  const [executionNodes, setExecutionNodes] = useState([]); // Array of node execution states
  const executionNodesRef = useRef([]); // Ref mirror for async access
  const [currentExecutingNodeIndex, setCurrentExecutingNodeIndex] = useState(-1);
  const [selectedExecutionSample, setSelectedExecutionSample] = useState(0);
  const [executionDisplayMode, setExecutionDisplayMode] = useState('table'); // 'table' | 'json'
  const [executionCarouselIndex, setExecutionCarouselIndex] = useState(0); // Current node in carousel
  const [executionDetailsExpanded, setExecutionDetailsExpanded] = useState(true); // Show/hide details section
  const [copiedSection, setCopiedSection] = useState(null); // Track which section was copied
  const [switchingToNode, setSwitchingToNode] = useState(null); // Badge: "Switching to Node X..."
  const [totalExecutionNodes, setTotalExecutionNodes] = useState(0); // Total nodes in pipeline
  const [executionSidebarOpen, setExecutionSidebarOpen] = useState(false);
  const [executionHistoryList, setExecutionHistoryList] = useState([]);
  const [loadingExecutionHistory, setLoadingExecutionHistory] = useState(false);
  const [viewingHistoryRun, setViewingHistoryRun] = useState(null); // execution_id of loaded past run

  // Decomposition streaming state
  const [thinkingMessage, setThinkingMessage] = useState(null);
  const [isDecomposing, setIsDecomposing] = useState(false);

  // Journey progress state for tracking stages across services
  const [journeyProgress, setJourneyProgress] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [agentDefinition, setAgentDefinition] = useState(null); // Full agent definition with schemas
  const [prototypeConfig, setPrototypeConfig] = useState(null); // Lifted prototype config for orchestrator
  const prototypeConfigRef = useRef(null); // Ref mirror for async access
  const [prototypeConfigUpdate, setPrototypeConfigUpdate] = useState(null); // Chat-driven config updates (with timestamp)
  const [protoAutoExecute, setProtoAutoExecute] = useState(false); // Auto-pilot: auto-run prototype
  const protoAutoExecuteResolveRef = useRef(null); // Resolve function for auto-execute promise
  const sessionEpochRef = useRef(0); // Incremented on reset to invalidate stale async callbacks
  const prevViewRef = useRef(null); // Track previous view for chat dividers
  const [isAutoPilot, setIsAutoPilot] = useState(false);
  const [autoPilotStep, setAutoPilotStep] = useState(0);
  const [autoPilotTotal] = useState(7);
  const takeOverRef = useRef(null);
  const [isAutoPilotPaused, setIsAutoPilotPaused] = useState(false);
  const isAutoPilotPausedRef = useRef(false);
  const autoPilotResumeRef = useRef(null); // holds resolve() to unpause
  const [autoPilotCountdownValue, setAutoPilotCountdownValue] = useState(null); // 3, 2, 1, null
  const [autoPilotNextLabel, setAutoPilotNextLabel] = useState('');
  const [showHandbackBanner, setShowHandbackBanner] = useState(false);
  const [isSelectingFlow, setIsSelectingFlow] = useState(false);
  const [isExportingCode, setIsExportingCode] = useState(false);
  const [exportProgress, setExportProgress] = useState(null); // {step, message}

  // Jira integration state
  const [jiraConnected, setJiraConnected] = useState(false);
  const [jiraSiteName, setJiraSiteName] = useState('');
  const [jiraConsolidation, setJiraConsolidation] = useState(null); // {ticket_key, title, requirements[], gaps[]}
  const [jiraChecklist, setJiraChecklist] = useState({}); // {req_id: true/false}
  const [jiraGapAnswers, setJiraGapAnswers] = useState({}); // {question: answer}
  const [jiraFlowStep, setJiraFlowStep] = useState(null); // 'consolidating' | 'checklist' | 'gaps' | 'building' | null
  const [isJiraProcessing, setIsJiraProcessing] = useState(false);
  const [jiraTicketKey, setJiraTicketKey] = useState(null);
  const [jiraTicketTitle, setJiraTicketTitle] = useState(null);
  const [jiraPostStatus, setJiraPostStatus] = useState(null); // null | 'posting' | 'posted' | 'error'
  const [jiraPostSummary, setJiraPostSummary] = useState('');
  const [jiraPostRequirements, setJiraPostRequirements] = useState([]);
  // userId removed — use userId directly (Cognito sub ID) for Jira APIs

  // Accordion state for workflow view
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [flowExpanded, setFlowExpanded] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [variableDictExpanded, setVariableDictExpanded] = useState(false);
  const [hoveredVarExample, setHoveredVarExample] = useState(null);
  const [mapDataSubTab, setMapDataSubTab] = useState('flow'); // 'flow' | 'test-data'
  const [shouldScrollToTestData, setShouldScrollToTestData] = useState(false);
  const [expandedTestDataNodes, setExpandedTestDataNodes] = useState({});

  // Flow editing state - edit nodes before finalization
  const [isEditingFlow, setIsEditingFlow] = useState(false);
  const [editedNodes, setEditedNodes] = useState(null); // null = no edits, array = edited copy
  const [editingNodeIndex, setEditingNodeIndex] = useState(null); // which node's card is open
  const [nodeValidations, setNodeValidations] = useState({}); // {index: {valid, explanation, suggestion}}
  const [isValidatingNode, setIsValidatingNode] = useState(false);
  const [draftNodeValues, setDraftNodeValues] = useState(null); // {alias, description} — draft for currently editing node, survives EditCard remounts

  // Detail tabs state (Node Details, Data, Prompts, Input/Output)
  const [activeDetailTab, setActiveDetailTab] = useState('node-details');

  // Floating chat state with snap-to-dock
  const [chatOpen, setChatOpen] = useState(true);
  const [chatDocked, setChatDocked] = useState('right'); // null = floating, 'left' or 'right' = docked (default: docked right)
  const [chatPosition, setChatPosition] = useState({ x: 20, y: 80 });
  const [chatSize, setChatSize] = useState({ width: 380, height: 480 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [nearEdge, setNearEdge] = useState(null); // 'left', 'right', or null - for visual feedback
  const [prevMessageCount, setPrevMessageCount] = useState(1);
  const [chatHovered, setChatHovered] = useState(false);
  const [chatMinimizedToBottom, setChatMinimizedToBottom] = useState(false);
  const [preMiniDockState, setPreMiniDockState] = useState(null); // remembers dock position before minimize
  const [hoveredNavItem, setHoveredNavItem] = useState(null); // For tooltip

  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true; // Default to dark mode
  });

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [agentsList, setAgentsList] = useState([]);
  const [sessionsList, setSessionsList] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [editingAgentName, setEditingAgentName] = useState('');
  const [sharedPrototypesList, setSharedPrototypesList] = useState([]);
  const [loadingSharedPrototypes, setLoadingSharedPrototypes] = useState(false);

  // Billing state
  const [flowsRemaining, setFlowsRemaining] = useState(null);
  const [balancePct, setBalancePct] = useState(null); // balance / lifetimePurchased
  const [currentPack, setCurrentPack] = useState(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      // Clear user-specific localStorage
      if (userId) {
        localStorage.removeItem(`bytesized_session_state__${userId}`);
      }
      localStorage.removeItem('bytesized_jira_user_id');
      localStorage.removeItem('chatState');

      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const messagesEndRef = useRef(null);
  const contentContainerRef = useRef(null);
  const chatRef = useRef(null);
  const abortControllerRef = useRef(null);
  const switchTimerRef = useRef(null);
  const executionCarouselIndexRef = useRef(0);
  const saveTimerRef = useRef(null);
  const serverSaveTimerRef = useRef(null);

  // Refs for flow tab pill animation
  const flowTabContainerRef = useRef(null);
  const flowTabRefs = useRef({});
  const [flowPillStyle, setFlowPillStyle] = useState({ left: 0, width: 0, opacity: 0 });

  // Refs for detail tab pill animation
  const detailTabContainerRef = useRef(null);
  const detailTabRefs = useRef({});
  const [detailPillStyle, setDetailPillStyle] = useState({ left: 0, width: 0, opacity: 0 });

  // Constants for snap behavior
  const SNAP_THRESHOLD = 60; // pixels from edge to trigger snap
  const DOCKED_WIDTH_PERCENT = 30; // percentage of screen when docked
  const MIN_CHAT_WIDTH = 320;
  const MAX_CHAT_WIDTH = 600;
  const MIN_CHAT_HEIGHT = 400;
  const MAX_CHAT_HEIGHT = 700;

  const CONTEXT_API = `${API_BASE}/v1/user_requirement_agent`;
  const DECOMPOSITION_API = `${API_BASE}/v1/flowdecomposition`;
  const PROMPT_GENERATION_API = `${API_BASE}/v1/prompt_generation`;
  const EXECUTION_API = `${API_BASE}/v1/execution`;
  const CHAT_ORCHESTRATOR_API = `${API_BASE}/v1/chat`;
  const PROTOTYPE_API = `${API_BASE}/v1/prototype`;
  const DATA_GENERATION_API = `${API_BASE}/v2/testdata`;
  const JIRA_API = `${API_BASE}/v1/jira`;
  const BILLING_API = `${API_BASE}/v1/billing`;

  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const getSession = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        if (tokens?.idToken) {
          const idToken = tokens.idToken.toString();
          setToken(idToken);
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          setUserId(payload.email || payload.sub);
        } else {
          navigate('/login');
        }
      } catch (err) {
        console.error('Error fetching auth session:', err);
        navigate('/login');
      }
    };
    getSession();
  }, [navigate]);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Inject custom styles (theme-aware scrollbars)
  useEffect(() => {
    const styleElement = document.createElement('style');
    const themeScrollbarStyles = isDarkMode ? `
      ::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.15); }
      ::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.3); }
      * { scrollbar-color: rgba(255, 255, 255, 0.15) transparent; }
    ` : `
      ::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.15); }
      ::-webkit-scrollbar-thumb:hover { background: rgba(0, 0, 0, 0.25); }
      * { scrollbar-color: rgba(0, 0, 0, 0.15) transparent; }
    `;
    styleElement.textContent = customStyles + themeScrollbarStyles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [isDarkMode]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check Jira connection status on mount + listen for OAuth popup callback
  useEffect(() => {
    if (!token || !userId) return;
    fetch(`${JIRA_API}/status?user_id=${userId}`, {
      headers: { Authorization: token },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.connected) {
          setJiraConnected(true);
          setJiraSiteName(data.siteName || '');
        }
      })
      .catch(() => { });

    const handleJiraMessage = (e) => {
      if (e.data?.type === 'jira-oauth-success') {
        setJiraConnected(true);
        addMessage('bot', `Jira connected! You can now paste Jira ticket links (e.g. PROJ-123) and I'll pull in the details.`, true);
        fetch(`${JIRA_API}/status?user_id=${userId}`, {
          headers: { Authorization: token },
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data?.siteName) setJiraSiteName(data.siteName); })
          .catch(() => { });
      }
    };
    window.addEventListener('message', handleJiraMessage);
    return () => window.removeEventListener('message', handleJiraMessage);
  }, [token, userId]);

  // Refresh billing balance
  const refreshBalance = () => {
    if (!token || !userId) return;
    fetch(`${BILLING_API}/balance?user_id=${userId}`, {
      headers: { Authorization: token },
    })
      .then(r => r.json())
      .then(data => {
        setFlowsRemaining(Math.floor(data.balance / 50));
        const purchased = data.lifetimePurchased || 1;
        setBalancePct(data.balance / purchased);
        setCurrentPack(data.currentPack || 'free');
      })
      .catch(() => {});
  };

  // Refresh billing balance on auth and after every view change (signals action completed)
  useEffect(() => {
    refreshBalance();
  }, [token, userId, currentView]);

  // Handle insufficient credits — show popup + reset all loading states
  const handleInsufficientCredits = () => {
    refreshBalance();
    setInsufficientCreditsDialog(true);
    setIsLoading(false);
    setIsDecomposing(false);
    setIsExecuting(false);
    setIsGeneratingPrompts(false);
    setIsGeneratingData(false);
    setThinkingMessage(null);
  };

  // Preflight credit check for EventSource callers (can't detect 402 from EventSource)
  const checkCreditsAvailable = async (action) => {
    try {
      const res = await fetch(`${BILLING_API}/balance?user_id=${userId}`, {
        headers: { Authorization: token },
      });
      if (!res.ok) return true; // fail open
      const data = await res.json();
      const costs = { execute: 3, code_export: 5 };
      return data.balance >= (costs[action] || 1);
    } catch {
      return true; // fail open
    }
  };

  // --- Session state persistence (localStorage + server) ---
  const VALID_AGENT_VIEWS = ['workflow', 'map-data', 'prompts', 'execute', 'prototype'];
  const SESSION_STATE_MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 days

  const saveSessionStateLocal = (aId, sId, view) => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        if (!userId) return;
        localStorage.setItem(`bytesized_session_state__${userId}`, JSON.stringify({
          agentId: aId ?? null,
          sessionId: sId ?? null,
          currentView: view ?? 'guide',
          updatedAt: Date.now(),
        }));
      } catch (e) { /* non-critical */ }
    }, 500);
  };

  const saveSessionStateServer = (aId, sId, view) => {
    clearTimeout(serverSaveTimerRef.current);
    serverSaveTimerRef.current = setTimeout(() => {
      if (!userId || !token) return;
      fetch(`${DECOMPOSITION_API}/user-state`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ userId, agentId: aId ?? null, sessionId: sId ?? null, currentView: view ?? 'guide' }),
      }).catch(() => { /* fire-and-forget */ });
    }, 2000);
  };

  const loadSessionStateLocal = () => {
    try {
      if (!userId) return null;
      const raw = localStorage.getItem(`bytesized_session_state__${userId}`);
      if (!raw) return null;
      const state = JSON.parse(raw);
      if (state.updatedAt && Date.now() - state.updatedAt > SESSION_STATE_MAX_AGE) return null;
      return state;
    } catch { return null; }
  };

  const restoreFromState = async (state) => {
    if (state.agentId) {
      // URL ?view= param takes priority (e.g. returning from billing)
      const urlView = new URLSearchParams(window.location.search).get('view');
      const targetView = (urlView && VALID_AGENT_VIEWS.includes(urlView))
        ? urlView
        : (state.currentView && VALID_AGENT_VIEWS.includes(state.currentView))
          ? state.currentView
          : null;
      const success = await loadAgent(state.agentId, targetView);
      if (success) {
        return true;
      }
      // Agent gone — clear stale localStorage
      try { localStorage.removeItem(`bytesized_session_state__${userId}`); } catch {}
    }
    if (state.sessionId) {
      try {
        await loadExistingSession(state.sessionId);
        return true;
      } catch { /* session may be gone */ }
    }
    return false;
  };

  const restoreFromServer = async () => {
    try {
      const res = await fetch(`${DECOMPOSITION_API}/user-state?user_id=${userId}`, {
        headers: { Authorization: token },
      });
      if (!res.ok) return false;
      const state = await res.json();
      if (!state.agentId && !state.sessionId) return false;
      // Cache server state to localStorage
      try {
        localStorage.setItem(`bytesized_session_state__${userId}`, JSON.stringify({
          agentId: state.agentId,
          sessionId: state.sessionId,
          currentView: state.currentView,
          updatedAt: Date.now(),
        }));
      } catch {}
      return await restoreFromState(state);
    } catch { return false; }
  };

  // Restore state on mount: URL params > localStorage > server > guide
  useEffect(() => {
    if (!userId || !token) return;
    if (isShareMode) return;
    const { agent, session } = getUrlParams();

    // URL params take highest priority
    if (agent) { loadAgent(agent); return; }
    if (session) { loadExistingSession(session); return; }

    // Try localStorage (instant, same-device)
    const localState = loadSessionStateLocal();
    if (localState && (localState.agentId || localState.sessionId)) {
      restoreFromState(localState);
      return;
    }

    // Try server (cross-device)
    restoreFromServer();
  }, [userId, token]);

  // Keep refs in sync for async access in auto-pilot
  useEffect(() => { executionNodesRef.current = executionNodes; }, [executionNodes]);
  useEffect(() => { prototypeConfigRef.current = prototypeConfig; }, [prototypeConfig]);

  // Sync state → URL whenever key state changes (skip in share mode — preserve share URL)
  useEffect(() => {
    if (isShareMode) return;
    updateUrl({
      session: sessionId,
      agent: agentId,
      view: currentView,
    });
    // Persist session state to localStorage (500ms debounce) and server (2s debounce)
    saveSessionStateLocal(agentId, sessionId, currentView);
    saveSessionStateServer(agentId, sessionId, currentView);
  }, [sessionId, agentId, currentView]);

  // Send screen_change event to chat orchestrator for contextual greetings
  useEffect(() => {
    // Inject tab divider in chat when view changes (skip initial mount)
    if (prevViewRef.current !== null && prevViewRef.current !== currentView) {
      const viewLabels = {
        guide: 'Getting Started', questionnaire: 'Questionnaire', workflow: 'Plan',
        'map-data': 'Map Data', prompts: 'Prompts', execute: 'Execute', prototype: 'Prototype',
      };
      const label = viewLabels[currentView] || currentView;
      setMessages(prev => [...prev, { id: `divider-${Date.now()}`, type: 'divider', content: label, timestamp: new Date() }]);
    }
    prevViewRef.current = currentView;

    if (!sessionId || currentView === 'questionnaire') return;

    const controller = new AbortController();
    fetch(`${CHAT_ORCHESTRATOR_API}/message?user_id=${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': token },
      body: JSON.stringify({
        event: 'screen_change',
        screen: currentView,
        sessionId: sessionId,
        agentId: agentId || undefined,
        message: '',
      }),
      signal: controller.signal,
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.reply) {
          addMessage('bot', data.reply, true); // skipSave for system greetings
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') console.error('[ScreenChange] Error:', err);
      });

    return () => controller.abort();
  }, [currentView]);

  // Send state_change event to chat orchestrator for running commentary (fire-and-forget)
  const sendStateChange = (type, summary, fallbackMessage) => {
    const epoch = sessionEpochRef.current; // Capture current epoch
    fetch(`${CHAT_ORCHESTRATOR_API}/message?user_id=${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': token },
      body: JSON.stringify({
        event: 'state_change',
        screen: currentView,
        sessionId: sessionId || 'anonymous',
        agentId: agentId || undefined,
        stateChange: { type, summary },
        message: '',
      }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (epoch !== sessionEpochRef.current) return; // Session was reset — discard
        if (data?.reply) {
          addMessage('bot', data.reply, true);
        } else if (fallbackMessage) {
          addMessage('bot', fallbackMessage, true);
        }
      })
      .catch(() => {
        if (epoch !== sessionEpochRef.current) return;
        if (fallbackMessage) addMessage('bot', fallbackMessage, true);
      });
  };

  // Async version — awaitable, used by auto-pilot for blocking analysis
  const awaitByteAnalysis = async (type, summary, timeoutMs = 15000) => {
    const epoch = sessionEpochRef.current;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(`${CHAT_ORCHESTRATOR_API}/message?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token },
        body: JSON.stringify({
          event: 'state_change',
          screen: currentView,
          sessionId: sessionId || 'anonymous',
          agentId: agentId || undefined,
          stateChange: { type, summary },
          message: '',
        }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (epoch !== sessionEpochRef.current) return null; // Session was reset
      if (res.ok) {
        const data = await res.json();
        if (data?.reply) {
          addMessage('bot', data.reply, true);
          return data.reply;
        }
      }
    } catch (err) {
      console.warn('[ByteAnalysis] Timed out or failed:', err.message);
    }
    return null;
  };

  // Build instant client-side execution summary from executionNodes
  const buildExecutionSummary = (nodes) => {
    if (!nodes || nodes.length === 0) return null;
    const completed = nodes.filter(n => n?.status === 'completed');
    const failed = nodes.filter(n => n?.status === 'failed');
    const green = completed.filter(n => n?.validation?.tier === 'green');
    const amber = completed.filter(n => n?.validation?.tier === 'amber');
    const red = completed.filter(n => n?.validation?.tier === 'red');
    const corrected = nodes.filter(n => n?.brainwaveCorrected);
    const total = nodes.length;

    let msg = `Execution done — ${completed.length}/${total} nodes completed`;
    if (failed.length > 0) msg += `, ${failed.length} failed`;
    msg += '.';
    const parts = [];
    if (green.length > 0) parts.push(`${green.length} green`);
    if (amber.length > 0) parts.push(`${amber.length} amber`);
    if (red.length > 0) parts.push(`${red.length} red`);
    if (parts.length > 0) msg += ` Quality: ${parts.join(', ')}.`;
    if (corrected.length > 0) msg += ` Brainwave auto-corrected ${corrected.length} node${corrected.length > 1 ? 's' : ''}.`;
    return msg;
  };

  // Build instant client-side prototype summary from config
  const buildPrototypeSummary = (config) => {
    if (!config) return null;
    const fieldCount = config.inputFields?.length || 0;
    const theme = config.theme || 'light';
    const name = config.appName || 'AI Agent';
    return `Prototype "${name}" ready — ${fieldCount} input field${fieldCount !== 1 ? 's' : ''}, ${theme} theme, submit button: "${config.submitLabel || 'Run'}".`;
  };

  // Build rich context string for LLM analysis
  const buildExecutionContext = (nodes) => {
    if (!nodes || nodes.length === 0) return '';
    return nodes.map((n, i) => {
      if (!n) return `Node ${i + 1}: not executed`;
      const score = n.validation ? `${n.validation.totalScore}/100 (${n.validation.tier})` : 'no score';
      const corrected = n.brainwaveCorrected ? ' [Brainwave corrected]' : '';
      const duration = n.durationMs ? ` in ${(n.durationMs / 1000).toFixed(1)}s` : '';
      return `${n.alias || `Node ${i + 1}`} (${n.nodeType}): ${n.status}${duration}, quality ${score}${corrected}`;
    }).join('\n');
  };

  // Fetch questionnaire from API when navigating to questionnaire view without data
  useEffect(() => {
    if (currentView === 'questionnaire' && !currentQuestionnaire && sessionId) {
      fetch(`${CONTEXT_API}/questions/session/${sessionId}`, {
        headers: { 'Authorization': token }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.round1_questions?.input?.length > 0) {
            setCurrentQuestionnaire(data.round1_questions);
            setRound1Questionnaire(data.round1_questions);
            setCurrentRound(1);
            if (data.round1_responses && Object.keys(data.round1_responses).length > 0) {
              setFormData(data.round1_responses);
              setRound1FormData(data.round1_responses);
            }
          }
        })
        .catch(() => { });
    }
  }, [currentView, currentQuestionnaire, sessionId]);

  // Fetch prompts from API when navigating to prompts view without data
  useEffect(() => {
    if (currentView === 'prompts' && !generatedPrompts && agentId && token) {
      fetch(`${PROMPT_GENERATION_API}/agent/${agentId}/prompts`, {
        headers: { Authorization: token },
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setGeneratedPrompts(data); })
        .catch(() => { });
    }
  }, [currentView, generatedPrompts, agentId, token]);

  // Fetch last execution when navigating to execute view without data
  useEffect(() => {
    if (currentView === 'execute' && executionNodes.length === 0 && !isExecuting && agentId && token) {
      fetch(`${EXECUTION_API}/agent/${agentId}/history?limit=1&skip=0`, {
        headers: { Authorization: token },
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.executions?.length > 0) {
            loadExecutionRun(data.executions[0].executionId);
          }
        })
        .catch(() => { });
    }
  }, [currentView, agentId]);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);


  // Determine which edge to highlight based on chat position
  const getInvitingEdge = () => {
    if (chatDocked !== null || !chatOpen) return null;
    const screenCenter = window.innerWidth / 2;
    const chatCenter = chatPosition.x + (chatSize.width / 2);
    // If chat is on left half, invite to right edge; if on right half, invite to left
    return chatCenter < screenCenter ? 'right' : 'left';
  };

  // Load saved chat state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('chatState');
    if (saved) {
      try {
        const state = JSON.parse(saved);
        if (state.position) setChatPosition(state.position);
        if (state.size) setChatSize(state.size);
        if (state.docked !== undefined) setChatDocked(state.docked);
      } catch (e) { }
    }
  }, []);

  // Save chat state to localStorage
  useEffect(() => {
    const state = { position: chatPosition, size: chatSize, docked: chatDocked };
    localStorage.setItem('chatState', JSON.stringify(state));
  }, [chatPosition, chatSize, chatDocked]);

  // Fetch journey progress when session changes
  useEffect(() => {
    if (!sessionId) return;

    const fetchJourneyProgress = async () => {
      try {
        const response = await fetch(`${CONTEXT_API}/progress/${sessionId}`, {
          headers: { 'Authorization': token }
        });
        if (response.ok) {
          const progress = await response.json();
          setJourneyProgress(progress);
          if (progress.agent_id) {
            setAgentId(progress.agent_id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch journey progress:', error);
      }
    };

    fetchJourneyProgress();
  }, [sessionId]);

  // Keep carousel index ref in sync for use inside SSE event handlers (avoids stale closures)
  useEffect(() => {
    executionCarouselIndexRef.current = executionCarouselIndex;
  }, [executionCarouselIndex]);

  // Auto-focus chat when new messages arrive
  useEffect(() => {
    if (messages.length > prevMessageCount) {
      // New message arrived - reopen chat if closed
      if (!chatOpen) {
        setChatOpen(true);
      }
      // If docked and collapsed, it's already visible
      // If floating and user closed it, reopen
    }
    setPrevMessageCount(messages.length);
  }, [messages.length]);

  // Drag handlers with snap-to-dock detection
  const handleDragStart = (e) => {
    if (e.target.closest('.chat-drag-handle')) {
      setIsDragging(true);
      // If docked, undock first
      if (chatDocked) {
        const startX = chatDocked === 'left' ? 20 : window.innerWidth - chatSize.width - 20;
        setChatPosition({ x: startX, y: 80 });
        setChatDocked(null);
      }
      setDragStart({ x: e.clientX - chatPosition.x, y: e.clientY - chatPosition.y });
    }
  };

  const handleDrag = (e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragStart.x;
    const newY = Math.max(0, Math.min(e.clientY - dragStart.y, window.innerHeight - chatSize.height));

    // Detect proximity to edges for snap preview
    if (newX < SNAP_THRESHOLD) {
      setNearEdge('left');
    } else if (newX > window.innerWidth - chatSize.width - SNAP_THRESHOLD) {
      setNearEdge('right');
    } else {
      setNearEdge(null);
    }

    // Clamp position
    const clampedX = Math.max(0, Math.min(newX, window.innerWidth - chatSize.width));
    setChatPosition({ x: clampedX, y: newY });
  };

  const handleDragEnd = () => {
    if (nearEdge) {
      // Snap to dock!
      setChatDocked(nearEdge);
      setNearEdge(null);
    }
    setIsDragging(false);
  };

  // Resize handlers
  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleResize = (e) => {
    if (!isResizing) return;
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    setChatSize(prev => ({
      width: Math.max(MIN_CHAT_WIDTH, Math.min(MAX_CHAT_WIDTH, prev.width + deltaX)),
      height: Math.max(MIN_CHAT_HEIGHT, Math.min(MAX_CHAT_HEIGHT, prev.height + deltaY))
    }));
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, dragStart, nearEdge]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResize);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResize);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, dragStart]);

  const addMessage = (type, content, skipSave = false) => {
    const epoch = sessionEpochRef.current;
    const newMessage = {
      id: Date.now(),
      type,
      content,
      timestamp: new Date()
    };
    setMessages(prev => {
      // Drop message if session was reset after this call was initiated
      if (sessionEpochRef.current !== epoch) return prev;
      return [...prev, newMessage];
    });

    // Save to backend if we have a session (don't await to avoid blocking UI)
    if (!skipSave && sessionId) {
      saveChatMessage(sessionId, type === 'user' ? 'user' : 'assistant', content);
    }
  };

  // Save a chat message to the backend
  const saveChatMessage = async (sessId, role, content) => {
    try {
      await fetch(`${DECOMPOSITION_API}/chat/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          session_id: sessId,
          role: role,
          content: content
        })
      });
    } catch (error) {
      console.error('Failed to save chat message:', error);
      // Don't throw - saving chat history is non-critical
    }
  };

  // Load chat history for a session
  const loadChatHistory = async (sessId) => {
    try {
      const response = await fetch(`${DECOMPOSITION_API}/chat/messages/${sessId}?limit=100`, {
        headers: { 'Authorization': token }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          // Convert backend messages to frontend format
          const loadedMessages = data.messages.map(msg => ({
            id: msg.message_id || Date.now(),
            type: msg.role === 'user' ? 'user' : 'bot',
            content: msg.content,
            timestamp: new Date(msg.created_at)
          }));
          setMessages(loadedMessages);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Failed to load chat history:', error);
      return false;
    }
  };

  // Handle stop/cancel button
  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    setIsDecomposing(false);
    setIsGeneratingPrompts(false);
    setIsGeneratingData(false);
    setIsGeneratingPreview(false);
    setThinkingMessage(null);
    addMessage('bot', 'Request cancelled.');
  };

  // Helper function for SSE API calls with thinking messages
  const fetchWithSSE = async (url, options, onThinking) => {
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Accept': 'text/event-stream'
      },
      signal: abortControllerRef.current.signal
    });

    if (!response.ok) {
      const err = new Error(`HTTP error! status: ${response.status}`);
      err.status = response.status;
      if (response.status === 402) {
        try { err.code = (await response.json()).code; } catch {}
      }
      throw err;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;
    let currentEventType = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEventType = line.substring(7).trim();
          console.log('[SSE] Event type:', currentEventType);
        }
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));
            console.log('[SSE] Data received:', currentEventType, data);
            if (data.message && onThinking) {
              onThinking(data.message);
            }
            if (data.result) {
              result = data.result;
            }
            // Handle similar agent exists warning
            if (data.code === 'SIMILAR_AGENT_EXISTS') {
              const warning = new Error(data.message);
              warning.code = data.code;
              warning.existingAgent = data.existing_agent;
              warning.suggestedName = data.suggested_name;
              throw warning;
            }
            if (data.error) {
              const error = new Error(data.message || data.error);
              error.code = data.error;
              error.existingAgentId = data.existing_agent_id;
              error.conceptName = data.concept_name;
              throw error;
            }
          } catch (e) {
            if (e.code === 'SIMILAR_AGENT_EXISTS' || e.code === 'DUPLICATE_AGENT') {
              throw e; // Re-throw our custom errors
            }
            if (e.message !== 'Unexpected end of JSON input' && !e.message.includes('Unexpected token')) {
              console.error('SSE parse error:', e);
            }
          }
        }
      }
    }

    return result;
  };

  const parseMCPTools = (mcpToolsText) => {
    if (!mcpToolsText || !mcpToolsText.trim()) return [];

    try {
      const tools = [];

      // Split by double newlines to separate tool blocks
      const toolBlocks = mcpToolsText.split('\n\n');

      for (const block of toolBlocks) {
        if (!block.trim()) continue;

        const lines = block.trim().split('\n').map(line => line.trim()).filter(line => line);
        const toolData = {};

        for (const line of lines) {
          if (line.toLowerCase().startsWith('tool name:')) {
            toolData.name = line.split(':', 1)[1].trim();
          } else if (line.toLowerCase().startsWith('what it does:')) {
            toolData.description = line.split(':', 1)[1].trim();
          } else if (line.toLowerCase().startsWith('required info:')) {
            const requiredFields = line.split(':', 1)[1].split(',').map(f => f.trim()).filter(f => f);
            toolData.required_params = requiredFields;
          } else if (line.toLowerCase().startsWith('optional info:')) {
            const optionalFields = line.split(':', 1)[1].split(',').map(f => f.trim()).filter(f => f);
            toolData.optional_params = optionalFields;
          }
        }

        // Build inputSchema if we have a tool name
        if (toolData.name) {
          const inputSchema = {
            type: "object",
            properties: {},
            required: toolData.required_params || []
          };

          // Add required parameters
          for (const param of (toolData.required_params || [])) {
            inputSchema.properties[param] = {
              type: "string",
              description: param.replace('_', ' ')
            };
          }

          // Add optional parameters  
          for (const param of (toolData.optional_params || [])) {
            inputSchema.properties[param] = {
              type: "string",
              description: param.replace('_', ' ')
            };
          }

          tools.push({
            name: toolData.name,
            description: toolData.description || '',
            inputSchema: inputSchema,
            required_params: toolData.required_params || [],
            optional_params: toolData.optional_params || []
          });
        }
      }

      return tools;
    } catch (error) {
      console.error('Error parsing MCP tools:', error);
      return [];
    }
  };

  // Helper function to actually generate questions (called after similarity check)
  const proceedWithQuestionGeneration = async (userMessage) => {
    setIsLoading(true);
    setThinkingMessage('Analyzing your problem...');

    try {
      const data = await fetchWithSSE(
        `${CONTEXT_API}/questions/round1/generate?stream=true&user_id=${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            user_id: userId,
            problem_description: userMessage
          })
        },
        (message) => setThinkingMessage(message)
      );

      if (!data) throw new Error('Failed to generate questions');

      setCurrentQuestionnaire(data);
      setRound1Questionnaire(data); // Store for navigation
      setSessionId(data.session_id);
      setCurrentRound(1);
      setFormData({});
      setOtherModeFields({});
      setCurrentView('questionnaire');

      // Now that we have session_id, save the initial user message and bot response
      // The user message was already added to UI but not saved (no session_id at that time)
      if (data.session_id) {
        saveChatMessage(data.session_id, 'user', userMessage);
        saveChatMessage(data.session_id, 'assistant', `I've created a questionnaire to understand your specific situation. Please fill out the form on the right to help me gather the right context.`);
      }
      addMessage('bot', `I've created a questionnaire to understand your specific situation. Please fill out the form on the right to help me gather the right context.`, true); // Skip save - already saved above

      // Refresh sidebar lists
      fetchAgentsList();
      fetchSessionsList();
    } catch (error) {
      if (error.status === 402) { handleInsufficientCredits(); return; }
      addMessage('bot', 'Sorry, there was an error generating the questions. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
      setThinkingMessage(null);
    }
  };

  const handleSendMessage = async (overrideMessage) => {
    const raw = overrideMessage || inputMessage.trim();
    if (!raw) return;

    const userMessage = raw;
    if (!overrideMessage) setInputMessage('');
    addMessage('user', userMessage);

    // ── Jira ticket detection ──
    // Matches PROJ-123 or full Atlassian URLs
    const jiraTicketMatch = userMessage.match(/\b([A-Z][A-Z0-9]+-\d+)\b/) ||
      userMessage.match(/atlassian\.net\/browse\/([A-Z][A-Z0-9]+-\d+)/);
    if (jiraTicketMatch) {
      const issueKey = jiraTicketMatch[1];
      if (jiraConnected) {
        // Switch to questionnaire view and trigger consolidation
        setCurrentView('questionnaire');
        await handleFetchJiraTicket(issueKey);
        return; // The Jira review UI takes over in the questionnaire panel
      } else {
        addMessage('bot', `I detected a Jira ticket reference (${issueKey}). Connect your Jira account to import ticket details automatically.`, true);
      }
    }

    // Track effective view locally — setCurrentView is async so currentView
    // won't update within this function call.
    let effectiveView = currentView;

    // ── Guide screen: if user sends a build request, switch to questionnaire ──
    if (currentView === 'guide') {
      const lowerMsg = userMessage.toLowerCase();
      const buildVerbs = /\b(create|build|make|generate|design|develop|set up|setup|implement)\b/;
      const buildNouns = /\b(agent|workflow|tool|app|system|pipeline|bot|assistant|automation|solution|prototype)\b/;
      if (buildVerbs.test(lowerMsg) && buildNouns.test(lowerMsg)) {
        // Clear old agent state before starting a new build
        resetForNewAgent();
        setCurrentView('questionnaire');
        effectiveView = 'questionnaire';
        // Fall through to the questionnaire handler below
      } else {
        // Route to orchestrator — backend returns intent: "build_request" or "product_help"
        setIsLoading(true);
        try {
          const payload = {
            message: userMessage, event: 'message', screen: 'guide',
            sessionId: sessionId || 'anonymous',
            agentId: agentId || undefined,
            history: messages.filter(m => m.type !== 'divider').slice(-15).map(m => ({ role: m.type === 'user' ? 'user' : 'assistant', content: m.content })),
          };
          const response = await fetch(`${CHAT_ORCHESTRATOR_API}/message?user_id=${userId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) },
            body: JSON.stringify(payload)
          });
          if (response.status === 402) { handleInsufficientCredits(); return; }
          const data = await response.json();
          if (data?.reply) addMessage('bot', data.reply);

          // Show CTA so user can confirm build intent — no more LLM guessing
          setPendingBuildMessage(userMessage);
        } catch (e) {
          addMessage('bot', 'Sorry, I couldn\'t process that right now. Try again!', true);
        } finally {
          setIsLoading(false);
        }
        return;
      }
    }

    // ── Questionnaire stage (no agent yet): route through orchestrator first ──
    if (effectiveView === 'questionnaire' && !agentId) {
      setIsLoading(true);
      setThinkingMessage('Thinking...');
      setPendingBuildMessage(null); // Clear any previous CTA

      // Quick check: if the message is clearly a build request, skip orchestrator
      const lowerMsg = userMessage.toLowerCase();
      const buildVerbs = /\b(create|build|make|generate|design|develop|set up|setup|implement)\b/;
      const buildNouns = /\b(agent|workflow|tool|app|system|pipeline|bot|assistant|automation|solution|prototype)\b/;
      const isClearBuildRequest = buildVerbs.test(lowerMsg) && buildNouns.test(lowerMsg);

      if (isClearBuildRequest) {
        // Obvious build intent — go straight to questionnaire generation
        setThinkingMessage('Checking for similar projects...');
        try {
          const checkResponse = await fetch(`${CONTEXT_API}/questions/check-similar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': token },
            body: JSON.stringify({ user_id: userId, problem_description: userMessage })
          });
          if (checkResponse.ok) {
            const checkData = await checkResponse.json();
            if (checkData.has_similar && checkData.similar_items?.length > 0) {
              setIsLoading(false);
              setThinkingMessage(null);
              const itemsList = checkData.similar_items.map(item => {
                const typeLabel = item.type === 'agent' ? 'Agent' : 'Session';
                const statusLabel = item.status ? ` (${item.status})` : '';
                return `• ${typeLabel}: "${item.name}"${statusLabel}`;
              }).join('\n');
              setConfirmDialog({
                isOpen: true, title: 'Similar Project Found',
                message: `You already have similar projects:\n\n${itemsList}\n\nWould you like to continue with one of these, or create a new one?`,
                confirmText: 'Create New', cancelText: 'View Existing',
                onConfirm: async () => {
                  setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
                  await proceedWithQuestionGeneration(userMessage);
                },
                onCancel: () => {
                  setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
                  const firstItem = checkData.similar_items[0];
                  if (firstItem.type === 'agent') loadExistingAgent(firstItem.id);
                  else loadExistingSession(firstItem.id);
                }
              });
              return;
            }
          }
          await proceedWithQuestionGeneration(userMessage);
        } catch (error) {
          console.error('[BuildDirect] Error:', error);
          await proceedWithQuestionGeneration(userMessage);
        }
        return;
      }

      try {
        // Route through chat orchestrator for classification + response
        const orchestratorPayload = {
          message: userMessage,
          event: 'message',
          screen: 'questionnaire',
          sessionId: sessionId || 'anonymous',
          history: messages.filter(m => m.type !== 'divider').slice(-10).map(m => ({
            role: m.type === 'user' ? 'user' : 'assistant',
            content: m.content
          })),
        };

        const orchestratorRes = await fetch(`${CHAT_ORCHESTRATOR_API}/message?user_id=${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token },
          body: JSON.stringify(orchestratorPayload),
        });

        if (orchestratorRes.ok) {
          const data = await orchestratorRes.json();
          const intent = data.intent || 'generic';

          // Build request: proceed to questionnaire generation
          if (intent === 'build_request') {
            if (data.reply) addMessage('bot', data.reply);
            await proceedWithQuestionGeneration(userMessage);
            return;
          }

          // Clear intents: just answer, no build CTA
          if (['product_help', 'small_talk', 'identity', 'navigation'].includes(intent)) {
            setIsLoading(false);
            setThinkingMessage(null);
            if (data.reply) addMessage('bot', data.reply);
            return;
          }

          // Generic / ambiguous: show reply + build CTA
          setIsLoading(false);
          setThinkingMessage(null);
          if (data.reply) addMessage('bot', data.reply);
          // Store the message so the CTA can trigger build with it
          setPendingBuildMessage(userMessage);
          return;
        }
      } catch (err) {
        console.warn('[Orchestrator] Failed, falling back to direct generation:', err);
      }

      // Fallback: orchestrator failed — proceed with questionnaire generation directly
      setThinkingMessage('Checking for similar projects...');

      try {
        console.log('[CheckSimilar] Checking for similar projects...');
        const checkResponse = await fetch(`${CONTEXT_API}/questions/check-similar`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            user_id: userId,
            problem_description: userMessage
          })
        });

        console.log('[CheckSimilar] Response status:', checkResponse.status);

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          console.log('[CheckSimilar] Response data:', checkData);

          if (checkData.has_similar && checkData.similar_items?.length > 0) {
            setIsLoading(false);
            setThinkingMessage(null);

            const itemsList = checkData.similar_items.map(item => {
              const typeLabel = item.type === 'agent' ? 'Agent' : 'Session';
              const statusLabel = item.status ? ` (${item.status})` : '';
              return `• ${typeLabel}: "${item.name}"${statusLabel}`;
            }).join('\n');

            setConfirmDialog({
              isOpen: true,
              title: 'Similar Project Found',
              message: `You already have similar projects:\n\n${itemsList}\n\nWould you like to continue with one of these, or create a new one?`,
              confirmText: 'Create New',
              cancelText: 'View Existing',
              onConfirm: async () => {
                setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
                await proceedWithQuestionGeneration(userMessage);
              },
              onCancel: () => {
                setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
                const firstItem = checkData.similar_items[0];
                if (firstItem.type === 'agent') {
                  loadExistingAgent(firstItem.id);
                } else {
                  loadExistingSession(firstItem.id);
                }
              }
            });
            return;
          }
        } else {
          console.warn('[CheckSimilar] Response not OK:', checkResponse.status);
        }

        console.log('[CheckSimilar] No similar items found or check skipped, proceeding with generation');
        await proceedWithQuestionGeneration(userMessage);

      } catch (error) {
        console.error('[CheckSimilar] Error checking for similar:', error);
        console.error('[CheckSimilar] Proceeding with generation anyway...');
        await proceedWithQuestionGeneration(userMessage);
      }
      return;
    }

    // ── All other stages: route through Chat Orchestrator ──
    setIsLoading(true);
    setThinkingMessage('Thinking...');

    try {
      const payload = {
        message: userMessage,
        event: 'message',
        screen: currentView,
        sessionId: sessionId || 'anonymous',
        agentId: agentId || undefined,
        history: messages.slice(-15).map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.content
        })),
        agentDefinition: agentDefinition || undefined,
        prototypeConfig: currentView === 'prototype' ? prototypeConfig : undefined,
      };

      const response = await fetch(`${CHAT_ORCHESTRATOR_API}/message?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 402) { handleInsufficientCredits(); return; }

      if (response.ok) {
        const data = await response.json();

        // Add bot reply
        if (data.reply) {
          addMessage('bot', data.reply);
        }

        // Handle navigation action
        if (data.navigation) {
          setCurrentView(data.navigation);
        }

        // Handle prototype config updates from chat
        if (data.prototypeChanges) {
          setPrototypeConfig(data.prototypeChanges);
          setPrototypeConfigUpdate({ config: data.prototypeChanges, ts: Date.now() });
        }
      } else {
        console.error('[ChatOrchestrator] Response not OK:', response.status);
        addMessage('bot', "I'm having trouble processing that. Could you try again?");
      }
    } catch (error) {
      console.error('[ChatOrchestrator] Error:', error);
      addMessage('bot', "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
      setThinkingMessage(null);
    }
  };

  // Load an existing agent from sidebar
  const loadExistingAgent = async (agentId) => {
    setIsLoading(true);
    setThinkingMessage('Loading agent...');
    try {
      const response = await fetch(`${DECOMPOSITION_API}/agent/${agentId}`, {
        headers: { 'Authorization': token }
      });
      if (response.ok) {
        const agentData = await response.json();
        setAgentId(agentData.agent_id);
        setAgentDefinition(agentData);
        if (agentData.prototype_config) {
          setPrototypeConfig(agentData.prototype_config);
        }
        setSessionId(agentData.session_id);
        setCurrentView('workflow');
        setDetailsExpanded(true);

        // Load the workflow to show flow visualization
        if (agentData.workflow_id) {
          const workflowResponse = await fetch(`${DECOMPOSITION_API}/decompose/${agentData.workflow_id}`, {
            headers: { 'Authorization': token }
          });
          if (workflowResponse.ok) {
            const workflowData = await workflowResponse.json();

            // Sort flows to ensure consistent order: balanced/recommended (0), simple (1), thorough (2)
            if (workflowData.flows && workflowData.flows.length > 1) {
              const flowOrder = { 'balanced': 0, 'simple': 1, 'thorough': 2 };
              workflowData.flows.sort((a, b) => {
                const aType = a.flow_type?.toLowerCase() || (a.label?.toLowerCase().includes('simple') ? 'simple' : a.label?.toLowerCase().includes('thorough') || a.label?.toLowerCase().includes('comprehensive') ? 'thorough' : 'balanced');
                const bType = b.flow_type?.toLowerCase() || (b.label?.toLowerCase().includes('simple') ? 'simple' : b.label?.toLowerCase().includes('thorough') || b.label?.toLowerCase().includes('comprehensive') ? 'thorough' : 'balanced');
                return (flowOrder[aType] ?? 1) - (flowOrder[bType] ?? 1);
              });
            }

            setWorkflowResult(workflowData);

            // Restore currentFlowIndex based on agent's selected flow
            const selectedRank = agentData.metadata?.selected_flow_rank;
            const selectedLabel = agentData.metadata?.selected_flow_label;
            if (workflowData.flows && workflowData.flows.length > 0) {
              // Try to find by label first (more reliable after sorting), then by rank
              let flowIndex = 0;
              if (selectedLabel) {
                const labelIndex = workflowData.flows.findIndex(f => f.label === selectedLabel);
                if (labelIndex >= 0) flowIndex = labelIndex;
              } else if (selectedRank && selectedRank >= 1 && selectedRank <= workflowData.flows.length) {
                flowIndex = selectedRank - 1;
              }
              setCurrentFlowIndex(flowIndex);
              setCurrentNodeIndex(0);
            }
          }
        }

        // Load chat history if session exists
        let hasHistory = false;
        if (agentData.session_id) {
          hasHistory = await loadChatHistory(agentData.session_id);
        }
        if (!hasHistory) {
          addMessage('bot', `Loaded agent: ${agentData.concept_name}`, true);
        }

        // Fetch journey progress
        if (agentData.session_id) {
          const progressResponse = await fetch(`${CONTEXT_API}/progress/${agentData.session_id}`, {
            headers: { 'Authorization': token }
          });
          if (progressResponse.ok) {
            const progress = await progressResponse.json();
            setJourneyProgress(progress);
          }
        }
      } else {
        addMessage('bot', 'Failed to load agent. It may have been deleted.', true);
      }
    } catch (error) {
      console.error('Error loading agent:', error);
      addMessage('bot', 'Error loading agent. Please try again.', true);
    } finally {
      setIsLoading(false);
      setThinkingMessage(null);
    }
  };

  // Load an existing session from sidebar
  const loadExistingSession = async (sessionIdToLoad) => {
    setIsLoading(true);
    setThinkingMessage('Loading session...');
    try {
      // Get session details
      const response = await fetch(`${CONTEXT_API}/questions/session/${sessionIdToLoad}`, {
        headers: { 'Authorization': token }
      });
      if (response.ok) {
        const sessionData = await response.json();
        console.log('[LoadSession] Session data:', sessionData);
        setSessionId(sessionIdToLoad);

        // Check journey stage FIRST to determine which view to show
        const stage = sessionData.journey?.current_stage;
        const journeyAgentId = sessionData.journey?.agent_id;
        const journeyWorkflowId = sessionData.journey?.workflow_id;
        const hasQuestionnaire = sessionData.round1_questions && sessionData.round1_questions.input && sessionData.round1_questions.input.length > 0;

        console.log('[LoadSession] Journey stage:', stage, 'agent_id:', journeyAgentId, 'workflow_id:', journeyWorkflowId);

        // Stages after decompose_complete where agent exists
        const agentStages = ['plan', 'prompts', 'present', 'prototype'];
        const shouldShowWorkflow = (journeyAgentId && agentStages.includes(stage)) ||
          (stage === 'decompose_complete' && journeyWorkflowId);

        // Load chat history
        const hasHistory = await loadChatHistory(sessionIdToLoad);
        if (!hasHistory) {
          setMessages([{ id: Date.now(), type: 'bot', content: 'Welcome! Describe the problem you want to solve, and I\'ll help you build an AI agent for it.', timestamp: new Date() }]);
        }

        // Only set questionnaire state if we're going to show the questionnaire view
        if (!shouldShowWorkflow) {
          if (hasQuestionnaire) {
            setCurrentQuestionnaire(sessionData.round1_questions);
            setRound1Questionnaire(sessionData.round1_questions);
            setCurrentRound(1);
          }
          if (sessionData.round1_responses && Object.keys(sessionData.round1_responses).length > 0) {
            setFormData(sessionData.round1_responses);
            setRound1FormData(sessionData.round1_responses);
          }
        }

        // Now load the appropriate view
        if (journeyAgentId && agentStages.includes(stage)) {
          // Agent was created - load the agent
          console.log('[LoadSession] Loading agent:', journeyAgentId);
          try {
            const agentResponse = await fetch(`${DECOMPOSITION_API}/agent/${journeyAgentId}`, {
              headers: { 'Authorization': token }
            });
            if (agentResponse.ok) {
              const agentData = await agentResponse.json();
              setAgentId(agentData.agent_id);
              setAgentDefinition(agentData);

              // Also load the workflow for visualization
              if (journeyWorkflowId) {
                const workflowResponse = await fetch(`${DECOMPOSITION_API}/decompose/${journeyWorkflowId}`, {
                  headers: { 'Authorization': token }
                });
                if (workflowResponse.ok) {
                  const workflowData = await workflowResponse.json();
                  setWorkflowResult(workflowData);
                }
              }

              // Fetch prompts if agent has them
              const agentStatus = (agentData.status || '').toLowerCase();
              if (agentStatus === 'prompts_ready' || agentStatus === 'execution_ready') {
                try {
                  const promptsRes = await fetch(`${PROMPT_GENERATION_API}/agent/${agentData.agent_id}/prompts`, {
                    headers: { Authorization: token },
                  });
                  if (promptsRes.ok) {
                    const promptsData = await promptsRes.json();
                    setGeneratedPrompts(promptsData);
                  }
                } catch (e) {
                  console.log('No prompts found for agent');
                }
              }

              setCurrentView('workflow');
              setDetailsExpanded(true);
              if (!hasHistory) {
                addMessage('bot', `Resumed agent: ${agentData.concept_name}`, true);
              }
            } else {
              // Agent not found, fall back to workflow view
              console.log('[LoadSession] Agent not found, trying workflow');
              if (journeyWorkflowId) {
                const workflowResponse = await fetch(`${DECOMPOSITION_API}/decompose/${journeyWorkflowId}`, {
                  headers: { 'Authorization': token }
                });
                if (workflowResponse.ok) {
                  const workflowData = await workflowResponse.json();
                  setWorkflowResult(workflowData);
                  setCurrentView('workflow');
                }
              }
            }
          } catch (agentError) {
            console.error('[LoadSession] Error loading agent:', agentError);
          }
        } else if (stage === 'decompose_complete' && journeyWorkflowId) {
          // Load workflow result
          console.log('[LoadSession] Loading workflow:', journeyWorkflowId);
          const workflowResponse = await fetch(`${DECOMPOSITION_API}/decompose/${journeyWorkflowId}`, {
            headers: { 'Authorization': token }
          });
          if (workflowResponse.ok) {
            const workflowData = await workflowResponse.json();
            console.log('[LoadSession] Workflow data loaded:', workflowData);
            setWorkflowResult(workflowData);
            setCurrentView('workflow');

            // Check if multiple flows available
            const hasMultipleFlows = workflowData.flows && workflowData.flows.length > 1;
            // Add appropriate message based on state
            if (!hasHistory) {
              if (hasMultipleFlows && !journeyAgentId) {
                addMessage('bot', `Resumed session with ${workflowData.flows.length} flow options. Select one below to continue.`, true);
              } else {
                addMessage('bot', `Resumed session with workflow. ${sessionData.initial_problem ? `Working on: "${sessionData.initial_problem.substring(0, 50)}..."` : ''}`, true);
              }
            }
          } else {
            // Workflow not found, show questionnaire
            console.log('[LoadSession] Workflow not found, falling back to questionnaire');
            // Now set questionnaire state since we need it
            if (hasQuestionnaire) {
              setCurrentQuestionnaire(sessionData.round1_questions);
              setRound1Questionnaire(sessionData.round1_questions);
              setCurrentRound(1);
            }
            if (sessionData.round1_responses && Object.keys(sessionData.round1_responses).length > 0) {
              setFormData(sessionData.round1_responses);
              setRound1FormData(sessionData.round1_responses);
            }
            setCurrentView('questionnaire');
            if (!hasHistory) {
              addMessage('bot', `Resumed session. ${hasQuestionnaire ? 'Continue with the questionnaire.' : 'The questionnaire needs to be regenerated.'}`, true);
            }
          }
        } else if (hasQuestionnaire) {
          console.log('[LoadSession] Showing questionnaire view');
          setCurrentView('questionnaire');
          if (!hasHistory) {
            addMessage('bot', `Resumed session. ${sessionData.initial_problem ? `Working on: "${sessionData.initial_problem.substring(0, 50)}..."` : ''}`, true);
          }
        } else {
          // No questionnaire - session was created but questionnaire wasn't generated
          setCurrentView('questionnaire');
          addMessage('bot', `Session found but questionnaire is missing. Please describe your problem again to regenerate.`, true);
        }
      } else {
        addMessage('bot', 'Failed to load session. It may have been deleted.', true);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      addMessage('bot', 'Error loading session. Please try again.', true);
    } finally {
      setIsLoading(false);
      setThinkingMessage(null);
    }
  };

  const handleFormSubmit = async () => {
    if (!sessionId || !formData) return;

    // Store current questionnaire for potential restore on error
    const previousQuestionnaire = currentQuestionnaire;

    // If we're viewing answers and regenerating, clear the old workflow
    if (isViewingAnswers) {
      setWorkflowResult(null);
      setIsViewingAnswers(false);
      setOriginalFormData({});
    }

    setIsLoading(true);
    setIsAnalyzingQuestionnaire(true);
    addMessage('user', getRandomMessage('round1_submit'));

    try {
      if (currentRound === 1) {
        setThinkingMessage('Reviewing your responses...');

        const analysisData = await fetchWithSSE(
          `${CONTEXT_API}/questions/round1/analyze?stream=true&user_id=${userId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token
            },
            body: JSON.stringify({
              session_id: sessionId,
              round1_responses: formData,
              additional_details: additionalDetails || undefined
            })
          },
          (message) => setThinkingMessage(message)
        );

        if (!analysisData) throw new Error('Failed to analyze responses');

        setAnalysisResult(analysisData.analysis_result);

        // Check if we have enough clarity to proceed (75% threshold)
        if (analysisData.analysis_result.ready_for_decomposition) {
          // Sufficient clarity - proceed directly to decomposition
          // Reset low clarity mode
          setLowClarityMode(false);
          setClarificationRound(0);
          setMissingContextItems([]);
          setAdditionalDetails('');
          setRound1FormData(formData);
          setIsAnalyzingQuestionnaire(false);
          await finalizeContextAndDecompose();
        } else {
          // Insufficient clarity - show message with what's missing and enable additional details
          setThinkingMessage(null);
          const missingContext = analysisData.analysis_result.missing_context || [];
          const clarityScore = Math.round((analysisData.analysis_result.clarity_score || 0) * 100);
          const nextRound = clarificationRound + 1;
          setClarificationRound(nextRound);

          if (nextRound >= 2) {
            // Capped: user already clarified once, give them a choice
            let message = `I'm still not fully clear on some requirements (clarity: ${clarityScore}%), but I don't want to hold you up.`;
            message += '\n\nYou can proceed with what I have and I\'ll make reasonable assumptions, or you can refine your answers further.';
            addMessage('bot', message);
            setLowClarityMode('capped');
            setMissingContextItems(missingContext);
            setIsAnalyzingQuestionnaire(false);
            setCurrentQuestionnaire(previousQuestionnaire);
          } else {
            let message = `I don't have enough information to design your workflow yet (clarity: ${clarityScore}%).`;
            if (missingContext.length > 0) {
              message += `\n\nMissing information:\n${missingContext.map(item => `• ${item}`).join('\n')}`;
            }
            message += '\n\nPlease provide more details in the "Additional Details" field below to clarify these requirements.';

            addMessage('bot', message);
            // Enable low clarity mode and show additional details field
            setLowClarityMode(true);
            setMissingContextItems(missingContext);
            // Restore the questionnaire so user can update their answers
            setIsAnalyzingQuestionnaire(false);
            setCurrentQuestionnaire(previousQuestionnaire);
          }
        }
      }
    } catch (error) {
      if (error.status === 402) { handleInsufficientCredits(); return; }
      // Restore questionnaire on error so user can retry
      setIsAnalyzingQuestionnaire(false);
      setCurrentQuestionnaire(previousQuestionnaire);
      addMessage('bot', 'Sorry, there was an error processing your responses. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
      setThinkingMessage(null);
    }
  };

  // Reset agent state for a new build (clears everything including chat)
  const resetForNewAgent = () => {
    sessionEpochRef.current += 1;
    prevViewRef.current = null;
    setCurrentQuestionnaire(null);
    setRound1Questionnaire(null);
    setFormData({});
    setOtherModeFields({});
    setRound1FormData({});
    setSessionId(null);
    setCurrentRound(0);
    setAnalysisResult(null);
    setWorkflowResult(null);
    setGeneratedPrompts(null);
    setGeneratedData(null);
    setPreviewData(null);
    setAgentId(null);
    setAgentDefinition(null);
    setJourneyProgress(null);
    setPrototypeConfig(null);
    setPrototypeConfigUpdate(null);
    setProtoAutoExecute(false);
    protoAutoExecuteResolveRef.current = null;
    setExecutionNodes([]);
    setExecutionStatus(null);
    setExecutionId(null);
    setIsExecuting(false);
    setLowClarityMode(false);
    setClarificationRound(0);
    setMissingContextItems([]);
    setAdditionalDetails('');
    setPendingBuildMessage(null);
    setJiraTicketKey(null);
    setJiraTicketTitle(null);
    setJiraPostStatus(null);
    setJiraPostSummary('');
    setJiraPostRequirements([]);
    setJiraFlowStep(null);
    setJiraConsolidation(null);
    setJiraChecklist({});
    setJiraGapAnswers({});
    setInputMessage('');
    setMessages([]);
  };

  // CTA Handler: Start Over - resets the entire session
  const handleStartOver = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Start Over?',
      message: 'This will clear all your progress and start fresh. Are you sure?',
      confirmText: 'Proceed',
      onConfirm: () => {
        // Close dialog first so subsequent state updates render cleanly
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
        // Invalidate any in-flight async callbacks (sendStateChange, awaitByteAnalysis)
        sessionEpochRef.current += 1;
        // Prevent the screen_change useEffect from injecting a stale divider
        prevViewRef.current = null;
        // Reset all state
        setCurrentQuestionnaire(null);
        setRound1Questionnaire(null);
        setFormData({});
        setOtherModeFields({});
        setRound1FormData({});
        setSessionId(null);
        setCurrentRound(0);
        setAnalysisResult(null);
        setWorkflowResult(null);
        setCurrentView('questionnaire');
        setGeneratedPrompts(null);
        setGeneratedData(null);
        setPreviewData(null);
        setAgentId(null);
        setAgentDefinition(null);
        setJourneyProgress(null);
        // Reset prototype state
        setPrototypeConfig(null);
        setPrototypeConfigUpdate(null);
        setProtoAutoExecute(false);
        protoAutoExecuteResolveRef.current = null;
        // Reset execution state
        setExecutionNodes([]);
        setExecutionStatus(null);
        setExecutionId(null);
        setIsExecuting(false);
        // Reset low clarity mode
        setLowClarityMode(false);
        setClarificationRound(0);
        setMissingContextItems([]);
        setAdditionalDetails('');
        setPendingBuildMessage(null);
        // Reset Jira post state
        setJiraTicketKey(null);
        setJiraTicketTitle(null);
        setJiraPostStatus(null);
        setJiraPostSummary('');
        setJiraPostRequirements([]);
        setJiraFlowStep(null);
        setJiraConsolidation(null);
        setJiraChecklist({});
        setJiraGapAnswers({});
        // Clear input
        setInputMessage('');
        // Clear chat and set fresh welcome message
        setMessages([
          { id: Date.now(), type: 'bot', content: 'Session reset. Describe a new problem to get started!', timestamp: new Date() }
        ]);
        // Restore chat if minimized - user needs to interact with chat for new agent
        if (!chatOpen) {
          setChatOpen(true);
        }
      }
    });
  };

  // Fetch agents list for sidebar
  const fetchAgentsList = async () => {
    if (!token || !userId) return;
    setLoadingAgents(true);
    try {
      const response = await fetch(`${DECOMPOSITION_API}/agents/list?user_id=${userId}&limit=20&offset=0`, {
        headers: { Authorization: token },
      });
      if (!response.ok) throw new Error('Failed to fetch agents');
      const data = await response.json();
      setAgentsList(data.agents || []);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      setAgentsList([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  // Fetch sessions list for sidebar (questionnaire stage sessions)
  const fetchSessionsList = async () => {
    if (!token || !userId) return;
    setLoadingSessions(true);
    try {
      const response = await fetch(`${DECOMPOSITION_API}/sessions/list?user_id=${userId}&limit=20&offset=0`, {
        headers: { Authorization: token },
      });
      if (!response.ok) throw new Error('Failed to fetch sessions');
      const data = await response.json();
      setSessionsList(data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      setSessionsList([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  // Fetch shared prototypes
  const fetchSharedPrototypes = async () => {
    if (!token || !userId) return;
    setLoadingSharedPrototypes(true);
    try {
      const response = await fetch(`${PROTOTYPE_API}/shared?user_id=${userId}`, {
        headers: { Authorization: token },
      });
      if (!response.ok) throw new Error('Failed to fetch shared prototypes');
      const data = await response.json();
      setSharedPrototypesList(data.prototypes || []);
    } catch (error) {
      console.error('Failed to fetch shared prototypes:', error);
      setSharedPrototypesList([]);
    } finally {
      setLoadingSharedPrototypes(false);
    }
  };

  // Toggle sidebar and fetch data
  const toggleSidebar = () => {
    if (!sidebarOpen) {
      // Fetch data when opening sidebar
      fetchAgentsList();
      fetchSessionsList();
      fetchSharedPrototypes();
    }
    setSidebarOpen(!sidebarOpen);
  };

  // Fetch execution history for current agent
  const fetchExecutionHistory = async () => {
    if (!agentId || !token) return;
    setLoadingExecutionHistory(true);
    try {
      const response = await fetch(`${EXECUTION_API}/agent/${agentId}/history?limit=30&skip=0`, {
        headers: { Authorization: token },
      });
      if (!response.ok) throw new Error('Failed to fetch execution history');
      const data = await response.json();
      setExecutionHistoryList(data.executions || []);
    } catch (error) {
      console.error('Failed to fetch execution history:', error);
      setExecutionHistoryList([]);
    } finally {
      setLoadingExecutionHistory(false);
    }
  };

  // Toggle execution history sidebar
  const toggleExecutionSidebar = () => {
    if (!executionSidebarOpen) {
      fetchExecutionHistory();
    }
    setExecutionSidebarOpen(!executionSidebarOpen);
  };

  // Load a past execution run into the carousel
  const loadExecutionRun = async (executionId) => {
    try {
      const response = await fetch(`${EXECUTION_API}/${executionId}`, {
        headers: { Authorization: token },
      });
      if (!response.ok) throw new Error('Failed to load execution');
      const data = await response.json();

      // Populate executionNodes from the saved node_executions
      const loadedNodes = (data.nodeExecutions || []).map(ne => ({
        nodeId: ne.nodeId,
        alias: ne.alias,
        nodeType: ne.nodeType,
        status: ne.status,
        input: ne.input,
        output: ne.output,
        durationMs: ne.durationMs,
        error: ne.error,
        validation: ne.validation,
        tldr: null,
        tldrLoading: false,
      }));

      setExecutionNodes(loadedNodes);
      setExecutionStatus(data.status);
      setExecutionCarouselIndex(0);
      setIsExecuting(false);
      setCurrentExecutingNodeIndex(-1);
      setViewingHistoryRun(data.executionId);
      setExecutionSidebarOpen(false);
    } catch (error) {
      console.error('Failed to load execution run:', error);
    }
  };

  // Load an existing agent
  const loadAgent = async (agentIdToLoad, targetView = null) => {
    try {
      const response = await fetch(`${DECOMPOSITION_API}/agent/${agentIdToLoad}`, {
        headers: { Authorization: token },
      });
      if (!response.ok) throw new Error(`Failed to load agent (${response.status})`);
      const agent = await response.json();

      // Clear old messages first, then load fresh history
      setMessages([]);

      // Set agent state
      setAgentId(agentIdToLoad);
      setAgentDefinition(agent);
      setSessionId(agent.session_id);
      // Reset prototype config so new agent gets fresh config
      setPrototypeConfig(null);
      setPrototypeConfigUpdate(null);
      if (!isShareMode) {
        setCurrentView(targetView || 'workflow');
        setSidebarOpen(false);
      }

      // Try to load workflow result if exists
      try {
        const workflowRes = await fetch(`${DECOMPOSITION_API}/decompose/${agent.workflow_id}`, {
          headers: { Authorization: token },
        });
        if (workflowRes.ok) {
          const workflow = await workflowRes.json();
          setWorkflowResult(workflow);
        }
      } catch (e) {
        console.log('No workflow found for agent');
      }

      // Fetch prompts if agent has them
      const agentStatus = (agent.status || '').toLowerCase();
      if (agentStatus === 'prompts_ready' || agentStatus === 'execution_ready') {
        try {
          const promptsRes = await fetch(`${PROMPT_GENERATION_API}/agent/${agentIdToLoad}/prompts`, {
            headers: { Authorization: token },
          });
          if (promptsRes.ok) {
            const promptsData = await promptsRes.json();
            setGeneratedPrompts(promptsData);
          }
        } catch (e) {
          console.log('No prompts found for agent');
        }
      }

      // Load chat history for this agent's session, or show a welcome message
      if (!isShareMode) {
        const hasHistory = await loadChatHistory(agent.session_id);
        if (!hasHistory) {
          addMessage('bot', `Loaded agent: ${agent.concept_name}`);
        }
      }
      return true;
    } catch (error) {
      console.error('Failed to load agent:', error);
      addMessage('bot', 'Failed to load agent. Please try again.');
      setAgentId(null);
      setAgentDefinition(null);
      setCurrentView('guide');
      return false;
    }
  };

  // Load an existing session (questionnaire stage) - called from sidebar
  const loadSession = async (sessionIdToLoad) => {
    setSidebarOpen(false);
    // Reuse the loadExistingSession function for consistency
    await loadExistingSession(sessionIdToLoad);
  };

  // Load a shared prototype from sidebar
  const loadSharedPrototype = async (shareId) => {
    try {
      const response = await fetch(`${PROTOTYPE_API}/share/${shareId}`, {
        headers: token ? { Authorization: token } : {},
      });
      if (!response.ok) throw new Error('Failed to load shared prototype');
      const data = await response.json();

      if (data.agentId && data.agentId !== 'unknown') {
        setAgentId(data.agentId);
      }
      if (data.agentDefinition) {
        setAgentDefinition(data.agentDefinition);
      }
      if (data.config) {
        setPrototypeConfig(data.config);
      }
      setCurrentView('prototype');
      setSidebarOpen(false);
    } catch (error) {
      console.error('Failed to load shared prototype:', error);
    }
  };

  // Delete an agent with confirmation
  const renameAgent = async (agentIdToRename, newName) => {
    try {
      const response = await fetch(`${DECOMPOSITION_API}/agent/${agentIdToRename}/rename`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': token },
        body: JSON.stringify({ concept_name: newName }),
      });
      if (response.ok) {
        setAgentsList(prev => prev.map(a =>
          a.agent_id === agentIdToRename ? { ...a, concept_name: newName } : a
        ));
        // Also update current agent definition if it's the one being renamed
        if (agentId === agentIdToRename && agentDefinition) {
          setAgentDefinition(prev => ({ ...prev, concept_name: newName }));
        }
      }
    } catch (error) {
      console.error('Failed to rename agent:', error);
    }
    setEditingAgentId(null);
    setEditingAgentName('');
  };

  const deleteAgent = async (agentIdToDelete, conceptName) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Agent?',
      message: `Are you sure you want to delete "${conceptName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      onConfirm: async () => {
        // Close dialog immediately to prevent double-clicks
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });

        try {
          const response = await fetch(`${DECOMPOSITION_API}/agent/${agentIdToDelete}`, {
            method: 'DELETE',
            headers: { Authorization: token },
          });
          // 404 means already deleted, which is fine
          if (!response.ok && response.status !== 404) {
            throw new Error('Failed to delete agent');
          }

          // If we're currently viewing this agent, reset state
          if (agentId === agentIdToDelete) {
            setAgentId(null);
            setAgentDefinition(null);
            setCurrentView('questionnaire');
          }

          // Refresh the agents list
          fetchAgentsList();
          addMessage('bot', `Deleted agent: ${conceptName}`);
        } catch (error) {
          console.error('Failed to delete agent:', error);
          addMessage('bot', 'Failed to delete agent. Please try again.');
        }
      }
    });
  };

  // Delete a session with confirmation
  const deleteSession = async (sessionIdToDelete, problemSummary) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Session?',
      message: `Are you sure you want to delete "${problemSummary || 'this session'}"? This action cannot be undone.`,
      confirmText: 'Delete',
      onConfirm: async () => {
        // Close dialog immediately to prevent double-clicks
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });

        try {
          const response = await fetch(`${DECOMPOSITION_API}/session/${sessionIdToDelete}`, {
            method: 'DELETE',
            headers: { Authorization: token },
          });
          // 404 means already deleted, which is fine
          if (!response.ok && response.status !== 404) {
            throw new Error('Failed to delete session');
          }

          // If we're currently viewing this session, start fresh
          if (sessionId === sessionIdToDelete && !agentId) {
            setSessionId(null);
            setCurrentQuestionnaire(null);
            setFormData({});
            setOtherModeFields({});
            setWorkflowResult(null);
            setMessages([{ id: Date.now(), type: 'bot', content: 'Welcome! Describe the problem you want to solve, and I\'ll help you build an AI agent for it.', timestamp: new Date() }]);
          }

          // Refresh the sessions list
          fetchSessionsList();
          addMessage('bot', `Deleted session: ${problemSummary || 'Session'}`, true); // Skip saving
        } catch (error) {
          console.error('Failed to delete session:', error);
          addMessage('bot', 'Failed to delete session. Please try again.', true);
        }
      }
    });
  };

  // CTA Handler: Go back to Questionnaire - shows confirmation first
  const handleGoBackToQuestionnaire = () => {
    if (workflowResult) {
      // Show confirmation dialog if there's a generated workflow
      setShowBackConfirmDialog(true);
    } else {
      // No workflow yet, go back directly
      confirmGoBackToQuestionnaire();
    }
  };

  // Fetch session data from backend (for page refresh or if state is lost)
  const fetchSessionData = async () => {
    if (!sessionId) return null;

    setIsLoadingSession(true);
    try {
      const response = await fetch(`${CONTEXT_API}/questions/session/${sessionId}`, {
        headers: { Authorization: token },
      });
      if (!response.ok) throw new Error('Failed to fetch session');

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching session:', error);
      return null;
    } finally {
      setIsLoadingSession(false);
    }
  };

  // Actually navigate back to questionnaire (after confirmation or if no workflow)
  const confirmGoBackToQuestionnaire = async () => {
    setShowBackConfirmDialog(false);

    // Try to use React state first, otherwise fetch from backend
    let questionnaire = round1Questionnaire;
    let responses = round1FormData;

    if (!questionnaire && sessionId) {
      // Fetch from backend if state is empty
      const sessionData = await fetchSessionData();
      if (sessionData) {
        questionnaire = sessionData.round1_questions;
        responses = sessionData.round1_responses || {};
      }
    }

    if (questionnaire) {
      setCurrentQuestionnaire(questionnaire);
      setFormData(responses);
      setOriginalFormData(JSON.parse(JSON.stringify(responses))); // Deep copy to track edits
      setCurrentRound(1);
      setCurrentView('questionnaire');
      setIsViewingAnswers(true); // Mark that we're viewing existing answers
      // Don't clear workflow result yet - only clear if they submit changes
      addMessage('bot', 'Here are your previous answers. You can review and modify them, or go back to your agent design.');
    } else {
      addMessage('bot', 'Could not retrieve questionnaire. Please start over.');
    }
  };

  // Check if form has been edited compared to original
  const hasFormBeenEdited = () => {
    if (!isViewingAnswers) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  // Go back to agent design without regenerating
  const handleBackToAgentDesign = () => {
    setCurrentView('workflow');
    setIsViewingAnswers(false);
    addMessage('bot', 'Back to your agent design. No changes were made to your answers.');
  };

  // Cancel going back
  const cancelGoBackToQuestionnaire = () => {
    setShowBackConfirmDialog(false);
  };

  // CTA Handler: Select flow and proceed to data generation
  const handleProceedToData = async () => {
    if (!workflowResult?.workflow_id || !sessionId) {
      addMessage('bot', 'Error: Missing workflow or session information. Please try again.');
      return;
    }

    // If agent already exists, check status and proceed to appropriate step
    if (agentId) {
      const currentStatus = agentDefinition?.status;

      // Handle based on current status
      if (currentStatus === 'variables_mapped' || currentStatus === 'VARIABLES_MAPPED') {
        // Generate prompts
        handleGeneratePrompts();
        return;
      }

      if (currentStatus === 'prompts_ready' || currentStatus === 'PROMPTS_READY') {
        // TODO: Implement agent execution (Phase 4)
        addMessage('bot', 'Agent execution is coming soon! Your prompts are ready.');
        return;
      }

      // Default: Map variables (status is schema_generated or similar)
      setIsSelectingFlow(true);
      setThinkingMessage('Mapping variables...');
      addMessage('bot', getRandomMessage('map_variables_start'));

      try {
        // Call map-variables API (SSE streaming for progress)
        await fetchWithSSE(
          `${DECOMPOSITION_API}/agent/${agentId}/map-variables?stream=true&user_id=${userId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token
            }
          },
          (message) => setThinkingMessage(message)
        );

        // After SSE completes, fetch the full updated agent definition
        // This ensures we get the nodes with field_sources populated
        const agentResponse = await fetch(`${DECOMPOSITION_API}/agent/${agentId}`, {
          headers: { 'Authorization': token }
        });

        if (agentResponse.ok) {
          const fullAgentData = await agentResponse.json();
          setAgentDefinition(fullAgentData);
          sendStateChange('variables_mapped', 'Variable mapping complete. Data flows connected between all nodes.',
            getRandomMessage('map_variables_complete'));
          // Auto-transition to Map Data view to show the variable mappings
          setCurrentView('map-data');
          // After 2s let the user absorb the mapping, then scroll to test data
          setTimeout(() => {
            setMapDataSubTab('test-data');
            setShouldScrollToTestData(true);
          }, 2000);
        } else {
          throw new Error('Failed to fetch updated agent definition');
        }
      } catch (error) {
        if (error.status === 402) { handleInsufficientCredits(); return; }
        console.error('Failed to map variables:', error);
        addMessage('bot', `Variable mapping encountered an issue: ${error.message || 'Please try again.'}`);
      } finally {
        setIsSelectingFlow(false);
        setThinkingMessage(null);
      }
      return;
    }

    setIsSelectingFlow(true);
    setThinkingMessage('Creating your agent...');
    addMessage('bot', 'Great choice! Creating your agent and generating schemas...');

    try {
      // Use SSE streaming to show progress during schema generation
      const agentData = await fetchWithSSE(
        `${DECOMPOSITION_API}/agent/select-flow?stream=true&user_id=${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            workflow_id: workflowResult.workflow_id,
            session_id: sessionId,
            flow_index: currentFlowIndex,
            user_id: userId,
            force_create: true, // Always create - add suffix if similar agent exists
            ...(jiraTicketKey && { jira_source: { ticket_key: jiraTicketKey, ticket_title: jiraTicketTitle || '' } }),
          })
        },
        (message) => setThinkingMessage(message)
      );

      if (!agentData) {
        throw new Error('Failed to create agent - no data returned');
      }

      setAgentId(agentData.agent_id);
      setAgentDefinition(agentData); // Store full agent definition with schemas
      initJiraPostPreview(agentData);

      // Refresh journey progress
      const progressResponse = await fetch(`${CONTEXT_API}/progress/${sessionId}`, {
        headers: { 'Authorization': token }
      });
      if (progressResponse.ok) {
        const progress = await progressResponse.json();
        setJourneyProgress(progress);
      }

      // Stay on workflow view to show generated schemas
      // (Data stage/VarMapper is Phase 2 - not implemented yet)
      setCurrentView('workflow');
      setDetailsExpanded(true); // Auto-expand details to show schemas
      sendStateChange('flow_selected', `User selected a flow. Agent created with ${agentData.nodes?.length || 0} nodes. Schemas generated.`,
        `Agent created successfully! Schemas have been generated for ${agentData.nodes?.length || 0} nodes. Check the Node Details tab to see the generated input/output schemas.`);
    } catch (error) {
      if (error.status === 402) { handleInsufficientCredits(); return; }
      console.error('Failed to select flow:', error);

      // Handle similar agent exists - ask user what to do
      if (error.code === 'SIMILAR_AGENT_EXISTS' && error.existingAgent) {
        setIsSelectingFlow(false);
        setThinkingMessage(null);

        setConfirmDialog({
          isOpen: true,
          title: 'Similar Agent Exists',
          message: `You already have an agent named "${error.existingAgent.concept_name}". Would you like to load it, or create a new one?`,
          confirmText: 'Create New',
          cancelText: 'Load Existing',
          onConfirm: async () => {
            setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
            // Create new with force_create flag
            setIsSelectingFlow(true);
            setThinkingMessage('Creating new agent...');
            try {
              const agentData = await fetchWithSSE(
                `${DECOMPOSITION_API}/agent/select-flow?stream=true&user_id=${userId}`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                  },
                  body: JSON.stringify({
                    workflow_id: workflowResult.workflow_id,
                    session_id: sessionId,
                    flow_index: currentFlowIndex,
                    user_id: userId,
                    force_create: true,
                    ...(jiraTicketKey && { jira_source: { ticket_key: jiraTicketKey, ticket_title: jiraTicketTitle || '' } }),
                  })
                },
                (message) => setThinkingMessage(message)
              );

              if (agentData) {
                setAgentId(agentData.agent_id);
                setAgentDefinition(agentData);
                initJiraPostPreview(agentData);
                setCurrentView('workflow');
                setDetailsExpanded(true);
                addMessage('bot', `Created new agent: ${agentData.concept_name}`);
              }
            } catch (e) {
              console.error('Failed to create new agent:', e);
              addMessage('bot', `Error creating agent: ${e.message}`);
            } finally {
              setIsSelectingFlow(false);
              setThinkingMessage(null);
            }
          },
          onCancel: async () => {
            setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
            // Load existing agent
            try {
              const response = await fetch(`${DECOMPOSITION_API}/agent/${error.existingAgent.agent_id}`, {
                headers: { Authorization: token },
              });
              if (response.ok) {
                const agentData = await response.json();
                setAgentId(agentData.agent_id);
                setAgentDefinition(agentData);
                initJiraPostPreview(agentData);
                setCurrentView('workflow');
                setDetailsExpanded(true);
                addMessage('bot', `Loaded existing agent: ${agentData.concept_name}`);
              }
            } catch (loadError) {
              console.error('Failed to load existing agent:', loadError);
              addMessage('bot', 'Failed to load existing agent.');
            }
          }
        });
        return;
      }

      // Handle duplicate agent error (same session) - load existing agent
      if (error.code === 'DUPLICATE_AGENT' && error.existingAgentId) {
        addMessage('bot', `An agent already exists for this session: "${error.conceptName}". Loading it now...`);
        try {
          const response = await fetch(`${DECOMPOSITION_API}/agent/${error.existingAgentId}`, {
            headers: { Authorization: token },
          });
          if (response.ok) {
            const agentData = await response.json();
            setAgentId(agentData.agent_id);
            setAgentDefinition(agentData);
            initJiraPostPreview(agentData);
            setCurrentView('workflow');
            setDetailsExpanded(true);
            addMessage('bot', `Loaded existing agent: ${agentData.concept_name}`);
          }
        } catch (loadError) {
          console.error('Failed to load existing agent:', loadError);
        }
      } else {
        addMessage('bot', `Error creating agent: ${error.message}. Please try again.`);
      }
    } finally {
      setIsSelectingFlow(false);
      setThinkingMessage(null);
    }
  };

  // ---- Flow Editing Handlers ----

  const handleEditFlow = () => {
    const currentNodes = (workflowResult.flows?.[currentFlowIndex]?.workflow_steps || []).map((step, idx) => ({
      alias: step.node?.display_name || `Node ${idx + 1}`,
      description: step.node?.description || step.description || '',
      type: step.node?.type || 'promptdel',
    }));
    setEditedNodes([...currentNodes]);
    setNodeValidations({});
    setEditingNodeIndex(null);
    setIsEditingFlow(true);
    setChatOpen(false); // Minimize chat to focus on flow editing
  };

  const handleCancelEdit = () => {
    setIsEditingFlow(false);
    setEditedNodes(null);
    setEditingNodeIndex(null);
    setNodeValidations({});
    setDraftNodeValues(null);
    setChatOpen(true); // Restore chat
  };

  const handleNodeEdit = (index, updatedNode) => {
    const updated = [...editedNodes];
    updated[index] = updatedNode;
    setEditedNodes(updated);
    // Clear this node's validation (content changed)
    setNodeValidations(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleApplySuggestion = (index, suggestion) => {
    // Apply the LLM's suggestion and auto-mark as valid
    const updated = [...editedNodes];
    updated[index] = { ...updated[index], alias: suggestion.alias, description: suggestion.description };
    setEditedNodes(updated);
    setNodeValidations(prev => ({
      ...prev,
      [index]: { valid: true, explanation: 'Using the suggested name and description.', suggestion: null, validated: true }
    }));
  };

  const handleAddNode = (afterIndex) => {
    if (editedNodes.length >= 5) return;
    const newNode = { alias: '', description: '', type: 'promptdel' };
    const updated = [...editedNodes];
    updated.splice(afterIndex + 1, 0, newNode);
    setEditedNodes(updated);
    setEditingNodeIndex(afterIndex + 1);
    setDraftNodeValues({ alias: '', description: '' });
    // Shift validation indices
    const shifted = {};
    Object.entries(nodeValidations).forEach(([k, v]) => {
      const ki = parseInt(k);
      shifted[ki >= afterIndex + 1 ? ki + 1 : ki] = v;
    });
    setNodeValidations(shifted);
  };

  const handleRemoveNode = (index) => {
    if (editedNodes.length <= 1) return;
    const updated = editedNodes.filter((_, i) => i !== index);
    setEditedNodes(updated);
    if (editingNodeIndex === index) setEditingNodeIndex(null);
    else if (editingNodeIndex > index) setEditingNodeIndex(editingNodeIndex - 1);
    // Shift validation indices and invalidate neighbors
    const shifted = {};
    Object.entries(nodeValidations).forEach(([k, v]) => {
      const ki = parseInt(k);
      if (ki < index) shifted[ki] = v;
      else if (ki > index) shifted[ki - 1] = v;
    });
    // Invalidate neighbors (their context changed)
    if (shifted[index]) shifted[index] = { ...shifted[index], valid: false, validated: false };
    if (index > 0 && shifted[index - 1]) shifted[index - 1] = { ...shifted[index - 1], valid: false, validated: false };
    setNodeValidations(shifted);
  };

  const handleValidateNode = async (index, draftAlias, draftDescription) => {
    setIsValidatingNode(true);
    try {
      // Build nodes list with the draft values at the edited index
      const nodesForValidation = editedNodes.map((n, i) => i === index
        ? { alias: draftAlias, description: draftDescription, type: n.type }
        : { alias: n.alias, description: n.description, type: n.type }
      );
      const response = await fetch(`${DECOMPOSITION_API}/flow/validate-node-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token },
        body: JSON.stringify({
          workflow_description: workflowResult.flows?.[currentFlowIndex]?.flow_summary || workflowResult.flows?.[currentFlowIndex]?.description || '',
          nodes: nodesForValidation,
          edited_node_index: index,
          edited_alias: draftAlias,
          edited_description: draftDescription,
        })
      });
      const data = await response.json();
      setNodeValidations(prev => ({ ...prev, [index]: { ...data, validated: true } }));

      // Only commit draft to editedNodes when validation passes
      if (data.valid) {
        const updated = [...editedNodes];
        updated[index] = { ...updated[index], alias: draftAlias, description: draftDescription };
        setEditedNodes(updated);
      }
    } catch (error) {
      console.error('Validation failed:', error);
      // Set an error state so the result view can show it
      setNodeValidations(prev => ({
        ...prev,
        [index]: { valid: false, explanation: 'Validation request failed. Please try again.', suggestion: null, validated: false }
      }));
    } finally {
      setIsValidatingNode(false);
    }
  };

  // Compute whether all edited/added nodes are validated
  const allEditedNodesValidated = isEditingFlow && editedNodes && editedNodes.length > 0 &&
    editedNodes.every((node, idx) => {
      const originalNodes = workflowResult.flows?.[currentFlowIndex]?.workflow_steps || [];
      const original = originalNodes[idx]?.node;
      const isUnchanged = original &&
        node.alias === (original.display_name || '') &&
        node.description === (original.business_function || '');
      return isUnchanged || nodeValidations[idx]?.valid === true;
    });

  const handleFinalizeEdits = async () => {
    setIsSelectingFlow(true);
    setThinkingMessage('Creating your agent with edited flow...');
    addMessage('bot', 'Finalizing your edited flow and generating schemas...');

    try {
      const agentData = await fetchWithSSE(
        `${DECOMPOSITION_API}/agent/select-flow?stream=true&user_id=${userId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token },
          body: JSON.stringify({
            workflow_id: workflowResult.workflow_id,
            session_id: sessionId,
            flow_index: currentFlowIndex,
            user_id: userId,
            force_create: true,
            edited_nodes: editedNodes,
            ...(jiraTicketKey && { jira_source: { ticket_key: jiraTicketKey, ticket_title: jiraTicketTitle || '' } }),
          })
        },
        (message) => setThinkingMessage(message)
      );

      if (!agentData) {
        throw new Error('Failed to create agent - no data returned');
      }

      setAgentId(agentData.agent_id);
      setAgentDefinition(agentData);
      initJiraPostPreview(agentData);
      setIsEditingFlow(false);
      setEditedNodes(null);
      setEditingNodeIndex(null);
      setNodeValidations({});
      setChatOpen(true); // Restore chat
      setCurrentView('workflow');
      setDetailsExpanded(true);
      sendStateChange('flow_selected', `User finalized an edited flow. Agent created with ${agentData.nodes?.length || 0} nodes. Schemas generated.`,
        `Agent created with your edited flow! Schemas generated for ${agentData.nodes?.length || 0} nodes.`);
    } catch (error) {
      if (error.status === 402) { handleInsufficientCredits(); return; }
      console.error('Failed to finalize edited flow:', error);
      addMessage('bot', `Error creating agent: ${error.message}. Please try again.`);
    } finally {
      setIsSelectingFlow(false);
      setThinkingMessage(null);
    }
  };

  // Called when user clicks "Proceed anyway" after clarity cap
  const handleProceedAnyway = async () => {
    setLowClarityMode(false);
    setClarificationRound(0);
    setMissingContextItems([]);
    setAdditionalDetails('');
    setRound1FormData(formData);
    addMessage('bot', 'Got it — proceeding with what I have. I\'ll make reasonable assumptions where needed.');
    await finalizeContextAndDecompose();
  };

  const finalizeContextAndDecompose = async () => {
    try {
      // Show workflow skeleton immediately for the entire finalize+decompose flow
      setIsDecomposing(true);

      // Step 1: Finalize context with SSE
      setThinkingMessage('Compiling all gathered context...');

      const finalData = await fetchWithSSE(
        `${CONTEXT_API}/questions/finalize-context?stream=true&user_id=${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            session_id: sessionId
          })
        },
        (message) => setThinkingMessage(message)
      );

      if (!finalData) throw new Error('Failed to finalize context');

      const mcpMessage = finalData.mcp_enabled ?
        ' with MCP integration enabled for intelligent API automation!' : '.';

      addMessage('bot', `Perfect! I've gathered all the context I need${mcpMessage} Now I'm generating your AI Agent...`);

      // Step 2: Decompose with SSE
      setIsDecomposing(true);
      setThinkingMessage('Starting AI agent design...');

      const workflowData = await fetchWithSSE(
        `${DECOMPOSITION_API}/decompose/problem?stream=true&user_id=${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            session_id: sessionId,
            final_context: finalData.final_context,
            ready_for_decomposition: true
          })
        },
        (message) => setThinkingMessage(message)
      );

      setIsDecomposing(false);
      setThinkingMessage(null);

      if (!workflowData) {
        throw new Error('No workflow data received');
      }

      // Sort flows to ensure consistent order: balanced/recommended (0), simple (1), thorough (2)
      if (workflowData.flows && workflowData.flows.length > 1) {
        const flowOrder = { 'balanced': 0, 'simple': 1, 'thorough': 2 };
        workflowData.flows.sort((a, b) => {
          const aType = a.flow_type?.toLowerCase() || (a.label?.toLowerCase().includes('simple') ? 'simple' : a.label?.toLowerCase().includes('thorough') || a.label?.toLowerCase().includes('comprehensive') ? 'thorough' : 'balanced');
          const bType = b.flow_type?.toLowerCase() || (b.label?.toLowerCase().includes('simple') ? 'simple' : b.label?.toLowerCase().includes('thorough') || b.label?.toLowerCase().includes('comprehensive') ? 'thorough' : 'balanced');
          return (flowOrder[aType] ?? 1) - (flowOrder[bType] ?? 1);
        });
      }

      setWorkflowResult(workflowData);
      setCurrentFlowIndex(0);
      setCurrentNodeIndex(0);
      setCurrentView('workflow');

      // Refresh journey progress after decompose completes
      try {
        const progressResponse = await fetch(`${CONTEXT_API}/progress/${sessionId}`, {
          headers: { 'Authorization': token }
        });
        if (progressResponse.ok) {
          const progress = await progressResponse.json();
          setJourneyProgress(progress);
        }
      } catch (e) {
        console.error('Failed to refresh journey progress:', e);
      }

      // Scroll to top when workflow view loads
      setTimeout(() => {
        contentContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);

      // Support both new flows format and legacy primary_workflow
      const currentWorkflow = workflowData.flows?.[0]?.workflow_steps || workflowData.primary_workflow || [];
      const promptdelCount = currentWorkflow.filter(step => step.node?.type === 'promptdel').length;
      const flowCount = workflowData.flows?.length || 1;
      const mcpNodeMessage = finalData.mcp_enabled ?
        ' The final node is MCP-enabled for intelligent API decision making!' : '';

      // Build informative completion message
      if (flowCount > 1 && workflowData.flows) {
        sendStateChange('schema_generated', `Decomposition complete. Generated ${flowCount} flow options for the agent.`,
          `I've generated ${flowCount} different flow options for your AI Agent. Select one below to continue.`);
      } else {
        const completionMessage = `Your AI Agent has been generated with ${currentWorkflow.length} steps.${mcpNodeMessage}`;
        sendStateChange('schema_generated', `Decomposition complete. Generated a workflow with ${currentWorkflow.length} steps.`,
          completionMessage);
      }

      setCurrentQuestionnaire(null);
      setCurrentRound(0);
    } catch (error) {
      if (error.status === 402) { handleInsufficientCredits(); return; }
      setIsDecomposing(false);
      setThinkingMessage(null);
      addMessage('bot', 'Sorry, there was an error generating your AI agent. Please try again.');
      console.error('Error:', error);
    }
  };

  const handleGeneratePrompts = async () => {
    if (!agentId) {
      addMessage('bot', 'Error: No agent ID found. Please select a flow and map variables first.');
      return;
    }

    // Check if variables are mapped
    if (agentDefinition?.status !== 'variables_mapped' && agentDefinition?.status !== 'VARIABLES_MAPPED') {
      addMessage('bot', 'Please map variables first before generating prompts. Go to the Map Data tab.');
      return;
    }

    setIsGeneratingPrompts(true);
    addMessage('user', 'Generate prompts for my agent');

    try {
      const response = await fetch(`${PROMPT_GENERATION_API}/agent/${agentId}/generate-prompts?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      });

      if (response.status === 402) { handleInsufficientCredits(); return; }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate prompts');
      }

      const promptsData = await response.json();
      setGeneratedPrompts(promptsData);
      setCurrentView('prompts');

      sendStateChange('prompts_generated', `Generated ${promptsData.generated_prompts?.length || 0} prompts for the agent.`,
        `Successfully generated ${promptsData.generated_prompts?.length || 0} functional prompts for your AI agent!`);
    } catch (error) {
      addMessage('bot', `Sorry, there was an error generating prompts: ${error.message}`);
    } finally {
      setIsGeneratingPrompts(false);
    }
  };

  // ── Auto-pilot: chain all steps from flow selection to prototype ──

  // Brief delay between auto-pilot steps (non-blocking, respects pause)
  const autoPilotDelay = (ms) => {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, ms);
      takeOverRef.current = () => {
        clearTimeout(timer);
        reject(new Error('User took over'));
      };
    });
  };

  // 3-2-1 countdown before switching screens — visual transition indicator
  const showScreenTransition = (label) => {
    return new Promise((resolve, reject) => {
      setAutoPilotNextLabel(label);
      let count = 3;
      setAutoPilotCountdownValue(count);

      const interval = setInterval(() => {
        count--;
        if (count <= 0) {
          clearInterval(interval);
          setAutoPilotCountdownValue(null);
          setAutoPilotNextLabel('');
          resolve();
        } else {
          setAutoPilotCountdownValue(count);
        }
      }, 1000);

      takeOverRef.current = () => {
        clearInterval(interval);
        setAutoPilotCountdownValue(null);
        setAutoPilotNextLabel('');
        reject(new Error('User took over'));
      };
    });
  };

  // Wait if paused — resolves immediately if not paused, else waits for resume click
  const autoPilotCheckPause = () => {
    return new Promise((resolve, reject) => {
      if (!isAutoPilotPausedRef.current) {
        resolve();
        return;
      }
      // Store resolve so handleResume can call it
      autoPilotResumeRef.current = resolve;
      takeOverRef.current = () => {
        autoPilotResumeRef.current = null;
        reject(new Error('User took over'));
      };
    });
  };

  const handlePauseAutoPilot = () => {
    isAutoPilotPausedRef.current = true;
    setIsAutoPilotPaused(true);
  };

  const handleResumeAutoPilot = () => {
    isAutoPilotPausedRef.current = false;
    setIsAutoPilotPaused(false);
    if (autoPilotResumeRef.current) {
      autoPilotResumeRef.current();
      autoPilotResumeRef.current = null;
    }
  };

  const runAutoPilot = async () => {
    if (!workflowResult?.workflow_id || !sessionId) {
      addMessage('bot', 'I need a completed questionnaire and workflow options first.');
      return;
    }

    setIsAutoPilot(true);
    setAutoPilotStep(1);

    try {
      // Step 1: Select flow and generate schemas
      addMessage('bot', 'Starting auto-pilot! Selecting the recommended flow and generating schemas...');
      setCurrentView('workflow');
      setIsSelectingFlow(true);
      setThinkingMessage('Creating your agent...');

      const agentData = await fetchWithSSE(
        `${DECOMPOSITION_API}/agent/select-flow?stream=true&user_id=${userId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token },
          body: JSON.stringify({
            workflow_id: workflowResult.workflow_id,
            session_id: sessionId,
            flow_index: currentFlowIndex || 0,
            user_id: userId,
            force_create: true,
            ...(jiraTicketKey && { jira_source: { ticket_key: jiraTicketKey, ticket_title: jiraTicketTitle || '' } }),
          })
        },
        (message) => setThinkingMessage(message)
      );

      if (!agentData) throw new Error('Failed to create agent');
      setAgentId(agentData.agent_id);
      setAgentDefinition(agentData);
      initJiraPostPreview(agentData);
      setIsSelectingFlow(false);
      setThinkingMessage(null);
      addMessage('bot', `Agent created with ${agentData.nodes?.length || 0} nodes. Mapping variables next...`, true);
      await showScreenTransition('Map Data');
      await autoPilotCheckPause();

      // Step 2: Map variables
      setAutoPilotStep(2);
      setCurrentView('map-data');
      setThinkingMessage('Mapping variables...');

      await fetchWithSSE(
        `${DECOMPOSITION_API}/agent/${agentData.agent_id}/map-variables?stream=true&user_id=${userId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token },
        },
        (message) => setThinkingMessage(message)
      );

      const agentResponse = await fetch(`${DECOMPOSITION_API}/agent/${agentData.agent_id}`, {
        headers: { 'Authorization': token }
      });
      if (agentResponse.ok) {
        const updatedAgent = await agentResponse.json();
        setAgentDefinition(updatedAgent);
      }
      setThinkingMessage(null);
      addMessage('bot', 'Variables mapped! Generating prompts...', true);
      await showScreenTransition('Prompts');
      await autoPilotCheckPause();

      // Step 3: Generate prompts
      setAutoPilotStep(3);
      setIsGeneratingPrompts(true);
      setCurrentView('prompts');

      const promptsResponse = await fetch(`${PROMPT_GENERATION_API}/agent/${agentData.agent_id}/generate-prompts?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token },
      });

      if (promptsResponse.status === 402) { handleInsufficientCredits(); return; }
      if (!promptsResponse.ok) throw new Error('Failed to generate prompts');
      const promptsData = await promptsResponse.json();
      setGeneratedPrompts(promptsData);
      setIsGeneratingPrompts(false);
      addMessage('bot', `Generated ${promptsData.generated_prompts?.length || 0} prompts! You can test individual nodes here.`, true);
      await showScreenTransition('Execute');
      await autoPilotCheckPause();

      // Step 4: Execute agent with sample data
      setAutoPilotStep(4);

      // Preflight credit check (EventSource can't detect 402)
      if (!await checkCreditsAvailable('execute')) {
        handleInsufficientCredits();
        return;
      }

      setIsExecuting(true);
      setExecutionStatus('running');
      setExecutionNodes([]);
      setCurrentExecutingNodeIndex(-1);
      setExecutionCarouselIndex(0);
      setTotalExecutionNodes(0);
      setIsPromptFlowExpanded(true);
      setCurrentView('execute');
      addMessage('bot', 'Running your agent with sample data...', true);

      // Run execution via SSE and wait for it to complete
      const execResultNodes = await new Promise((resolve, reject) => {
        const eventSource = new EventSource(
          `${EXECUTION_API}/agent/${agentData.agent_id}/run/stream?sample_index=0&user_id=${userId}&token=${encodeURIComponent(token)}`
        );

        takeOverRef.current = () => {
          eventSource.close();
          reject(new Error('User took over'));
        };

        eventSource.addEventListener('execution_started', (e) => {
          const data = JSON.parse(e.data);
          setExecutionId(data.executionId);
          setIsExecuting(true);
          if (data.totalNodes) setTotalExecutionNodes(data.totalNodes);
        });

        eventSource.addEventListener('node_started', (e) => {
          const data = JSON.parse(e.data);
          setCurrentExecutingNodeIndex(data.nodeIndex);
          setExecutionCarouselIndex(data.nodeIndex);
          setExecutionNodes(prev => {
            const updated = [...prev];
            updated[data.nodeIndex] = {
              nodeId: data.nodeId,
              alias: data.alias,
              nodeType: data.nodeType,
              status: 'running',
              input: data.input,
              inputDisplay: data.inputDisplay,
              output: null, outputDisplay: null, validation: null, durationMs: null
            };
            return updated;
          });
        });

        eventSource.addEventListener('node_completed', (e) => {
          const data = JSON.parse(e.data);
          setExecutionNodes(prev => {
            const updated = [...prev];
            updated[data.nodeIndex] = {
              ...updated[data.nodeIndex],
              status: 'completed',
              output: data.output,
              outputDisplay: data.display,
              validation: data.validation,
              durationMs: data.durationMs,
              tldr: null, tldrLoading: true
            };
            return updated;
          });
        });

        eventSource.addEventListener('node_tldr', (e) => {
          const data = JSON.parse(e.data);
          setExecutionNodes(prev => {
            const updated = [...prev];
            if (updated[data.nodeIndex]) {
              updated[data.nodeIndex] = { ...updated[data.nodeIndex], tldr: data.tldr, tldrLoading: false };
            }
            return updated;
          });
        });

        eventSource.addEventListener('node_failed', (e) => {
          const data = JSON.parse(e.data);
          setExecutionNodes(prev => {
            const updated = [...prev];
            updated[data.nodeIndex] = { ...updated[data.nodeIndex], status: 'failed', error: data.error };
            return updated;
          });
        });

        eventSource.addEventListener('brainwave_intercepted', (e) => {
          const data = JSON.parse(e.data);
          setExecutionNodes(prev => {
            const updated = [...prev];
            updated[data.nodeIndex] = { ...updated[data.nodeIndex], status: 'correcting', error: data.error, brainwaveMessage: data.message };
            return updated;
          });
        });

        eventSource.addEventListener('brainwave_corrected', (e) => {
          const data = JSON.parse(e.data);
          setExecutionNodes(prev => {
            const updated = [...prev];
            updated[data.nodeIndex] = {
              ...updated[data.nodeIndex],
              status: 'completed', output: data.output, outputDisplay: data.display,
              validation: data.validation, durationMs: data.durationMs,
              brainwaveCorrected: true, correctionDetails: data.correctionDetails,
              tldr: null, tldrLoading: true
            };
            return updated;
          });
        });

        eventSource.addEventListener('execution_completed', () => {
          setExecutionStatus('completed');
          setIsExecuting(false);
          setCurrentExecutingNodeIndex(-1);
          eventSource.close();
          resolve(executionNodesRef.current);
        });

        eventSource.addEventListener('execution_failed', (e) => {
          const data = JSON.parse(e.data);
          setExecutionStatus('failed');
          setIsExecuting(false);
          setCurrentExecutingNodeIndex(-1);
          eventSource.close();
          addMessage('bot', `Execution failed: ${data.error}`);
          resolve(null); // Continue auto-pilot even on failure
        });

        eventSource.onerror = () => {
          setExecutionStatus('failed');
          setIsExecuting(false);
          eventSource.close();
          resolve(null); // Continue auto-pilot even on error
        };
      });

      // Step 5: Byte analyzes execution results
      setAutoPilotStep(5);
      if (execResultNodes && execResultNodes.length > 0) {
        // Instant client-side summary
        const quickSummary = buildExecutionSummary(execResultNodes);
        if (quickSummary) addMessage('bot', quickSummary, true);
        // Blocking LLM analysis
        const execContext = buildExecutionContext(execResultNodes);
        await awaitByteAnalysis('execution_complete',
          `Agent execution finished. Here are the results:\n${execContext}\n\nProvide a brief, insightful assessment of the execution quality. Mention specific nodes by name. If brainwave corrected anything, mention it. Be encouraging but honest. 2-3 sentences max.`
        );
      } else {
        addMessage('bot', 'Execution complete! Review the results.', true);
      }
      await showScreenTransition('Prototype');
      await autoPilotCheckPause();

      // Step 6: Navigate to prototype, auto-execute, and analyze
      setAutoPilotStep(6);
      setProtoAutoExecute(true); // Tell PrototypeView to auto-fill + run
      setCurrentView('prototype');

      // Wait for prototype config to be generated (poll via ref)
      const protoConfig = await new Promise((resolve) => {
        let attempts = 0;
        const check = () => {
          attempts++;
          const config = prototypeConfigRef.current;
          if (config && config.appName) {
            resolve(config);
          } else if (attempts > 30) {
            resolve(null); // Timeout after ~15s
          } else {
            setTimeout(check, 500);
          }
        };
        setTimeout(check, 1000); // Give PrototypeView a moment to mount
      });

      // Byte comments on prototype config
      if (protoConfig) {
        const quickProtoSummary = buildPrototypeSummary(protoConfig);
        if (quickProtoSummary) addMessage('bot', quickProtoSummary, true);
      }

      // Wait for prototype execution to complete (triggered by autoExecute prop)
      addMessage('bot', 'Running the prototype with sample data...', true);
      await new Promise((resolve) => {
        protoAutoExecuteResolveRef.current = resolve;
        // Timeout safety: resolve after 60s if execution doesn't complete
        setTimeout(() => {
          if (protoAutoExecuteResolveRef.current) {
            protoAutoExecuteResolveRef.current();
            protoAutoExecuteResolveRef.current = null;
          }
        }, 60000);
      });

      // Byte analyzes prototype execution results
      if (protoConfig) {
        const protoContext = `Prototype config: App name="${protoConfig.appName}", ${protoConfig.inputFields?.length || 0} input fields (${(protoConfig.inputFields || []).map(f => f.label).join(', ')}), theme=${protoConfig.theme}, submit button="${protoConfig.submitLabel}", primary color=${protoConfig.primaryColor}. The prototype was executed with sample data and produced output.`;
        await awaitByteAnalysis('config_generated',
          `Prototype executed successfully. ${protoContext}\n\nProvide a brief assessment of the prototype. Mention the app name, that it ran with sample data, and suggest what the user can customize (colors, labels, theme). Be encouraging. 2-3 sentences max.`
        );
      } else {
        addMessage('bot', 'Your prototype is ready! Feel free to customize it — just ask me to change anything.', true);
      }

      // Step 7: Done — hand back control
      setAutoPilotStep(7);
      setShowHandbackBanner(true);
      addMessage('bot', 'All done! I\'ve set everything up for you. You\'re in control now — feel free to explore and customize.', true);
      setTimeout(() => setShowHandbackBanner(false), 6000);

    } catch (err) {
      if (err.status === 402) { handleInsufficientCredits(); return; }
      if (err.message === 'User took over') {
        addMessage('bot', 'Auto-pilot paused. You\'re in control now — continue from where I left off!');
      } else {
        addMessage('bot', `Auto-pilot hit an issue: ${err.message}. You can continue manually.`);
      }
    } finally {
      setIsAutoPilot(false);
      setAutoPilotStep(0);
      setIsAutoPilotPaused(false);
      setIsSelectingFlow(false);
      setIsGeneratingPrompts(false);
      setIsExecuting(false);
      setThinkingMessage(null);
      setAutoPilotCountdownValue(null);
      setAutoPilotNextLabel('');
      autoPilotResumeRef.current = null;
    }
  };

  const handleTakeOver = () => {
    if (takeOverRef.current) {
      takeOverRef.current();
      takeOverRef.current = null;
    }
    setIsAutoPilot(false);
    setAutoPilotStep(0);
    setIsAutoPilotPaused(false);
    autoPilotResumeRef.current = null;
  };

  // Test a prompt by running it with sample data
  // Helper to strip markdown code blocks from output
  const stripMarkdownCodeBlocks = (text) => {
    if (!text) return text;
    // Remove ```json or ``` at start and ``` at end
    let cleaned = text.trim();
    if (cleaned.startsWith('```')) {
      // Remove opening ```json or ```
      cleaned = cleaned.replace(/^```(?:json)?\s*\n?/, '');
      // Remove closing ```
      cleaned = cleaned.replace(/\n?```\s*$/, '');
    }
    return cleaned.trim();
  };

  const handleTestPrompt = async () => {
    if (!generatedPrompts?.generated_prompts?.[selectedPromptNodeIndex]) {
      addMessage('bot', 'Error: No prompt selected to test.');
      return;
    }

    const selectedPrompt = generatedPrompts.generated_prompts[selectedPromptNodeIndex];
    const nodeKey = selectedPromptNodeIndex;
    const existingResult = promptTestResults[nodeKey];

    setIsRunningPromptTest(true);

    try {
      // Modify system prompt if JSON output is requested
      let systemPrompt = selectedPrompt.system_prompt;
      if (requestJsonOutput) {
        systemPrompt += '\n\nIMPORTANT: Return your response as valid JSON only. Do not wrap it in markdown code blocks or add any explanation.';
      }

      const response = await fetch(`${PROMPT_GENERATION_API}/test-prompt?user_id=${userId}&agent_id=${agentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          system_prompt: systemPrompt,
          user_prompt: selectedPrompt.user_prompt,
          input_data: existingResult?.input || null, // Use existing input on subsequent runs
          node_description: selectedPrompt.display_name
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to test prompt');
      }

      const result = await response.json();

      // Strip markdown code blocks from output if present
      const cleanedOutput = stripMarkdownCodeBlocks(result.output_data);

      // Store result per node
      setPromptTestResults(prev => ({
        ...prev,
        [nodeKey]: {
          input: result.input_data,
          output: cleanedOutput
        }
      }));

    } catch (error) {
      console.error('Error testing prompt:', error);
      addMessage('bot', `Error testing prompt: ${error.message}`);
    } finally {
      setIsRunningPromptTest(false);
    }
  };

  const handleGeneratePreview = async () => {
    if (!workflowResult) {
      addMessage('bot', 'Error: No agent found. Please generate an AI agent first.');
      return;
    }

    setIsGeneratingPreview(true);
    addMessage('user', `Generating ${dataGenerationSettings.numSamples} realistic data samples for preview...`);

    try {
      const schemaResponse = await fetch(`${DATA_GENERATION_API}/schema/plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          conceptId: workflowResult.concept_id,
          flow: (workflowResult.flows?.[currentFlowIndex]?.workflow_steps || workflowResult.primary_workflow || []).map(step => ({
            name: step.node?.display_name,
            description: step.node?.description
          })),
          purpose: dataGenerationSettings.purpose || workflowResult.workflow_summary,
          numRows: dataGenerationSettings.numRows
        })
      });

      if (!schemaResponse.ok) throw new Error('Failed to generate schema');
      const schemaData = await schemaResponse.json();
      setGeneratedSchema(schemaData);

      const previewResponse = await fetch(`${DATA_GENERATION_API}/data/preview/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          conceptId: workflowResult.concept_id,
          schemaVersion: schemaData.schemaVersion,
          numSamples: dataGenerationSettings.numSamples,
          ttlMinutes: 30
        })
      });

      if (!previewResponse.ok) throw new Error('Failed to generate preview data');

      const previewResult = await previewResponse.json();
      setPreviewData(previewResult);
      setEditedSamples(previewResult.sampleRows.map(row => ({ ...row })));
      setPreviewStep(1);
      setCurrentView('data-preview');

      addMessage('bot', `Generated ${previewResult.sampleRows.length} realistic sample rows! These show what your test data will look like. Edit them to match your preferred format, then I'll generate ${dataGenerationSettings.numRows} rows following your patterns.`);
    } catch (error) {
      addMessage('bot', `Sorry, there was an error generating preview: ${error.message}`);
      console.error('Error:', error);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleUpdatePreview = async () => {
    if (!previewData?.previewId || !editedSamples) {
      addMessage('bot', 'Error: No preview data to update.');
      return;
    }

    setIsLoading(true);
    addMessage('user', 'Learning patterns from your edits...');

    try {
      const response = await fetch(`${DATA_GENERATION_API}/data/preview/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          previewId: previewData.previewId,
          editedSamples: editedSamples
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 422 || response.status === 400) {
          console.log('Validation error details:', errorData);
          console.log('Edited samples being sent:', editedSamples);

          let errorMessage = 'Validation failed. ';
          if (errorData.error?.message) {
            errorMessage += errorData.error.message;
          }

          errorMessage += '\n\nTip: Check that your edits match the expected data types. Arrays should be in JSON format like ["item1", "item2"]';

          throw new Error(errorMessage);
        }

        throw new Error(`Failed to update preview: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      setLearnedPatterns(result.learnedPatterns);
      setPreviewStep(2);

      const patternCount = Object.keys(result.learnedPatterns || {}).length;
      addMessage('bot', `Perfect! I've learned ${patternCount} pattern${patternCount !== 1 ? 's' : ''} from your edits. Now I can generate ${dataGenerationSettings.numRows} rows that follow your preferred format. Click "Generate Final Data" when ready!`);
    } catch (error) {
      addMessage('bot', `Sorry, there was an error learning from your edits: ${error.message}`);
      console.error('Error:', error);

      console.error('Preview data:', previewData);
      console.error('Edited samples:', editedSamples);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateFromPreview = async () => {
    if (!previewData?.previewId) {
      addMessage('bot', 'Error: No preview session found.');
      return;
    }

    setIsGeneratingData(true);
    addMessage('user', `Generating ${dataGenerationSettings.numRows} rows using your preferred patterns...`);

    try {
      const response = await fetch(`${DATA_GENERATION_API}/data/generate/from-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          previewId: previewData.previewId,
          numRows: dataGenerationSettings.numRows,
          change_note: `Generated from preview with user patterns`
        })
      });

      if (!response.ok) throw new Error('Failed to generate final data');

      const dataResult = await response.json();
      setGeneratedData(dataResult);
      setPreviewStep(3);
      setCurrentView('data');

      addMessage('bot', `Successfully generated ${dataResult.numRows} rows of realistic test data following your patterns! The data is ready for testing your AI agent.`);
    } catch (error) {
      addMessage('bot', `Sorry, there was an error generating final data: ${error.message}`);
      console.error('Error:', error);
    } finally {
      setIsGeneratingData(false);
    }
  };

  const handleProviderSwitch = async (provider) => {
    try {
      const response = await fetch(`${DECOMPOSITION_API}/decompose/switch-provider?provider=${provider}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        }
      });

      if (response.ok) {
        setLlmProvider(provider);
        addMessage('bot', `Switched to ${provider.toUpperCase()} for agent generation.`);
      }
    } catch (error) {
      console.error('Error switching provider:', error);
    }
  };

  const handleRegenerateWorkflow = async () => {
    if (!workflowResult) return;

    setIsLoading(true);
    addMessage('user', 'Regenerating AI agent with current provider...');

    try {
      const response = await fetch(`${DECOMPOSITION_API}/decompose/${workflowResult.workflow_id}/regenerate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          regeneration_request: {
            timestamp: new Date().toISOString(),
            provider: llmProvider
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to regenerate workflow: ${response.status} ${response.statusText}. ${errorData.detail || ''}`);
      }

      const regenerationData = await response.json();

      if (!regenerationData.new_workflow_id) {
        throw new Error('Invalid regeneration response: missing new_workflow_id');
      }

      const newWorkflowResponse = await fetch(`${DECOMPOSITION_API}/decompose/${regenerationData.new_workflow_id}`, {
        headers: { Authorization: token },
      });

      if (!newWorkflowResponse.ok) {
        const errorData = await newWorkflowResponse.json().catch(() => ({}));
        throw new Error(`Failed to fetch new workflow: ${newWorkflowResponse.status} ${newWorkflowResponse.statusText}. ${errorData.detail || ''}`);
      }

      const newWorkflowData = await newWorkflowResponse.json();

      let processedWorkflow;

      // Check for new flows format first
      if (newWorkflowData.flows && Array.isArray(newWorkflowData.flows)) {
        processedWorkflow = {
          ...newWorkflowData,
          workflow_id: regenerationData.new_workflow_id
        };
      } else if (newWorkflowData.primary_workflow && Array.isArray(newWorkflowData.primary_workflow)) {
        processedWorkflow = {
          ...newWorkflowData,
          workflow_id: regenerationData.new_workflow_id
        };
      } else if (newWorkflowData.workflow_nodes && Array.isArray(newWorkflowData.workflow_nodes)) {
        processedWorkflow = {
          ...newWorkflowData,
          workflow_id: regenerationData.new_workflow_id,
          primary_workflow: newWorkflowData.workflow_nodes.map(node => ({
            position: node.position,
            node: {
              type: node.type,
              display_name: node.display_name,
              description: node.description,
              business_function: node.business_function,
              node_details: node.node_details,
              additional_tools: node.additional_tools,
              input_schema: node.input_schema,
              output_schema: node.output_schema
            },
            estimated_cost: node.estimated_cost,
            estimated_time: node.estimated_time
          }))
        };
      } else {
        console.error('Unexpected workflow data structure:', newWorkflowData);
        throw new Error('Unexpected workflow data structure returned from API');
      }

      setWorkflowResult(processedWorkflow);
      setCurrentFlowIndex(0);
      setCurrentNodeIndex(0);
      addMessage('bot', `AI agent regenerated successfully with ${llmProvider.toUpperCase()}!`);

      setGeneratedPrompts(null);
      setGeneratedData(null);
      setPreviewData(null);
      setEditedSamples(null);
      setPreviewStep(0);
      setLearnedPatterns(null);

    } catch (error) {
      console.error('Regeneration error:', error);
      addMessage('bot', `Sorry, there was an error regenerating the AI agent: ${error.message}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Jira Handlers ──────────────────────────────────────────────────────

  const handleConnectJira = () => {
    const width = 600, height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    window.open(
      `${JIRA_API}/authorize?user_id=${userId}`,
      'jira-oauth',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  const handlePostToJira = async () => {
    if (!jiraTicketKey || jiraPostStatus === 'posting' || jiraPostStatus === 'posted') return;
    setJiraPostStatus('posting');
    try {
      // Generate a share link for the prototype so it's publicly accessible
      let prototypeUrl = `${window.location.origin}/?view=prototype&agent=${agentId}`;
      try {
        const snapshot = prototypeConfig || { appName: agentDefinition?.concept_name || 'Agent' };
        const shareRes = await fetch(`${PROTOTYPE_API}/share`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token },
          body: JSON.stringify({ agentId: agentId || 'unknown', config: snapshot, agentDefinition: agentDefinition || null }),
        });
        if (shareRes.ok) {
          const { shareId } = await shareRes.json();
          prototypeUrl = `${window.location.origin}/?mode=share&sid=${shareId}`;
        }
      } catch (e) {
        // Fall back to direct link if share fails
        console.warn('Share link generation failed, using direct link:', e);
      }
      const designUrl = `${window.location.origin}/?view=workflow&agent=${agentId}`;

      const resp = await fetch(`${JIRA_API}/issue/${jiraTicketKey}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          user_id: userId,
          prototype_url: prototypeUrl,
          design_url: designUrl,
          agent_summary: jiraPostSummary,
          requirements: jiraPostRequirements,
        }),
      });

      if (resp.status === 403) {
        throw new Error('JIRA_RECONNECT');
      }
      if (!resp.ok) {
        throw new Error('Failed to post comment');
      }

      setJiraPostStatus('posted');
      addMessage('bot', `Posted to ${jiraTicketKey}!`);
    } catch (err) {
      console.error('Post to Jira failed:', err);
      setJiraPostStatus('error');
      if (err.message === 'JIRA_RECONNECT') {
        addMessage('bot', `Could not post — Jira write permission missing. Please reconnect Jira and try again.`);
      }
    }
  };

  const handleDismissJiraPost = () => {
    setJiraTicketKey(null);
  };

  const handleRetryJiraPost = () => {
    setJiraPostStatus(null);
  };

  const initJiraPostPreview = (agentData) => {
    if (jiraTicketKey && agentData) {
      const nodes = agentData.nodes || [];
      setJiraPostSummary(`${agentData.concept_name || 'Agent'} — ${nodes.length} node${nodes.length !== 1 ? 's' : ''}`);
      setJiraPostRequirements(nodes.map(n => `${n.alias}: ${n.description}`));
      setJiraPostStatus(null);
    }
  };

  const handleFetchJiraTicket = async (issueKey) => {
    if (!jiraConnected) {
      addMessage('bot', 'Connect your Jira account first to import tickets.', true);
      return null;
    }

    // Step 1: Fetch the ticket
    setIsJiraProcessing(true);
    setJiraFlowStep('consolidating');
    addMessage('bot', `Fetching ${issueKey} from Jira...`, true);

    let issue;
    try {
      const resp = await fetch(`${JIRA_API}/issue/${issueKey}?user_id=${userId}`, {
        headers: { Authorization: token },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to fetch ${issueKey}`);
      }
      issue = await resp.json();
    } catch (err) {
      addMessage('bot', `Could not fetch Jira ticket: ${err.message}`, true);
      setIsJiraProcessing(false);
      setJiraFlowStep(null);
      return null;
    }

    // Step 2: Consolidate via LLM
    addMessage('bot', `Analyzing ticket content — extracting requirements...`, true);
    try {
      const consResp = await fetch(`${JIRA_API}/consolidate?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ issue_data: issue }),
      });
      if (consResp.status === 402) { handleInsufficientCredits(); return; }
      if (!consResp.ok) throw new Error('Consolidation failed');
      const consolidated = await consResp.json();

      // Initialize checklist — all checked by default
      const initialChecklist = {};
      (consolidated.requirements || []).forEach(r => { initialChecklist[r.id] = true; });

      setJiraConsolidation(consolidated);
      setJiraChecklist(initialChecklist);
      setJiraFlowStep('checklist');
      setIsJiraProcessing(false);

      return { issue, consolidated };
    } catch (err) {
      addMessage('bot', `Could not analyze ticket: ${err.message}`, true);
      setIsJiraProcessing(false);
      setJiraFlowStep(null);
      return null;
    }
  };

  const handleJiraChecklistConfirm = () => {
    if (!jiraConsolidation) return;

    // Collect confirmed requirements
    const confirmed = (jiraConsolidation.requirements || []).filter(r => jiraChecklist[r.id]);
    if (confirmed.length === 0) {
      addMessage('bot', 'Please keep at least one requirement checked.', true);
      return;
    }

    // Check if there are gap questions
    const gaps = jiraConsolidation.gaps || [];
    // Always ask about trigger and output if not inferred
    const coreGaps = [];
    if (!jiraConsolidation.inferred_trigger) {
      coreGaps.push('What should trigger this workflow? (e.g. webhook, scheduled, manual, API call)');
    }
    if (!jiraConsolidation.inferred_output) {
      coreGaps.push('Where should the results go? (e.g. email, Slack, database, API response)');
    }
    const allGaps = [...coreGaps, ...gaps].slice(0, 4); // Max 4 questions

    if (allGaps.length > 0) {
      // Initialize gap answers
      const initialGapAnswers = {};
      allGaps.forEach(g => { initialGapAnswers[g] = ''; });
      setJiraGapAnswers(initialGapAnswers);
      setJiraFlowStep('gaps');
    } else {
      // No gaps — go straight to building
      handleJiraBuildContext(confirmed, {});
    }
  };

  const handleJiraGapSubmit = () => {
    const confirmed = (jiraConsolidation.requirements || []).filter(r => jiraChecklist[r.id]);
    // Include inferred values in gap answers
    const fullAnswers = { ...jiraGapAnswers };
    if (jiraConsolidation.inferred_trigger) {
      fullAnswers['trigger'] = jiraConsolidation.inferred_trigger;
    }
    if (jiraConsolidation.inferred_output) {
      fullAnswers['output'] = jiraConsolidation.inferred_output;
    }
    handleJiraBuildContext(confirmed, fullAnswers);
  };

  const handleJiraBuildContext = async (confirmedRequirements, gapAnswers) => {
    setJiraFlowStep('building');
    setIsJiraProcessing(true);
    addMessage('bot', 'Building workflow context from your requirements...', true);

    try {
      // Step 1: Build FinalContext via LLM
      const ctxResp = await fetch(`${JIRA_API}/build-context?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          confirmed_requirements: confirmedRequirements,
          gap_answers: gapAnswers,
          ticket_key: jiraConsolidation.ticket_key,
          ticket_title: jiraConsolidation.title,
        }),
      });
      if (ctxResp.status === 402) { handleInsufficientCredits(); return; }
      if (!ctxResp.ok) throw new Error('Context build failed');
      const finalContext = await ctxResp.json();

      addMessage('bot', 'Context ready! Generating your AI Agent...', true);

      // Step 2: Call decompose directly (skip Q1/Q2)
      setIsDecomposing(true);
      setThinkingMessage('Starting AI agent design...');

      const workflowData = await fetchWithSSE(
        `${DECOMPOSITION_API}/decompose/problem?stream=true&user_id=${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify({
            session_id: sessionId || `jira_${Date.now()}`,
            final_context: finalContext,
            ready_for_decomposition: true,
          }),
        },
        (message) => setThinkingMessage(message)
      );

      setIsDecomposing(false);
      setThinkingMessage(null);

      if (!workflowData) throw new Error('No workflow data received');

      // Sort flows
      if (workflowData.flows && workflowData.flows.length > 1) {
        const flowOrder = { 'balanced': 0, 'simple': 1, 'thorough': 2 };
        workflowData.flows.sort((a, b) => {
          const aType = a.flow_type?.toLowerCase() || (a.label?.toLowerCase().includes('simple') ? 'simple' : a.label?.toLowerCase().includes('thorough') || a.label?.toLowerCase().includes('comprehensive') ? 'thorough' : 'balanced');
          const bType = b.flow_type?.toLowerCase() || (b.label?.toLowerCase().includes('simple') ? 'simple' : b.label?.toLowerCase().includes('thorough') || b.label?.toLowerCase().includes('comprehensive') ? 'thorough' : 'balanced');
          return (flowOrder[aType] ?? 1) - (flowOrder[bType] ?? 1);
        });
      }

      // Set session state from decomposition response (Jira flow bypasses questionnaire)
      if (workflowData.session_id) {
        setSessionId(workflowData.session_id);
      }
      setCurrentRound(1); // Mark as having completed requirements

      setWorkflowResult(workflowData);
      setCurrentFlowIndex(0);
      setCurrentNodeIndex(0);
      setCurrentView('workflow');

      // Persist ticket key for "Post to Jira" on Prototype screen
      if (jiraConsolidation?.ticket_key) {
        setJiraTicketKey(jiraConsolidation.ticket_key);
        setJiraTicketTitle(jiraConsolidation.title || '');
      }

      // Clean up Jira flow state
      setJiraFlowStep(null);
      setJiraConsolidation(null);
      setJiraChecklist({});
      setJiraGapAnswers({});

      addMessage('bot', `Agent designed from ${jiraConsolidation?.ticket_key || 'Jira ticket'}! Review the workflow below.`);

    } catch (err) {
      if (err.status === 402) { handleInsufficientCredits(); return; }
      console.error('Jira build context failed:', err);
      addMessage('bot', `Failed to build agent from ticket: ${err.message}`, true);
      setIsDecomposing(false);
      setThinkingMessage(null);
    } finally {
      setIsJiraProcessing(false);
    }
  };

  const handleCopyPrompt = (promptText) => {
    navigator.clipboard.writeText(promptText);
    addMessage('bot', 'Prompt copied to clipboard!');
  };

  const handleCopyHint = (hintText) => {
    // Strip "e.g., " or "e.g." or "Example:" or "Example, " prefix and quotes from the text before copying
    const cleanedText = hintText
      .replace(/^e\.g\.,?\s*/i, '')
      .replace(/^example[,:]\s*/i, '')
      .replace(/^["']+|["']+$/g, ''); // Remove leading/trailing quotes (both single and double)
    navigator.clipboard.writeText(cleanedText);
  };

  const handleExportCode = async () => {
    if (!agentId || isExportingCode) return;

    // Preflight credit check (EventSource can't detect 402)
    if (!await checkCreditsAvailable('code_export')) {
      handleInsufficientCredits();
      return;
    }

    setIsExportingCode(true);
    setExportProgress({ step: 'connecting', message: 'Connecting...' });

    const url = `${EXECUTION_API.replace('/v1/execution', '/v1/codegen')}/agent/${agentId}/export?user_id=${userId}&token=${encodeURIComponent(token)}`;
    const eventSource = new EventSource(url);

    eventSource.addEventListener('progress', (e) => {
      const data = JSON.parse(e.data);
      setExportProgress({ step: data.step, message: data.message });
    });

    eventSource.addEventListener('completed', (e) => {
      const data = JSON.parse(e.data);
      setExportProgress({ step: 'downloading', message: `Downloading ${data.fileCount} files...` });

      // Decode base64 zip and trigger download
      const byteChars = atob(data.zipBase64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/zip' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      setIsExportingCode(false);
      setExportProgress(null);
      eventSource.close();
    });

    eventSource.addEventListener('failed', (e) => {
      const data = JSON.parse(e.data);
      addMessage('bot', `Export failed: ${data.error}`, true);
      setIsExportingCode(false);
      setExportProgress(null);
      eventSource.close();
    });

    eventSource.onerror = () => {
      // Only show error if we didn't already handle a 'failed' event
      if (isExportingCode) {
        addMessage('bot', 'Code export failed — please contact support if this issue persists.', true);
        setIsExportingCode(false);
        setExportProgress(null);
      }
      eventSource.close();
    };
  };

  const handleDownloadPrompts = () => {
    if (!generatedPrompts) return;

    const promptsJson = JSON.stringify(generatedPrompts, null, 2);
    const blob = new Blob([promptsJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow-prompts-${generatedPrompts.workflow_id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadData = () => {
    if (!generatedData || !generatedData.rows) return;

    const headers = Object.keys(generatedData.rows[0]).filter(key => key !== '_id');
    const csvContent = [
      headers.join(','),
      ...generatedData.rows.map(row =>
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow-test-data-${generatedData.conceptId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadExecutionResults = () => {
    if (!executionNodes || executionNodes.length === 0) return;

    const rows = [];
    executionNodes.forEach((node) => {
      if (!node) return;
      rows.push([`--- ${node.alias || node.nodeId} (${node.nodeType || ''}) ---`]);
      rows.push(['Section', 'Field', 'Value']);

      if (node.input && typeof node.input === 'object') {
        Object.entries(node.input).forEach(([key, value]) => {
          const val = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
          rows.push(['INPUT', key, val.includes(',') || val.includes('"') || val.includes('\n') ? `"${val.replace(/"/g, '""')}"` : val]);
        });
      }

      if (node.output && typeof node.output === 'object') {
        Object.entries(node.output).forEach(([key, value]) => {
          const val = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
          rows.push(['OUTPUT', key, val.includes(',') || val.includes('"') || val.includes('\n') ? `"${val.replace(/"/g, '""')}"` : val]);
        });
      }

      rows.push([]); // blank line between nodes
    });

    const csvContent = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `execution-results-${executionId || 'unknown'}-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSampleEdit = (rowIndex, field, value) => {
    setEditedSamples(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [field]: value };
      return updated;
    });
  };

  const handleAddColumn = () => {
    const newColumnName = prompt('Enter the new column name:');
    if (!newColumnName || !newColumnName.trim()) return;

    const columnName = newColumnName.trim().toLowerCase().replace(/\s+/g, '_');

    if (editedSamples.length > 0 && editedSamples[0].hasOwnProperty(columnName)) {
      alert('A column with this name already exists!');
      return;
    }

    setEditedSamples(prev =>
      prev.map(row => ({
        ...row,
        [columnName]: ''
      }))
    );

    addMessage('bot', `Added new column "${columnName}" to your data preview!`);
  };

  const handleRemoveColumn = (columnToRemove) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Column',
      message: `Are you sure you want to remove the "${columnToRemove}" column? This action cannot be undone.`,
      confirmText: 'Remove',
      onConfirm: () => {
        setEditedSamples(prev =>
          prev.map(row => {
            const newRow = { ...row };
            delete newRow[columnToRemove];
            return newRow;
          })
        );
        addMessage('bot', `Removed column "${columnToRemove}" from your data preview.`);
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
      }
    });
  };

  const handleAddRow = async () => {
    if (!previewData?.previewId || !editedSamples || editedSamples.length === 0) return;

    const templateRow = editedSamples[0];
    const newRow = {};

    Object.keys(templateRow).forEach(key => {
      if (key !== '_id') {
        if (typeof templateRow[key] === 'object' && Array.isArray(templateRow[key])) {
          newRow[key] = [];
        } else if (typeof templateRow[key] === 'object') {
          newRow[key] = {};
        } else {
          newRow[key] = '';
        }
      }
    });

    try {
      const response = await fetch(`${DATA_GENERATION_API}/data/preview/add-row`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token },
        body: JSON.stringify({
          previewId: previewData.previewId,
          newRow: newRow
        })
      });

      if (!response.ok) throw new Error('Failed to add row');

      setEditedSamples(prev => [...prev, newRow]);
      addMessage('bot', 'Added new row to preview data.');
    } catch (error) {
      addMessage('bot', `Error adding row: ${error.message}`);
    }
  };


  // Handler: Run execution via SSE (extracted from inline EventSource in execute view)
  const handleRunExecution = async (sampleIndex) => {
    if (!agentId) {
      addMessage('bot', 'Error: No agent selected.');
      return;
    }

    // Preflight credit check (EventSource can't detect 402)
    if (!await checkCreditsAvailable('execute')) {
      handleInsufficientCredits();
      return;
    }

    setIsExecuting(true);
    setExecutionStatus('running');
    setExecutionNodes([]);
    setCurrentExecutingNodeIndex(-1);
    setExecutionCarouselIndex(0);
    executionCarouselIndexRef.current = 0;
    setTotalExecutionNodes(0);
    setViewingHistoryRun(null);
    setExecutionSidebarOpen(false);
    setSwitchingToNode(null);
    setChatOpen(false);
    if (switchTimerRef.current) {
      clearTimeout(switchTimerRef.current);
      switchTimerRef.current = null;
    }

    try {
      const eventSource = new EventSource(
        `${EXECUTION_API}/agent/${agentId}/run/stream?sample_index=${sampleIndex}&user_id=${userId}&token=${encodeURIComponent(token)}`
      );

      eventSource.addEventListener('execution_started', (e) => {
        const data = JSON.parse(e.data);
        setExecutionId(data.executionId);
        if (data.totalNodes) setTotalExecutionNodes(data.totalNodes);
      });

      eventSource.addEventListener('node_started', (e) => {
        const data = JSON.parse(e.data);
        setCurrentExecutingNodeIndex(data.nodeIndex);
        if (data.nodeIndex !== executionCarouselIndexRef.current) {
          const targetAlias = data.alias || `Node ${data.nodeIndex + 1}`;
          setSwitchingToNode({ index: data.nodeIndex, alias: targetAlias });
          if (switchTimerRef.current) { clearTimeout(switchTimerRef.current); }
          switchTimerRef.current = setTimeout(() => {
            setExecutionCarouselIndex(data.nodeIndex);
            executionCarouselIndexRef.current = data.nodeIndex;
            setSwitchingToNode(null);
            switchTimerRef.current = null;
          }, 1500);
        } else {
          setExecutionCarouselIndex(data.nodeIndex);
        }
        setExecutionNodes(prev => {
          const updated = [...prev];
          updated[data.nodeIndex] = {
            nodeId: data.nodeId, alias: data.alias, nodeType: data.nodeType,
            status: 'running', input: data.input, inputDisplay: data.inputDisplay,
            output: null, outputDisplay: null, validation: null, durationMs: null
          };
          return updated;
        });
      });

      eventSource.addEventListener('node_completed', (e) => {
        const data = JSON.parse(e.data);
        setExecutionNodes(prev => {
          const updated = [...prev];
          updated[data.nodeIndex] = {
            ...updated[data.nodeIndex], status: 'completed',
            output: data.output, outputDisplay: data.display,
            validation: data.validation, durationMs: data.durationMs,
            tldr: null, tldrLoading: true
          };
          return updated;
        });
      });

      eventSource.addEventListener('node_tldr', (e) => {
        const data = JSON.parse(e.data);
        setExecutionNodes(prev => {
          const updated = [...prev];
          if (updated[data.nodeIndex]) {
            updated[data.nodeIndex] = { ...updated[data.nodeIndex], tldr: data.tldr, tldrLoading: false };
          }
          return updated;
        });
      });

      eventSource.addEventListener('node_failed', (e) => {
        const data = JSON.parse(e.data);
        setExecutionNodes(prev => {
          const updated = [...prev];
          updated[data.nodeIndex] = { ...updated[data.nodeIndex], status: 'failed', error: data.error };
          return updated;
        });
      });

      eventSource.addEventListener('brainwave_intercepted', (e) => {
        const data = JSON.parse(e.data);
        setExecutionNodes(prev => {
          const updated = [...prev];
          updated[data.nodeIndex] = { ...updated[data.nodeIndex], status: 'correcting', error: data.error, brainwaveMessage: data.message };
          return updated;
        });
      });

      eventSource.addEventListener('brainwave_corrected', (e) => {
        const data = JSON.parse(e.data);
        setExecutionNodes(prev => {
          const updated = [...prev];
          updated[data.nodeIndex] = {
            ...updated[data.nodeIndex], status: 'completed',
            output: data.output, outputDisplay: data.display,
            validation: data.validation, durationMs: data.durationMs,
            brainwaveCorrected: true, correctionDetails: data.correctionDetails,
            tldr: null, tldrLoading: true
          };
          return updated;
        });
      });

      eventSource.addEventListener('execution_completed', (e) => {
        setExecutionStatus('completed');
        setIsExecuting(false);
        setCurrentExecutingNodeIndex(-1);
        setChatOpen(true);
        sendStateChange('execution_complete', 'Agent execution finished. All nodes processed successfully.',
          'Execution finished! Check the results to see how each node performed.');
        eventSource.close();
      });

      eventSource.addEventListener('execution_failed', (e) => {
        const data = JSON.parse(e.data);
        setExecutionStatus('failed');
        setIsExecuting(false);
        setCurrentExecutingNodeIndex(-1);
        setChatOpen(true);
        eventSource.close();
        addMessage('bot', `Execution failed: ${data.error}`);
      });

      eventSource.onerror = (e) => {
        console.error('SSE error:', e);
        setExecutionStatus('failed');
        setIsExecuting(false);
        setChatOpen(true);
        eventSource.close();
      };
    } catch (error) {
      console.error('Error starting execution:', error);
      setExecutionStatus('failed');
      setIsExecuting(false);
      setChatOpen(true);
      addMessage('bot', `Error starting execution: ${error.message}`);
    }
  };



  const isFormValid = () => {
    if (!currentQuestionnaire?.input) return false;

    const requiredFields = currentQuestionnaire.input.filter(field => field.required);
    return requiredFields.every(field => {
      const value = formData[field.name];

      // Skip validation for outputApiEndpoint when MCP is enabled
      if (field.name === 'outputApiEndpoint' && formData['enableMCP']) {
        return true;
      }

      if (field.type === 'checkbox') {
        return !field.required || value !== undefined;
      }
      return value && value.toString().trim();
    });
  };

  const getPromptdelCount = () => {
    if (!workflowResult) return 0;
    const currentWorkflow = workflowResult.flows?.[currentFlowIndex]?.workflow_steps || workflowResult.primary_workflow || [];
    return currentWorkflow.filter(step => step.node?.type === 'promptdel').length;
  };

  // Header height to account for logo + breadcrumb (single row)
  const HEADER_HEIGHT = '5rem';

  // Get chat window style based on docked state
  const getChatStyle = () => {
    if (chatDocked) {
      return {
        position: 'fixed',
        top: HEADER_HEIGHT,
        [chatDocked]: 0,
        width: `${DOCKED_WIDTH_PERCENT}%`,
        height: `calc(100vh - ${HEADER_HEIGHT})`,
        borderRadius: 0,
        transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      };
    }
    return {
      position: 'fixed',
      left: `${chatPosition.x}px`,
      top: `${chatPosition.y}px`,
      width: `${chatSize.width}px`,
      height: `${chatSize.height}px`,
      transition: isDragging ? 'none' : 'all 0.2s ease-out'
    };
  };

  // Get content panel style based on docked state
  const getContentStyle = () => {
    const base = {
      position: 'fixed',
      top: HEADER_HEIGHT,
      bottom: 0,
      left: 0,
      right: 0,
    };
    // If chat is not open, content takes full width
    if (!chatOpen) {
      return base;
    }
    if (chatDocked === 'left') {
      return { ...base, left: `${DOCKED_WIDTH_PERCENT}%` };
    }
    if (chatDocked === 'right') {
      return { ...base, right: `${DOCKED_WIDTH_PERCENT}%` };
    }
    return base;
  };

  // Map journey stages to nav item ids
  const journeyToNavMap = {
    questionnaire: 'q1',
    questionnaire_complete: 'q1',
    decompose: 'flow',
    decompose_complete: 'flow',
    plan: 'flow',
    data: 'map-data',  // Map old 'data' stage to 'map-data' view
    prompts: 'prompts',
    present: 'present',
    prototype: 'prototype'
  };

  // Determine nav item states based on app progress
  const getNavItemState = (id) => {
    // Use local state-based logic (more reliable than server journey progress)
    // Guide is always accessible — not part of the linear progress
    if (id === 'guide') return currentView === 'guide' ? 'active' : 'enabled';

    const viewProgress = {
      questionnaire: 0,
      workflow: 1,
      'map-data': 2,
      prompts: 3,
      present: 4,
      prototype: 5
    };
    const currentViewIndex = viewProgress[currentView] ?? 0;

    // Check if variables have been mapped
    const variablesMapped = agentDefinition?.status === 'variables_mapped' ||
      agentDefinition?.status === 'prompts_ready' ||
      agentDefinition?.nodes?.some(n => n.field_sources?.length > 0);

    const stages = {
      q1: {
        // Questionnaire is completed when we've moved to workflow/Plan stage or beyond
        completed: workflowResult || currentViewIndex >= 1 || isDecomposing,
        active: currentView === 'questionnaire' && !isDecomposing
      },
      flow: {
        // Plan is completed when agent is created (flow selected)
        completed: agentId || currentViewIndex >= 2,
        active: currentView === 'workflow' || isDecomposing
      },
      'map-data': {
        // Map Data is completed when variables are mapped
        completed: variablesMapped,
        active: currentView === 'map-data'
      },
      prompts: {
        // Prompts is completed when prompts have been generated
        completed: !!generatedPrompts,
        active: currentView === 'prompts'
      },
      present: {
        // Execute is reachable once prompts are generated
        completed: !!generatedPrompts,
        active: currentView === 'present' || currentView === 'execute'
      },
      prototype: {
        // Prototype is reachable once prompts are generated
        completed: false,
        active: currentView === 'prototype',
        reachable: !!generatedPrompts
      }
    };

    const stage = stages[id];
    if (stage.active) return 'active';
    if (stage.completed) return 'completed';

    // If the stage is explicitly marked reachable, enable it
    if (stage.reachable) return 'enabled';

    // Allow navigating back to any stage that has been reached
    const order = ['q1', 'flow', 'map-data', 'prompts', 'present', 'prototype'];
    const stageIndex = order.indexOf(id);

    // Always enable questionnaire
    if (stageIndex === 0) return 'enabled';

    // Enable if any stage at or after this one has been completed or active
    for (let i = stageIndex; i < order.length; i++) {
      const s = stages[order[i]];
      if (s.completed || s.active) return 'enabled';
    }

    // Enable if previous stage is completed or active (forward progression)
    const prevStage = stages[order[stageIndex - 1]];
    if (prevStage.completed || prevStage.active) return 'enabled';

    return 'disabled';
  };

  const navItems = [
    { icon: BookOpen, label: 'Getting Started', tooltip: 'Learn about ByteSizedAI Studio', id: 'guide' },
    { icon: ClipboardList, label: 'Questionnaire', tooltip: 'Questions to understand your requirements', id: 'q1' },
    { icon: GitBranch, label: 'Plan', tooltip: 'AI-generated plan based on your inputs', id: 'flow' },
    { icon: Sliders, label: 'Map Data', tooltip: 'Map variables and data flow between nodes', id: 'map-data' },
    { icon: Code, label: 'Prompts', tooltip: 'Generate prompts for AI nodes', id: 'prompts' },
    { icon: Presentation, label: 'Present', tooltip: 'Review your solution', id: 'present' },
    { icon: Rocket, label: 'Prototype', tooltip: 'Try your AI agent', id: 'prototype' },
  ];

  // ── Small screen gate + compact nav ─────────────────────────────
  const MIN_SCREEN_WIDTH = 1024;
  const MIN_SCREEN_HEIGHT = 600;
  const COMPACT_NAV_WIDTH = 1400;
  const [isSmallScreen, setIsSmallScreen] = useState(
    () => window.innerWidth < MIN_SCREEN_WIDTH || window.innerHeight < MIN_SCREEN_HEIGHT
  );
  const [isCompactNav, setIsCompactNav] = useState(() => window.innerWidth < COMPACT_NAV_WIDTH);

  useEffect(() => {
    const checkScreen = () => {
      setIsSmallScreen(window.innerWidth < MIN_SCREEN_WIDTH || window.innerHeight < MIN_SCREEN_HEIGHT);
      setIsCompactNav(window.innerWidth < COMPACT_NAV_WIDTH);
    };
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  // Refs for fluid pill animation
  const navContainerRef = useRef(null);
  const navItemRefs = useRef({});
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0, opacity: 0 });

  // Update pill position when active item changes
  const updatePillPosition = () => {
    // Find active item, or fall back to first enabled item
    let targetItem = navItems.find(item => getNavItemState(item.id) === 'active');
    if (!targetItem) {
      // If no active item, highlight the first enabled item
      targetItem = navItems.find(item => getNavItemState(item.id) === 'enabled');
    }

    if (targetItem && navItemRefs.current[targetItem.id] && navContainerRef.current) {
      const containerRect = navContainerRef.current.getBoundingClientRect();
      const itemRect = navItemRefs.current[targetItem.id].getBoundingClientRect();
      setPillStyle({
        left: itemRect.left - containerRect.left,
        width: itemRect.width,
        opacity: 1,
      });
    }
  };

  useEffect(() => {
    updatePillPosition();
  }, [currentRound, analysisResult, currentQuestionnaire, workflowResult, currentView, isDecomposing, generatedPrompts, generatedData, agentId, journeyProgress, isCompactNav]);

  // Also update pill position after initial mount (refs need time to populate)
  useEffect(() => {
    const timer = setTimeout(updatePillPosition, 100);
    return () => clearTimeout(timer);
  }, []);

  // Update flow tab pill position when selected flow changes
  const updateFlowPillPosition = () => {
    if (flowTabRefs.current[currentFlowIndex] && flowTabContainerRef.current) {
      const containerRect = flowTabContainerRef.current.getBoundingClientRect();
      const itemRect = flowTabRefs.current[currentFlowIndex].getBoundingClientRect();
      setFlowPillStyle({
        left: itemRect.left - containerRect.left,
        width: itemRect.width,
        opacity: 1,
      });
    }
  };

  useEffect(() => {
    // Update immediately
    updateFlowPillPosition();
    // Also update after a short delay to handle any layout shifts
    const timer = setTimeout(updateFlowPillPosition, 50);
    return () => clearTimeout(timer);
  }, [currentFlowIndex, workflowResult?.flows, flowExpanded, agentId]);

  // Update detail tab pill position
  const updateDetailPillPosition = () => {
    if (detailTabRefs.current[activeDetailTab] && detailTabContainerRef.current) {
      const containerRect = detailTabContainerRef.current.getBoundingClientRect();
      const itemRect = detailTabRefs.current[activeDetailTab].getBoundingClientRect();
      setDetailPillStyle({
        left: itemRect.left - containerRect.left,
        width: itemRect.width,
        opacity: 1,
      });
    }
  };

  useEffect(() => {
    // Multiple delays to ensure layout is fully settled
    const timer1 = setTimeout(updateDetailPillPosition, 50);
    const timer2 = setTimeout(updateDetailPillPosition, 150);
    const timer3 = setTimeout(updateDetailPillPosition, 300);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [activeDetailTab, detailsExpanded, currentView, workflowResult]);

  // Also update detail pill position after details section opens or workflow loads
  useEffect(() => {
    if (detailsExpanded && currentView === 'workflow' && workflowResult) {
      const timer = setTimeout(updateDetailPillPosition, 200);
      return () => clearTimeout(timer);
    }
  }, [detailsExpanded, currentView, workflowResult]);

  // Update pill positions on window resize
  useEffect(() => {
    const handleResize = () => {
      updatePillPosition();
      updateDetailPillPosition();
      updateFlowPillPosition();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Share Mode: standalone prototype page ────────────────────────
  const [shareConfig, setShareConfig] = useState(null);
  const [shareLoading, setShareLoading] = useState(isShareMode);
  const shareLoadedRef = useRef(false);

  useEffect(() => {
    if (!isShareMode || !urlParams.sid || shareLoadedRef.current) return;
    // Wait for auth so we can pass user_id to record the viewer
    // But don't block forever — fire without userId after a short delay if auth is slow
    const hasAuth = token && userId;
    if (!hasAuth) {
      // Set a timeout fallback: if auth doesn't arrive in 2s, load without user_id
      const timer = setTimeout(() => {
        if (shareLoadedRef.current) return;
        shareLoadedRef.current = true;
        setShareLoading(true);
        fetch(`${PROTOTYPE_API}/share/${urlParams.sid}`, {})
          .then(res => { if (!res.ok) throw new Error('Not found'); return res.json(); })
          .then(data => {
            setShareConfig(data.config);
            if (data.agentId && data.agentId !== 'unknown') setAgentId(data.agentId);
            if (data.agentDefinition) setAgentDefinition(data.agentDefinition);
            setShareLoading(false);
          })
          .catch(err => { console.error('Failed to load share:', err); setShareLoading(false); });
      }, 2000);
      return () => clearTimeout(timer);
    }
    shareLoadedRef.current = true;
    setShareLoading(true);
    fetch(`${PROTOTYPE_API}/share/${urlParams.sid}?user_id=${userId}`, {
      headers: { Authorization: token },
    })
      .then(res => { if (!res.ok) throw new Error('Not found'); return res.json(); })
      .then(data => {
        setShareConfig(data.config);
        if (data.agentId && data.agentId !== 'unknown') {
          setAgentId(data.agentId);
        }
        if (data.agentDefinition) {
          setAgentDefinition(data.agentDefinition);
        }
        setShareLoading(false);
      })
      .catch(err => {
        console.error('Failed to load share:', err);
        setShareLoading(false);
      });
  }, [token, userId]);

  // Auto-open sidebar when navigated with ?sidebar=open (e.g. from billing back)
  useEffect(() => {
    if (urlParams.sidebar === 'open' && token && userId) {
      fetchAgentsList();
      fetchSessionsList();
      fetchSharedPrototypes();
      setSidebarOpen(true);
      updateUrl({ sidebar: null });
    }
  }, [token, userId]);


  if (isShareMode) {
    const shareDark = shareConfig?.theme === 'dark';
    const ready = !!shareConfig;

    return (
      <div className={`h-screen overflow-auto ${shareDark ? 'bg-gray-900' : ''}`} style={{
        ...(!shareDark ? { background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)' } : {})
      }}>
        <style>{customStyles}</style>
        {!ready ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-3">
              <Loader className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
              <p className={`text-sm ${shareDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading prototype...</p>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <PrototypeView
              agentId={agentId}
              agentDefinition={agentDefinition}
              isDarkMode={shareDark}
              token={token}
              executionApi={EXECUTION_API}
              externalConfig={shareConfig}
              onConfigChange={() => { }}
              onInsufficientCredits={handleInsufficientCredits}
              isShareMode={true}
              userId={userId}
            />
          </div>
        )}
      </div>
    );
  }

  // ── Small screen gate ──────────────────────────────────────────────
  if (isSmallScreen) {
    return (
      <div className="h-screen relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
      }}>
        <style>{customStyles}</style>
        {/* Skeleton background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.06]">
          {/* Fake header */}
          <div className="h-14 mx-6 mt-4 rounded-xl bg-white skeleton-pulse" />
          {/* Fake sidebar */}
          <div className="absolute top-24 left-6 w-64 bottom-6 rounded-xl bg-white skeleton-pulse" style={{ animationDelay: '0.3s' }} />
          {/* Fake content cards */}
          <div className="absolute top-24 left-[296px] right-6 bottom-6 space-y-4">
            <div className="h-40 rounded-xl bg-white skeleton-pulse" style={{ animationDelay: '0.5s' }} />
            <div className="h-24 rounded-xl bg-white skeleton-pulse" style={{ animationDelay: '0.7s' }} />
            <div className="h-32 rounded-xl bg-white skeleton-pulse" style={{ animationDelay: '0.9s' }} />
          </div>
        </div>

        {/* Centered banner */}
        <div className="relative z-10 flex items-center justify-center h-full px-6">
          <div className="max-w-md w-full text-center space-y-6 backdrop-blur-xl bg-slate-800/80 border border-white/10 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10">
            {/* Icon */}
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 flex items-center justify-center">
              <svg className="w-8 h-8 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
              </svg>
            </div>

            {/* Logo + Title */}
            <div>
              <div className="flex items-center justify-center space-x-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-sm">B</div>
                <span className="text-lg font-bold text-white">ByteSized</span>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Larger screen required
              </h2>
              <p className="text-sm text-gray-400 leading-relaxed">
                ByteSized AI Architect needs a laptop or desktop screen to work properly. Please switch to a device with a screen width of at least 1024px.
              </p>
            </div>

            {/* Screen size hint */}
            <div className="flex items-center justify-center space-x-3 text-xs text-gray-500">
              <span className="px-2.5 py-1 rounded-full bg-red-900/30 text-red-400 border border-red-800/30">
                Current: {window.innerWidth} x {window.innerHeight}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-green-900/30 text-green-400 border border-green-800/30">
                Min: 1024 x 600
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen relative overflow-hidden" style={{
      background: isDarkMode
        ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
        : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)'
    }}>
      {/* Ambient gradient orbs for liquid glass effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="orb-1 absolute -top-1/4 -left-1/4 w-full h-full rounded-full blur-3xl" style={{
          background: isDarkMode
            ? 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 60%)'
            : 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 60%)'
        }}></div>
        <div className="orb-2 absolute -bottom-1/4 -right-1/4 w-full h-full rounded-full blur-3xl" style={{
          background: isDarkMode
            ? 'radial-gradient(circle, rgba(56, 189, 248, 0.25) 0%, transparent 60%)'
            : 'radial-gradient(circle, rgba(56, 189, 248, 0.12) 0%, transparent 60%)'
        }}></div>
        <div className="orb-3 absolute top-1/2 left-1/2 w-2/3 h-2/3 rounded-full blur-3xl" style={{
          background: isDarkMode
            ? 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 60%)'
            : 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 60%)'
        }}></div>
      </div>

      {/* Header with Logo and Breadcrumb on same line */}
      <header className={`liquid-glass fixed top-0 left-0 right-0 z-40 backdrop-blur-2xl shadow-lg shadow-blue-500/5 overflow-visible ${isDarkMode
        ? 'bg-slate-900/80 border-b border-white/10'
        : 'bg-white/80 border-b border-gray-200'
        }`}>
        <div className="flex items-center px-6 py-3">
          {/* Logo - The Architect's face */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <div className={`w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg transition-all ${(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview)
              ? 'logo-active'
              : 'logo-shimmer'
              }`}>
              <span className="text-white font-bold text-lg">B</span>
            </div>
            <span className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>ByteSized</span>
          </div>

          {/* Breadcrumb Navigation - centered in remaining space */}
          <nav className="flex-1 flex items-center justify-center">
            {/* Gradient border container */}
            <div
              className={`liquid-glow relative p-[1px] rounded-full overflow-visible ${(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview) ? 'border-flow' : ''}`}
              style={{ background: 'linear-gradient(90deg, #3b82f6, #0ea5e9, #06b6d4, #0ea5e9, #3b82f6)' }}
            >
              <div
                ref={navContainerRef}
                className={`relative flex items-center gap-1 backdrop-blur-xl rounded-full px-2 py-1 overflow-visible ${isDarkMode ? 'bg-slate-800/90' : 'bg-white/90'
                  }`}
              >
                {/* Animated fluid pill indicator */}
                <div
                  className={`absolute top-1 bottom-1 rounded-full transition-all duration-500 ease-out ${(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview)
                    ? 'architect-active'
                    : ''
                    }`}
                  style={{
                    left: pillStyle.left,
                    width: pillStyle.width,
                    opacity: pillStyle.opacity,
                    background: (isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview)
                      ? isDarkMode
                        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.5), rgba(14, 165, 233, 0.5), rgba(6, 182, 212, 0.5))'
                        : 'linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(14, 165, 233, 0.4), rgba(6, 182, 212, 0.4))'
                      : isDarkMode
                        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(14, 165, 233, 0.3), rgba(6, 182, 212, 0.3))'
                        : 'linear-gradient(135deg, rgba(59, 130, 246, 0.25), rgba(14, 165, 233, 0.25), rgba(6, 182, 212, 0.25))',
                    boxShadow: (isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview)
                      ? isDarkMode
                        ? '0 0 30px rgba(59, 130, 246, 0.6), inset 0 0 15px rgba(255, 255, 255, 0.2)'
                        : '0 0 20px rgba(59, 130, 246, 0.5), inset 0 0 10px rgba(255, 255, 255, 0.3)'
                      : isDarkMode
                        ? '0 0 20px rgba(59, 130, 246, 0.4), inset 0 0 10px rgba(255, 255, 255, 0.1)'
                        : '0 0 15px rgba(59, 130, 246, 0.3), inset 0 0 8px rgba(255, 255, 255, 0.2)',
                  }}
                />

                {navItems.map((item, index) => {
                  const state = getNavItemState(item.id);
                  const isDisabled = state === 'disabled';
                  const isActive = state === 'active';
                  const isCompleted = state === 'completed';

                  return (
                    <div
                      key={item.id}
                      className="flex items-center"
                      onMouseEnter={() => setHoveredNavItem(item)}
                      onMouseLeave={() => setHoveredNavItem(null)}
                    >
                      {/* Nav Item */}
                      <button
                        ref={(el) => { navItemRefs.current[item.id] = el; }}
                        disabled={isDisabled}
                        onClick={() => {
                          if (isDisabled) return;
                          // Map nav item id to view
                          const viewMap = {
                            'guide': () => setCurrentView('guide'),
                            'q1': () => setCurrentView('questionnaire'),
                            'flow': () => setCurrentView('workflow'),
                            'map-data': () => setCurrentView('map-data'),
                            'prompts': () => setCurrentView('prompts'),
                            'present': () => {
                              setExecutionNodes([]);
                              setExecutionStatus(null);
                              setCurrentExecutingNodeIndex(-1);
                              setIsPromptFlowExpanded(true);
                              setCurrentView('execute');
                            },
                            'prototype': () => setCurrentView('prototype')
                          };
                          viewMap[item.id]?.();
                        }}
                        title={isCompactNav ? item.label : undefined}
                        className={`relative z-10 flex items-center ${isCompactNav ? 'px-2' : 'space-x-2 px-3'} py-1.5 rounded-full transition-all duration-300 group ${isActive
                          ? isDarkMode ? 'text-white' : 'text-gray-900'
                          : isCompleted
                            ? isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'
                            : isDisabled
                              ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                              : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                          }`}
                      >
                        <div className="relative">
                          <item.icon className={`w-4 h-4 transition-colors ${isActive ? 'text-cyan-600' : isCompleted ? (isDarkMode ? 'text-green-400' : 'text-green-500') : ''}`} />
                          {isCompleted && (
                            <CheckCircle className={`absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full ${isDarkMode ? 'text-green-400 bg-gray-900' : 'text-green-500 bg-white'}`} style={{ filter: isDarkMode ? 'drop-shadow(0 0 6px rgba(74, 222, 128, 0.8))' : 'drop-shadow(0 0 4px rgba(52, 211, 153, 0.6))' }} />
                          )}
                        </div>
                        {!isCompactNav && (
                          <span className="text-sm font-medium">
                            {item.label}
                          </span>
                        )}

                      </button>

                      {/* Arrow between items */}
                      {index < navItems.length - 1 && !isCompactNav && (
                        <ArrowRight className={`w-3 h-3 flex-shrink-0 ${isCompleted || isActive ? (isDarkMode ? 'text-gray-400' : 'text-gray-500') : (isDarkMode ? 'text-gray-600' : 'text-gray-400')}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Right side buttons */}
          <div className="flex items-center space-x-2 relative z-50">
            {/* Fuel / Billing Button */}
            {(() => {
              const pct = balancePct ?? 1;
              const fillColor = pct > 0.5 ? '#22c55e' : pct > 0.2 ? '#f59e0b' : '#ef4444';
              const isEmpty = pct <= 0;
              return (
                <button
                  onClick={() => { localStorage.setItem('bytesized_billing_return', `/?view=${currentView}`); isEmpty ? navigate('/billing?recharge=true') : navigate('/billing'); }}
                  className={`flex-shrink-0 flex items-center space-x-1.5 px-2.5 py-2 rounded-lg transition-all duration-300 ${
                    isDarkMode
                      ? 'bg-gray-700 hover:bg-gray-600 dark-tooltip'
                      : 'bg-gray-200 hover:bg-gray-300 light-tooltip'
                  }`}
                  data-tooltip={isEmpty ? 'Tank empty — add credits' : pct <= 0.2 ? 'Running low — add credits' : 'Billing'}
                >
                  {/* Fuel pump gauge */}
                  <svg width="26" height="28" viewBox="0 0 18 20" fill="none" className="flex-shrink-0" style={{
                    animation: isEmpty ? 'fuel-urgent 1.2s ease-in-out infinite' : 'none',
                  }}>
                    <defs>
                      {/* Clip to keep liquid inside pump body only */}
                      <clipPath id="tankClip">
                        <rect x="1.5" y="4" width="10" height="14.5" rx="1.5" />
                      </clipPath>
                    </defs>
                    {/* Pump body */}
                    <rect x="1.5" y="4" width="10" height="14.5" rx="1.5"
                      stroke={isDarkMode ? '#6b7280' : '#9ca3af'} strokeWidth="1.2" fill={isDarkMode ? '#1f2937' : '#f3f4f6'} />
                    {/* Liquid fill inside body */}
                    {pct > 0 && (
                      <rect
                        x="1.5" rx="1.5"
                        y={4 + (14.5 * (1 - pct))}
                        width="10"
                        height={14.5 * pct}
                        fill={fillColor}
                        clipPath="url(#tankClip)"
                        style={{ transition: 'y 0.8s ease, height 0.8s ease, fill 0.5s ease' }}
                      />
                    )}
                    {/* Pump neck */}
                    <rect x="4.5" y="1.5" width="4" height="3" rx="0.8"
                      stroke={isDarkMode ? '#6b7280' : '#9ca3af'} strokeWidth="1" fill={isDarkMode ? '#1f2937' : '#f3f4f6'} />
                    {/* Nozzle — hose from right side */}
                    <path d="M11.5 7 L14 7 Q16 7 16 9 L16 13 L14.5 14.5"
                      stroke={isDarkMode ? '#6b7280' : '#9ca3af'} strokeWidth="1.2" strokeLinecap="round" fill="none" />
                    {/* Nozzle drip tip */}
                    <line x1="14.5" y1="14.5" x2="14.5" y2="16" stroke={isDarkMode ? '#6b7280' : '#9ca3af'} strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                  <span className={`text-xs font-medium ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>Billing</span>
                  {currentPack && (
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                      currentPack === 'free'
                        ? (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-500')
                        : (isDarkMode ? 'bg-cyan-900/60 text-cyan-300' : 'bg-cyan-100 text-cyan-700')
                    }`}>{currentPack.charAt(0).toUpperCase() + currentPack.slice(1)}</span>
                  )}
                </button>
              );
            })()}

            {/* Theme Toggle Button */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`flex-shrink-0 p-2 rounded-lg transition-all duration-300 tooltip-bottom ${isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400 dark-tooltip'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700 light-tooltip'
                }`}
              data-tooltip={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className={`flex-shrink-0 p-2 rounded-lg transition-all duration-300 tooltip-bottom ${isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-red-400 dark-tooltip'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-red-500 light-tooltip'
                }`}
              data-tooltip="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar - Agents & Sessions List - docks opposite to chat */}
      <div
        className={`fixed top-[60px] h-[calc(100vh-60px)] z-30 transition-all duration-300 ${chatDocked === 'left' || chatDocked === null
          ? `right-0 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`
          : `left-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
          }`}
        style={{ width: '280px' }}
      >
        <div className={`h-full backdrop-blur-xl shadow-xl ${chatDocked === 'left' || chatDocked === null ? 'border-l' : 'border-r'
          } ${isDarkMode
            ? 'bg-slate-900/95 border-white/10'
            : 'bg-white/95 border-gray-200'
          }`}>
          {/* Sidebar Header */}
          <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              My Agents
            </h3>
          </div>

          {/* Sidebar Content */}
          <div className="overflow-y-auto h-[calc(100%-52px)]">
            {/* Agents Section */}
            <div className={`px-3 py-2 border-b ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Agents
                </span>
                {loadingAgents && <Loader className="w-3 h-3 animate-spin text-cyan-500" />}
              </div>

              {agentsList.length === 0 && !loadingAgents ? (
                <p className={`text-xs py-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  No agents yet
                </p>
              ) : (
                <div className="space-y-1">
                  {agentsList.map((agent) => (
                    <div
                      key={agent.agent_id}
                      className={`relative w-full text-left px-3 py-2 rounded-lg transition-all duration-200 group cursor-pointer ${agentId === agent.agent_id
                        ? isDarkMode
                          ? 'bg-cyan-900/30 border border-cyan-700/50'
                          : 'bg-cyan-50 border border-cyan-200'
                        : isDarkMode
                          ? 'hover:bg-gray-800/50'
                          : 'hover:bg-gray-50'
                        }`}
                      onClick={() => loadAgent(agent.agent_id)}
                    >
                      <div className="flex items-start space-x-2">
                        <Workflow className={`w-4 h-4 mt-0.5 flex-shrink-0 ${agent.status === 'schema_generated' || agent.status === 'variables_mapped'
                          ? 'text-green-500'
                          : agent.status === 'error'
                            ? 'text-red-500'
                            : 'text-cyan-500'
                          }`} />
                        <div className="flex-1 min-w-0">
                          {editingAgentId === agent.agent_id ? (
                            <input
                              type="text"
                              value={editingAgentName}
                              onChange={(e) => setEditingAgentName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && editingAgentName.trim()) {
                                  renameAgent(agent.agent_id, editingAgentName.trim());
                                } else if (e.key === 'Escape') {
                                  setEditingAgentId(null);
                                  setEditingAgentName('');
                                }
                              }}
                              onBlur={() => {
                                if (editingAgentName.trim() && editingAgentName.trim() !== agent.concept_name) {
                                  renameAgent(agent.agent_id, editingAgentName.trim());
                                } else {
                                  setEditingAgentId(null);
                                  setEditingAgentName('');
                                }
                              }}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                              className={`w-full text-sm font-medium bg-transparent border-0 border-b focus:ring-0 px-0 py-0.5 ${isDarkMode
                                ? 'text-gray-200 border-cyan-500 focus:border-cyan-400'
                                : 'text-gray-700 border-cyan-500 focus:border-cyan-600'
                                }`}
                            />
                          ) : (
                            <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              {agent.concept_name?.substring(0, 40) || 'Unnamed Agent'}
                              {agent.concept_name?.length > 40 && '...'}
                            </p>
                          )}
                          <div className="flex items-center space-x-2 mt-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${agent.status === 'schema_generated'
                              ? isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'
                              : agent.status === 'variables_mapped'
                                ? isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-700'
                                : agent.status === 'error'
                                  ? isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'
                                  : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                              }`}>
                              {agent.status?.replace(/_/g, ' ')}
                            </span>
                            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {new Date(agent.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                        {/* Edit & Delete buttons - appear on hover */}
                        {editingAgentId !== agent.agent_id && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingAgentId(agent.agent_id);
                                setEditingAgentName(agent.concept_name || '');
                              }}
                              className={`p-1 rounded ${isDarkMode
                                ? 'hover:bg-gray-700 text-gray-500 hover:text-gray-300'
                                : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                                }`}
                              title="Rename agent"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteAgent(agent.agent_id, agent.concept_name);
                              }}
                              className={`p-1 rounded ${isDarkMode
                                ? 'hover:bg-red-900/50 text-gray-500 hover:text-red-400'
                                : 'hover:bg-red-50 text-gray-400 hover:text-red-500'
                                }`}
                              title="Delete agent"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sessions Section (In Progress) */}
            <div className="px-3 py-2">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  In Progress
                </span>
                {loadingSessions && <Loader className="w-3 h-3 animate-spin text-cyan-500" />}
              </div>

              {sessionsList.length === 0 && !loadingSessions ? (
                <p className={`text-xs py-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  No sessions in progress
                </p>
              ) : (
                <div className="space-y-1">
                  {sessionsList.map((session) => (
                    <div
                      key={session.session_id}
                      className={`relative w-full text-left px-3 py-2 rounded-lg transition-all duration-200 group cursor-pointer ${sessionId === session.session_id && !agentId
                        ? isDarkMode
                          ? 'bg-cyan-900/30 border border-cyan-700/50'
                          : 'bg-cyan-50 border border-cyan-200'
                        : isDarkMode
                          ? 'hover:bg-gray-800/50'
                          : 'hover:bg-gray-50'
                        }`}
                      onClick={() => loadSession(session.session_id)}
                    >
                      <div className="flex items-start space-x-2">
                        <MessageSquare className={`w-4 h-4 mt-0.5 flex-shrink-0 ${session.journey_stage === 'decompose_complete'
                          ? 'text-amber-500'
                          : 'text-gray-400'
                          }`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {session.session_name || 'New Project'}
                          </p>
                          <div className="flex items-center space-x-2 mt-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-amber-900/50 text-amber-400' : 'bg-amber-100 text-amber-700'
                              }`}>
                              {session.journey_stage?.replace(/_/g, ' ') || 'questionnaire'}
                            </span>
                            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {new Date(session.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                        {/* Delete button - appears on hover */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSession(session.session_id, session.session_name || session.problem_summary);
                          }}
                          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${isDarkMode
                            ? 'hover:bg-red-900/50 text-gray-500 hover:text-red-400'
                            : 'hover:bg-red-50 text-gray-400 hover:text-red-500'
                            }`}
                          title="Delete session"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Shared With Me ── */}
            {/* ── Shared With Me ── */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Shared With Me
                </h3>
                {loadingSharedPrototypes && <Loader className="w-3 h-3 animate-spin text-cyan-500" />}
              </div>

              {sharedPrototypesList.length === 0 && !loadingSharedPrototypes ? (
                <p className={`text-xs py-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  No prototypes shared with you yet
                </p>
              ) : (
                <div className="space-y-1">
                  {sharedPrototypesList.map((proto) => (
                    <div
                      key={proto.shareId}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer ${
                        isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => loadSharedPrototype(proto.shareId)}
                    >
                      <div className="flex items-start space-x-2">
                        <Presentation
                          className="w-4 h-4 mt-0.5 flex-shrink-0"
                          style={{ color: proto.primaryColor || '#0077A8' }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {proto.appName || 'Shared Prototype'}
                          </p>
                          {proto.createdAt && (
                            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {new Date(proto.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar Backdrop - closes sidebar when clicking outside */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/20 backdrop-blur-sm"
          style={{ top: '60px' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Nav Tooltip - rendered outside header to avoid clipping */}
      {hoveredNavItem && navItemRefs.current[hoveredNavItem.id] && (
        <div
          className="fixed z-[200] px-3 py-2 text-xs rounded-lg shadow-xl pointer-events-none transition-opacity duration-150"
          style={{
            top: navItemRefs.current[hoveredNavItem.id].getBoundingClientRect().bottom + 8,
            left: navItemRefs.current[hoveredNavItem.id].getBoundingClientRect().left +
              navItemRefs.current[hoveredNavItem.id].getBoundingClientRect().width / 2,
            transform: 'translateX(-50%)',
            background: '#1f2937',
            color: '#f3f4f6',
            border: '1px solid #374151',
          }}
        >
          {hoveredNavItem.tooltip}
          {/* Arrow pointing up */}
          <div
            className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent"
            style={{ borderBottomColor: '#1f2937' }}
          ></div>
        </div>
      )}

      {/* Edge Hint Indicators - show inviting glow on opposite edge */}
      {!isDragging && chatDocked === null && chatOpen && (
        <>
          {getInvitingEdge() === 'left' && (
            <div className={`fixed top-0 left-0 w-1 h-full bg-gradient-to-r to-transparent z-30 edge-hint pointer-events-none ${isDarkMode ? 'from-cyan-500/30' : 'from-cyan-500/50'
              }`} />
          )}
          {getInvitingEdge() === 'right' && (
            <div className={`fixed top-0 right-0 w-1 h-full bg-gradient-to-l to-transparent z-30 edge-hint pointer-events-none ${isDarkMode ? 'from-cyan-500/30' : 'from-cyan-500/50'
              }`} />
          )}
        </>
      )}

      {/* Snap Preview Indicators - show when dragging near edges */}
      {isDragging && nearEdge && (
        <div
          className={`fixed top-0 ${nearEdge === 'left' ? 'left-0' : 'right-0'} h-full z-40 snap-indicator ${isDarkMode ? 'w-3' : 'w-4'
            }`}
          style={{
            background: nearEdge === 'left'
              ? `linear-gradient(to right, ${isDarkMode ? 'rgba(6, 182, 212, 0.7)' : 'rgba(6, 182, 212, 0.9)'}, transparent)`
              : `linear-gradient(to left, ${isDarkMode ? 'rgba(6, 182, 212, 0.7)' : 'rgba(6, 182, 212, 0.9)'}, transparent)`,
            boxShadow: isDarkMode
              ? `${nearEdge === 'left' ? '' : '-'}10px 0 30px rgba(6, 182, 212, 0.5)`
              : `${nearEdge === 'left' ? '' : '-'}15px 0 40px rgba(6, 182, 212, 0.6)`
          }}
        />
      )}

      {/* Floating/Docked Chat Window */}
      {chatOpen ? (
        <div
          className={`z-50 p-[1px] ${chatDocked ? 'rounded-none' : 'rounded-3xl'} ${(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview) ? 'border-flow header-breathing' : ''} ${isDragging && nearEdge ? 'magnetic-pull' : ''}`}
          style={{
            ...getChatStyle(),
            background: 'linear-gradient(135deg, #3b82f6, #0ea5e9, #06b6d4, #0ea5e9, #3b82f6)',
            backgroundSize: '200% 100%',
            // Magnetic attraction glow on the side near the edge
            ...(isDragging && nearEdge ? {
              boxShadow: nearEdge === 'left'
                ? `${isDarkMode
                  ? '-20px 0 40px rgba(6, 182, 212, 0.6), -10px 0 20px rgba(6, 182, 212, 0.4), inset -3px 0 15px rgba(6, 182, 212, 0.3)'
                  : '-25px 0 50px rgba(6, 182, 212, 0.7), -12px 0 25px rgba(6, 182, 212, 0.5), inset -4px 0 20px rgba(6, 182, 212, 0.4)'}`
                : `${isDarkMode
                  ? '20px 0 40px rgba(6, 182, 212, 0.6), 10px 0 20px rgba(6, 182, 212, 0.4), inset 3px 0 15px rgba(6, 182, 212, 0.3)'
                  : '25px 0 50px rgba(6, 182, 212, 0.7), 12px 0 25px rgba(6, 182, 212, 0.5), inset 4px 0 20px rgba(6, 182, 212, 0.4)'}`
            } : {})
          }}
        >
          <div
            ref={chatRef}
            className={`flex flex-col h-full backdrop-blur-2xl shadow-2xl shadow-cyan-500/10 ${chatDocked ? 'rounded-none' : 'rounded-3xl'
              } ${(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview) ? 'architect-active' : 'architect-breathing'} ${isDarkMode ? 'bg-slate-900/95' : 'bg-white/95'
              }`}
            style={{
              cursor: isDragging ? 'grabbing' : 'default'
            }}
            onMouseDown={handleDragStart}
            onMouseEnter={() => setChatHovered(true)}
            onMouseLeave={() => setChatHovered(false)}
          >
            {/* Chat Header - Draggable, double-click to dock */}
            <div
              className={`p-[1px] ${chatDocked ? '' : 'rounded-t-3xl'} ${(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview)
                ? 'border-flow'
                : ''
                }`}
              style={{ background: 'linear-gradient(90deg, #3b82f6, #0ea5e9, #06b6d4, #0ea5e9, #3b82f6)', backgroundSize: '200% 100%' }}
            >
              <div
                className={`liquid-glass ${isDarkMode ? 'chat-header-hover-dark' : 'chat-header-hover-light'} chat-drag-handle px-3 py-2 cursor-grab flex items-center justify-between select-none ${chatDocked ? '' : 'rounded-t-3xl'} backdrop-blur-xl relative overflow-hidden ${(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview)
                  ? (isDarkMode ? 'header-alive-dark' : 'header-alive')
                  : ''
                  }`}
                style={{
                  ...((isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview)
                    ? {} // Let CSS animation control the background
                    : {
                      background: isDarkMode
                        ? 'rgba(30, 41, 59, 0.9)'
                        : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)'
                    }
                  )
                }}
                onDoubleClick={() => {
                  if (chatDocked) {
                    // If already docked, undock
                    setChatDocked(null);
                    setChatPosition({ x: 20, y: 80 });
                  } else {
                    // Dock to nearest side
                    const screenCenter = window.innerWidth / 2;
                    const chatCenter = chatPosition.x + (chatSize.width / 2);
                    setChatDocked(chatCenter < screenCenter ? 'left' : 'right');
                  }
                }}
              >
                <div className="flex items-center space-x-2">
                  {/* Byte avatar */}
                  <div className={`w-6 h-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-md flex items-center justify-center ${(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview) ? 'logo-active' : 'logo-shimmer'
                    }`}>
                    <span className="text-white font-bold text-xs">B</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm leading-tight text-white">Byte</span>
                    <span className={`text-xs leading-tight ${isDarkMode ? 'text-gray-400' : 'text-white/80'}`}>AI Architect</span>
                  </div>
                  {/* Status indicator */}
                  {(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview || isAutoPilot) ? (
                    <span className="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-cyan-500/30 text-white text-xs border border-cyan-400/50">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full bg-cyan-300 animate-bounce"
                        style={{
                          boxShadow: '0 0 8px rgba(34, 211, 238, 0.8)',
                          animationDuration: '0.5s'
                        }}
                      />
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full bg-cyan-300 animate-bounce"
                        style={{
                          boxShadow: '0 0 8px rgba(34, 211, 238, 0.8)',
                          animationDuration: '0.5s',
                          animationDelay: '0.1s'
                        }}
                      />
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full bg-cyan-300 animate-bounce"
                        style={{
                          boxShadow: '0 0 8px rgba(34, 211, 238, 0.8)',
                          animationDuration: '0.5s',
                          animationDelay: '0.2s'
                        }}
                      />
                      <span className="ml-1 font-medium">{isAutoPilot ? (isAutoPilotPaused ? 'Paused' : 'Working') : 'Thinking'}</span>
                    </span>
                  ) : showHandbackBanner ? (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/20 text-white'
                      }`}>
                      ✓ You're in control
                    </span>
                  ) : (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-white/20 text-white'
                      }`}>
                      ● Byte is watching
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  {chatDocked && (
                    <button
                      onClick={() => {
                        setChatDocked(null);
                        setChatPosition({ x: 20, y: 80 });
                      }}
                      className="p-1 rounded transition-colors text-white/80 hover:text-white hover:bg-white/20 dark-tooltip tooltip-bottom"
                      data-tooltip="Undock"
                    >
                      <ArrowUpCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setChatOpen(false)}
                    className="p-1 rounded transition-colors text-white/80 hover:text-white hover:bg-white/20 dark-tooltip tooltip-bottom"
                    data-tooltip="Minimize"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Agent action buttons — full-width row */}
            <div className={`flex items-stretch ${isDarkMode
              ? 'bg-gray-800 border-b border-white/5'
              : 'bg-white border-b border-gray-200'
            }`}>
              <button
                onClick={toggleSidebar}
                className={`flex-1 py-2 transition-all duration-200 text-xs font-medium flex items-center justify-center space-x-1.5 ${sidebarOpen
                  ? isDarkMode
                    ? 'bg-cyan-900/50 text-cyan-300'
                    : 'bg-cyan-50 text-cyan-700'
                  : isDarkMode
                    ? 'hover:bg-gray-700 text-gray-300 hover:text-cyan-300'
                    : 'hover:bg-gray-50 text-gray-600 hover:text-cyan-600'
                }`}
                title={sidebarOpen ? 'Close sidebar' : 'View your agents and sessions'}
              >
                <PanelLeft className="w-3.5 h-3.5" />
                <span>My Agents</span>
              </button>
              <div className={`w-px self-stretch my-1.5 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
              <button
                onClick={handleStartOver}
                className={`flex-1 py-2 transition-all duration-200 text-xs font-medium flex items-center justify-center space-x-1.5 ${isDarkMode
                  ? 'hover:bg-gray-700 text-gray-300 hover:text-cyan-300'
                  : 'hover:bg-gray-50 text-gray-600 hover:text-cyan-600'
                }`}
                title="Create a new agent from scratch"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New Agent</span>
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((message) => (
                message.type === 'divider' ? (
                  <div key={message.id} className="flex items-center space-x-3 py-1.5">
                    <div className={`flex-1 h-px ${isDarkMode ? 'bg-gray-700/60' : 'bg-gray-200'}`} />
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                      {message.content}
                    </span>
                    <div className={`flex-1 h-px ${isDarkMode ? 'bg-gray-700/60' : 'bg-gray-200'}`} />
                  </div>
                ) :
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {/* Special flow-selector message type with interactive controls */}
                    {message.type === 'flow-selector' && workflowResult?.flows ? (
                      <div className={`max-w-[95%] px-3 py-3 rounded-lg ${isDarkMode ? 'bg-white/10 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>
                        <div className="flex items-start space-x-2">
                          <div className="w-5 h-5 bg-gradient-to-br from-blue-500 to-cyan-500 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-white font-bold text-[10px]">B</span>
                          </div>
                          {agentId ? (
                            /* Show selected flow confirmation when agent is created */
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center space-x-2">
                                <Check className={`w-4 h-4 ${currentFlowIndex === 0 ? 'text-[#0099CC]' : currentFlowIndex === 1 ? 'text-emerald-500' : 'text-purple-500'
                                  }`} />
                                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {workflowResult.flows[currentFlowIndex]?.label || `Flow ${currentFlowIndex + 1}`}
                                </span>
                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  • {workflowResult.flows[currentFlowIndex]?.workflow_steps?.length || 0} steps
                                </span>
                              </div>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Agent created with schemas generated
                              </p>
                            </div>
                          ) : (
                            /* Show interactive flow selector when no agent yet */
                            <div className="flex-1 space-y-3">
                              {/* Flow indicator */}
                              <div className="flex items-center justify-between">
                                <span className={`text-xs font-semibold uppercase tracking-wide ${currentFlowIndex === 0 ? 'text-[#0099CC]' : currentFlowIndex === 1 ? 'text-emerald-500' : 'text-purple-500'
                                  }`}>
                                  {currentFlowIndex === 0 ? 'Recommended' : currentFlowIndex === 1 ? 'Simple' : 'Thorough'}
                                </span>
                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {currentFlowIndex + 1} of {workflowResult.flows.length}
                                </span>
                              </div>

                              {/* Flow name and steps */}
                              <div>
                                <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {workflowResult.flows[currentFlowIndex]?.label || `Flow ${currentFlowIndex + 1}`}
                                </div>
                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {workflowResult.flows[currentFlowIndex]?.workflow_steps?.length || 0} steps
                                </div>
                              </div>

                              {/* Flow description */}
                              <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {workflowResult.flows[currentFlowIndex]?.description || 'No description available.'}
                              </p>

                              {/* Navigation controls */}
                              <div className="flex items-center justify-between pt-2 border-t border-gray-600/30">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      setCurrentFlowIndex(prev => Math.max(0, prev - 1));
                                      setCurrentNodeIndex(0);
                                    }}
                                    disabled={currentFlowIndex === 0}
                                    className={`p-1.5 rounded transition-colors ${currentFlowIndex === 0
                                      ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                                      : isDarkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-200'
                                      }`}
                                  >
                                    <ChevronLeft className="w-4 h-4" />
                                  </button>
                                  {/* Pill indicators */}
                                  <div className="flex space-x-1.5 items-center">
                                    {workflowResult.flows.map((_, index) => (
                                      <button
                                        key={index}
                                        onClick={() => {
                                          setCurrentFlowIndex(index);
                                          setCurrentNodeIndex(0);
                                        }}
                                        className={`h-2 rounded-full transition-all duration-300 ${index === currentFlowIndex
                                          ? `w-6 ${index === 0 ? 'bg-[#0099CC]' : index === 1 ? 'bg-emerald-500' : 'bg-purple-500'}`
                                          : `w-2 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-400 hover:bg-gray-500'}`
                                          }`}
                                      />
                                    ))}
                                  </div>
                                  <button
                                    onClick={() => {
                                      setCurrentFlowIndex(prev => Math.min(workflowResult.flows.length - 1, prev + 1));
                                      setCurrentNodeIndex(0);
                                    }}
                                    disabled={currentFlowIndex === workflowResult.flows.length - 1}
                                    className={`p-1.5 rounded transition-colors ${currentFlowIndex === workflowResult.flows.length - 1
                                      ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                                      : isDarkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-200'
                                      }`}
                                  >
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={handleProceedToData}
                                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${currentFlowIndex === 0
                                      ? 'bg-[#0077A8] hover:bg-[#006090] text-white'
                                      : currentFlowIndex === 1
                                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                                      }`}
                                  >
                                    Use This Flow
                                  </button>
                                  {!agentId && (
                                    <button
                                      onClick={handleEditFlow}
                                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${isDarkMode
                                        ? 'border border-gray-500 text-gray-300 hover:bg-gray-700'
                                        : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                      I want to tweak this
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`max-w-[90%] px-3 py-2 rounded-lg ${message.type === 'user'
                          ? isDarkMode
                            ? 'backdrop-blur-sm border border-cyan-500/30 text-white'
                            : 'backdrop-blur-sm border border-cyan-500/30 text-gray-800'
                          : isDarkMode
                            ? 'bg-white/10 text-gray-200'
                            : 'bg-gray-100 text-gray-800'
                          }`}
                        style={message.type === 'user' ? {
                          background: isDarkMode
                            ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.25) 0%, rgba(14, 165, 233, 0.2) 50%, rgba(59, 130, 246, 0.25) 100%)'
                            : 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(14, 165, 233, 0.12) 50%, rgba(59, 130, 246, 0.15) 100%)'
                        } : {}}
                      >
                        <div className="flex items-start space-x-2">
                          {message.type === 'bot' && (
                            <div className="w-5 h-5 bg-gradient-to-br from-blue-500 to-cyan-500 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-white font-bold text-[10px]">B</span>
                            </div>
                          )}
                          <div className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</div>
                        </div>
                      </div>
                    )}
                  </div>
              ))}

              {(isLoading || isGeneratingPrompts || isGeneratingData || isGeneratingPreview || isDecomposing || isSelectingFlow || thinkingMessage) && (
                <div className="flex justify-start">
                  <div className={`max-w-[85%] ${thinkingMessage
                    ? isDarkMode
                      ? 'bg-gradient-to-r from-cyan-900/50 to-blue-900/50 border border-cyan-500/30'
                      : 'bg-gradient-to-r from-cyan-100 to-blue-100 border border-cyan-300'
                    : isDarkMode
                      ? 'bg-white/10'
                      : 'bg-gray-100'
                    } ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} px-3 py-2 rounded-lg space-y-2`}>
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-gradient-to-br from-blue-500 to-cyan-500 rounded flex items-center justify-center flex-shrink-0 logo-active">
                        <span className="text-white font-bold text-[10px]">B</span>
                      </div>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span className="text-sm">
                        {thinkingMessage ? thinkingMessage :
                          isGeneratingData ? 'Generating data...' :
                            isGeneratingPreview ? 'Generating preview...' :
                              isGeneratingPrompts ? 'Generating prompts...' : 'Processing...'}
                      </span>
                    </div>
                    {/* Skeleton loading bars */}
                    <div className="space-y-1.5 pl-7">
                      <div className={`h-2 rounded-full skeleton-pulse ${isDarkMode ? 'bg-cyan-700/30' : 'bg-cyan-300/50'}`} style={{ width: '90%' }} />
                      <div className={`h-2 rounded-full skeleton-pulse ${isDarkMode ? 'bg-cyan-700/25' : 'bg-cyan-300/40'}`} style={{ width: '70%', animationDelay: '0.2s' }} />
                      <div className={`h-2 rounded-full skeleton-pulse ${isDarkMode ? 'bg-cyan-700/20' : 'bg-cyan-300/30'}`} style={{ width: '50%', animationDelay: '0.4s' }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Build Agent CTA — shown on guide or questionnaire when user might want to build */}
            {pendingBuildMessage && (currentView === 'guide' || currentView === 'questionnaire') && !agentId && !isLoading && (
              <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-white/10 bg-gray-900/30' : 'border-gray-200 bg-blue-50/50'}`}>
                <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
                  <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Would you like me to build an AI agent for this?
                  </p>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={async () => {
                        const msg = pendingBuildMessage;
                        setPendingBuildMessage(null);
                        if (currentView === 'guide') {
                          resetForNewAgent();
                          setCurrentView('questionnaire');
                        }
                        setIsLoading(true);
                        setThinkingMessage('Generating questionnaire...');
                        await proceedWithQuestionGeneration(msg);
                      }}
                      className="flex-1 px-3 py-2 bg-[#0077A8] text-white text-sm rounded-lg hover:bg-[#006090] flex items-center justify-center space-x-1.5 font-medium"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Yes, build it</span>
                    </button>
                    <button
                      onClick={() => setPendingBuildMessage(null)}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg flex items-center justify-center space-x-1.5 font-medium ${isDarkMode
                        ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                      <span>No, just asking</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Flow Selector - Show when multiple flows available and no agent yet */}
            {!isLoading && !isDecomposing && currentView === 'workflow' && workflowResult?.flows?.length > 1 && !agentId && (
              <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-white/10 bg-gray-900/30' : 'border-gray-200 bg-blue-50/50'}`}>
                <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${currentFlowIndex === 0 ? 'text-[#0099CC]' : currentFlowIndex === 1 ? 'text-emerald-500' : 'text-purple-500'
                      }`}>
                      {workflowResult.flows[currentFlowIndex]?.label || `Flow ${currentFlowIndex + 1}`}
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {currentFlowIndex + 1} of {workflowResult.flows.length} flows
                    </span>
                  </div>
                  <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {workflowResult.flows[currentFlowIndex]?.description || 'Select a flow to use for your AI agent.'}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => { setCurrentFlowIndex(prev => Math.max(0, prev - 1)); setCurrentNodeIndex(0); }}
                        disabled={currentFlowIndex === 0}
                        className={`p-1.5 rounded transition-colors ${currentFlowIndex === 0
                          ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                          : isDarkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-200'
                          }`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <div className="flex space-x-1.5 items-center">
                        {workflowResult.flows.map((flow, index) => (
                          <button
                            key={index}
                            onClick={() => { setCurrentFlowIndex(index); setCurrentNodeIndex(0); }}
                            className={`h-2 rounded-full transition-all duration-300 ${index === currentFlowIndex
                              ? `w-6 ${index === 0 ? 'bg-[#0099CC]' : index === 1 ? 'bg-emerald-500' : 'bg-purple-500'}`
                              : `w-2 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-400 hover:bg-gray-500'}`
                              }`}
                            title={flow.label}
                          />
                        ))}
                      </div>
                      <button
                        onClick={() => { setCurrentFlowIndex(prev => Math.min(workflowResult.flows.length - 1, prev + 1)); setCurrentNodeIndex(0); }}
                        disabled={currentFlowIndex === workflowResult.flows.length - 1}
                        className={`p-1.5 rounded transition-colors ${currentFlowIndex === workflowResult.flows.length - 1
                          ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                          : isDarkMode ? 'text-gray-300 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-200'
                          }`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {workflowResult.flows[currentFlowIndex]?.workflow_steps?.length || 0} steps
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Chat CTAs - Show based on current stage */}
            {!isLoading && !isDecomposing && (currentRound > 0 || workflowResult) && (
              <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-white/10 bg-gray-900/50' : 'border-gray-200 bg-gray-50/50'}`}>
                <div className="flex flex-wrap gap-2">
                  {/* Questionnaire 1 CTAs */}
                  {currentView === 'questionnaire' && currentRound === 1 && currentQuestionnaire && (
                    <>
                      {isViewingAnswers && !hasFormBeenEdited() ? (
                        /* Back to Agent Design - when viewing answers without edits */
                        <button
                          onClick={handleBackToAgentDesign}
                          className="flex-1 min-w-[140px] px-3 py-2 bg-[#0077A8] text-white text-sm rounded-md hover:bg-[#006090] flex items-center justify-center space-x-1"
                        >
                          <ArrowLeft className="w-3 h-3" />
                          <span>Back to Agent Design</span>
                        </button>
                      ) : (
                        /* Generate/Regenerate AI Agent */
                        <button
                          onClick={handleFormSubmit}
                          disabled={!isFormValid()}
                          className="flex-1 min-w-[140px] px-3 py-2 bg-[#0077A8] text-white text-sm rounded-md hover:bg-[#006090] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1"
                        >
                          <ArrowRight className="w-3 h-3" />
                          <span>{isViewingAnswers ? 'Regenerate AI Agent' : 'Generate AI Agent'}</span>
                        </button>
                      )}
                      {isViewingAnswers && hasFormBeenEdited() ? (
                        /* Discard Changes - when viewing answers with edits */
                        <button
                          onClick={handleBackToAgentDesign}
                          className={`px-3 py-2 text-sm rounded-md flex items-center justify-center space-x-1 ${isDarkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                          <ArrowLeft className="w-3 h-3" />
                          <span>Discard Changes</span>
                        </button>
                      ) : (
                        <button
                          onClick={handleStartOver}
                          className={`px-3 py-2 text-sm rounded-md flex items-center justify-center space-x-1 ${isDarkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                          <RefreshCw className="w-3 h-3" />
                          <span>Start Over</span>
                        </button>
                      )}
                    </>
                  )}

                  {/* Workflow/Decompose CTAs */}
                  {currentView === 'workflow' && workflowResult && (
                    <>
                      {isEditingFlow ? (
                        <>
                          <button
                            onClick={handleFinalizeEdits}
                            disabled={!allEditedNodesValidated || isSelectingFlow}
                            className={`flex-1 min-w-[140px] px-3 py-2 bg-[#0077A8] text-white text-sm rounded-md hover:bg-[#006090] flex items-center justify-center space-x-1 ${(!allEditedNodesValidated || isSelectingFlow) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isSelectingFlow ? (
                              <>
                                <Loader className="w-3 h-3 animate-spin" />
                                <span>Creating...</span>
                              </>
                            ) : (
                              <>
                                <Rocket className="w-3 h-3" />
                                <span>Finalize & Generate</span>
                              </>
                            )}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className={`px-3 py-2 text-sm rounded-md flex items-center justify-center space-x-1 ${isDarkMode
                              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                              }`}
                          >
                            <X className="w-3 h-3" />
                            <span>Cancel</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={handleProceedToData}
                            className="flex-1 min-w-[140px] px-3 py-2 bg-[#0077A8] text-white text-sm rounded-md hover:bg-[#006090] flex items-center justify-center space-x-1"
                          >
                            <Rocket className="w-3 h-3" />
                            <span>
                              {agentId
                                ? (agentDefinition?.status === 'variables_mapped' || agentDefinition?.status === 'VARIABLES_MAPPED')
                                  ? 'Generate Prompts'
                                  : (agentDefinition?.status === 'prompts_ready' || agentDefinition?.status === 'PROMPTS_READY')
                                    ? 'Run Agent'
                                    : 'Map Variables'
                                : workflowResult?.flows && workflowResult.flows.length > 1
                                  ? `Use ${workflowResult.flows[currentFlowIndex]?.label || 'This'} Flow`
                                  : 'Continue'
                              }
                            </span>
                          </button>
                          {!agentId && workflowResult?.flows && (
                            <button
                              onClick={handleEditFlow}
                              className={`px-3 py-2 text-sm rounded-md flex items-center justify-center space-x-1 ${isDarkMode
                                ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
                                : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                              <Edit3 className="w-3 h-3" />
                              <span>I want to tweak this</span>
                            </button>
                          )}
                          {!agentId && workflowResult?.flows && (
                            <button
                              onClick={runAutoPilot}
                              disabled={isSelectingFlow || isAutoPilot}
                              className="byte-magic-btn flex-1 min-w-[140px] px-3 py-2 text-sm rounded-lg flex items-center justify-center space-x-1.5 transition-all duration-200"
                            >
                              <Sparkles className="w-3.5 h-3.5 relative z-10" />
                              <span className="relative z-10">Let Byte handle it</span>
                            </button>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Map Data CTAs */}
                  {currentView === 'map-data' && (
                    <>
                      <button
                        onClick={handleGeneratePrompts}
                        disabled={isGeneratingPrompts || !(agentDefinition?.status === 'variables_mapped' || agentDefinition?.status === 'VARIABLES_MAPPED')}
                        className="flex-1 min-w-[140px] px-3 py-2 bg-[#0077A8] text-white text-sm rounded-md hover:bg-[#006090] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1"
                      >
                        <Code className="w-3 h-3" />
                        <span>{isGeneratingPrompts ? 'Generating...' : 'Generate Prompts'}</span>
                      </button>
                      <button
                        onClick={() => setCurrentView('workflow')}
                        className={`px-3 py-2 text-sm rounded-md flex items-center justify-center space-x-1 ${isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                          }`}
                      >
                        <ChevronLeft className="w-3 h-3" />
                        <span>Back to Plan</span>
                      </button>
                    </>
                  )}

                  {/* Execute/Present CTAs */}
                  {currentView === 'execute' && agentId && (
                    <>
                      <button
                        onClick={handleExportCode}
                        disabled={isExportingCode}
                        className={`flex-1 min-w-[140px] px-3 py-2 text-sm rounded-md flex items-center justify-center space-x-1 ${isExportingCode
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                          } ${isDarkMode
                            ? 'bg-emerald-700 text-white hover:bg-emerald-600'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                          }`}
                      >
                        {isExportingCode ? (
                          <Loader className="w-3 h-3 animate-spin" />
                        ) : (
                          <Download className="w-3 h-3" />
                        )}
                        <span>{isExportingCode ? 'Generating...' : 'Export Code'}</span>
                      </button>
                      <button
                        onClick={() => setCurrentView('prototype')}
                        className="flex-1 min-w-[140px] px-3 py-2 bg-[#0077A8] text-white text-sm rounded-md hover:bg-[#006090] flex items-center justify-center space-x-1"
                      >
                        <Sparkles className="w-3 h-3" />
                        <span>Generate Prototype</span>
                      </button>
                    </>
                  )}

                  {/* Prompts CTAs */}
                  {currentView === 'prompts' && generatedPrompts && (
                    <>
                      <button
                        onClick={() => {
                          // Reset execution state and navigate to execute view
                          setExecutionNodes([]);
                          setExecutionStatus(null);
                          setCurrentExecutingNodeIndex(-1);
                          setIsPromptFlowExpanded(true); // Expand flow by default
                          setCurrentView('execute');
                        }}
                        className="flex-1 min-w-[140px] px-3 py-2 bg-[#0077A8] text-white text-sm rounded-md hover:bg-[#006090] flex items-center justify-center space-x-1"
                      >
                        <Rocket className="w-3 h-3" />
                        <span>Run Agent</span>
                      </button>
                      <button
                        onClick={() => setCurrentView('map-data')}
                        className={`px-3 py-2 text-sm rounded-md flex items-center justify-center space-x-1 ${isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                          }`}
                      >
                        <ChevronLeft className="w-3 h-3" />
                        <span>Back to Map Data</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Jira Post Card — shown in prototype view after auto-pilot finishes (or manual navigation) */}
            {currentView === 'prototype' && jiraTicketKey && !isAutoPilot && (
              <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-white/10 bg-gray-900/50' : 'border-gray-200 bg-gray-50/50'}`}>
                {jiraPostStatus === 'posted' ? (
                  /* Collapsed success pill */
                  <div className={`flex items-center justify-center space-x-2 py-1.5 px-4 rounded-full text-sm font-medium mx-auto w-fit ${isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700/40' : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                    <CheckCircle className="w-4 h-4" />
                    <span>Posted to {jiraTicketKey}</span>
                  </div>
                ) : (
                  /* Editable preview card */
                  <div className={`rounded-xl border-2 overflow-hidden ${isDarkMode ? 'border-purple-700/40 bg-gray-800/60' : 'border-purple-200 bg-white'
                    }`}>
                    {/* Card header */}
                    <div className={`px-4 py-3 flex items-center justify-between ${isDarkMode ? 'bg-purple-900/20 border-b border-purple-700/30' : 'bg-purple-50/80 border-b border-purple-100'
                      }`}>
                      <div className="flex items-center space-x-2">
                        <svg className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.35V2.84a.84.84 0 0 0-.84-.84H11.53zM6.77 6.8a4.36 4.36 0 0 0 4.34 4.34h1.8v1.72a4.36 4.36 0 0 0 4.34 4.34V7.63a.84.84 0 0 0-.83-.83H6.77zM2 11.6a4.35 4.35 0 0 0 4.34 4.34h1.8v1.72a4.35 4.35 0 0 0 4.34 4.34v-9.57a.84.84 0 0 0-.84-.83H2z" />
                        </svg>
                        <span className={`text-sm font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                          Post to Jira
                        </span>
                        <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-purple-900/40 text-purple-400' : 'bg-purple-100 text-purple-600'
                          }`}>
                          {jiraTicketKey}
                        </span>
                      </div>
                    </div>

                    <div className="px-4 py-3 space-y-3">
                      {/* Summary — editable textarea */}
                      <div>
                        <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Summary
                        </label>
                        <textarea
                          value={jiraPostSummary}
                          onChange={(e) => setJiraPostSummary(e.target.value)}
                          rows={1}
                          className={`w-full px-3 py-2 rounded-lg text-sm border resize-none focus:outline-none focus:ring-2 ${isDarkMode
                            ? 'bg-gray-700/50 border-gray-600 text-gray-200 placeholder-gray-500 focus:ring-purple-500/50'
                            : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:ring-purple-500/30'
                            }`}
                          style={{ minHeight: '36px' }}
                          onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                        />
                      </div>

                      {/* Requirements — readable list with remove buttons */}
                      {jiraPostRequirements.length > 0 && (
                        <div>
                          <label className={`text-xs font-medium mb-1.5 block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            What we'll include
                          </label>
                          <div className="space-y-1">
                            {jiraPostRequirements.map((req, idx) => {
                              const colonIdx = req.indexOf(':');
                              const alias = colonIdx > -1 ? req.slice(0, colonIdx) : null;
                              const desc = colonIdx > -1 ? req.slice(colonIdx + 1).trim() : req;
                              return (
                                <div
                                  key={idx}
                                  className={`group flex items-start justify-between gap-2 px-2.5 py-1.5 rounded-lg text-xs ${isDarkMode
                                    ? 'bg-gray-700/40 hover:bg-gray-700/70'
                                    : 'bg-gray-50 hover:bg-gray-100'
                                    }`}
                                >
                                  <div className="flex items-start space-x-1.5 min-w-0">
                                    <span className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${isDarkMode ? 'bg-purple-400' : 'bg-purple-500'}`} />
                                    <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
                                      {alias && <span className="font-medium">{alias}: </span>}
                                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{desc}</span>
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => setJiraPostRequirements(prev => prev.filter((_, i) => i !== idx))}
                                    className={`flex-shrink-0 p-0.5 rounded transition-colors opacity-0 group-hover:opacity-100 ${isDarkMode ? 'hover:bg-gray-600 text-gray-500 hover:text-gray-300' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'
                                      }`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Auto-generated links */}
                      <div className="flex flex-wrap gap-2">
                        <span className={`inline-flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-blue-900/30 text-blue-400 border border-blue-700/30' : 'bg-blue-50 text-blue-600 border border-blue-200'
                          }`}>
                          <Play className="w-3 h-3" />
                          <span>Prototype link</span>
                        </span>
                        <span className={`inline-flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-cyan-900/30 text-cyan-400 border border-cyan-700/30' : 'bg-cyan-50 text-cyan-600 border border-cyan-200'
                          }`}>
                          <GitBranch className="w-3 h-3" />
                          <span>Design link</span>
                        </span>
                      </div>

                      {/* Error message */}
                      {jiraPostStatus === 'error' && (
                        <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                          Failed to post. Check your connection and try again.
                        </p>
                      )}

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={handlePostToJira}
                          disabled={jiraPostStatus === 'posting'}
                          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center space-x-2 transition-all ${jiraPostStatus === 'posting'
                            ? isDarkMode ? 'bg-purple-800/50 text-purple-300 cursor-wait' : 'bg-purple-200 text-purple-500 cursor-wait'
                            : isDarkMode ? 'bg-purple-600 text-white hover:bg-purple-500' : 'bg-purple-600 text-white hover:bg-purple-700'
                            }`}
                        >
                          {jiraPostStatus === 'posting' ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              <span>Posting...</span>
                            </>
                          ) : jiraPostStatus === 'error' ? (
                            <>
                              <RefreshCw className="w-4 h-4" />
                              <span>Retry</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.35V2.84a.84.84 0 0 0-.84-.84H11.53zM6.77 6.8a4.36 4.36 0 0 0 4.34 4.34h1.8v1.72a4.36 4.36 0 0 0 4.34 4.34V7.63a.84.84 0 0 0-.83-.83H6.77zM2 11.6a4.35 4.35 0 0 0 4.34 4.34h1.8v1.72a4.35 4.35 0 0 0 4.34 4.34v-9.57a.84.84 0 0 0-.84-.83H2z" />
                              </svg>
                              <span>Post to {jiraTicketKey}</span>
                            </>
                          )}
                        </button>
                        <div className={`flex items-center ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>
                          <span className="text-xs px-2">or</span>
                        </div>
                        <button
                          onClick={handleDismissJiraPost}
                          className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isDarkMode
                            ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Slash Command Pills — always visible on non-questionnaire screens */}
            {currentView !== 'questionnaire' && (
              <div className={`px-4 py-2 border-t ${isDarkMode ? 'border-white/10 bg-slate-800/50' : 'border-gray-200 bg-gray-50/80'}`}>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {/* Jira connection pill */}
                  {jiraConnected ? (
                    <span className={`px-2 py-1 text-xs rounded-full flex items-center space-x-1 ${isDarkMode
                      ? 'bg-blue-900/30 text-blue-400 border border-blue-700/30'
                      : 'bg-blue-50 text-blue-600 border border-blue-200/80'
                      }`}>
                      <CheckCircle className="w-3 h-3" />
                      <span>Jira{jiraSiteName ? ` · ${jiraSiteName}` : ''}</span>
                    </span>
                  ) : (
                    <button
                      onClick={handleConnectJira}
                      className={`px-2 py-1 text-xs rounded-full transition-colors flex items-center space-x-1 ${isDarkMode
                        ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-800/50 border border-blue-700/20'
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200/80'
                        }`}
                    >
                      <Zap className="w-3 h-3" />
                      <span>Connect Jira</span>
                    </button>
                  )}
                  <span className={`w-px h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`} />
                  {[
                    ...(currentView === 'prototype' ? [
                      { cmd: '/name', desc: 'Change app name' },
                      { cmd: '/color', desc: 'Change primary color' },
                      { cmd: '/theme', desc: 'Toggle dark/light' },
                      { cmd: '/button', desc: 'Change button text' },
                      { cmd: '/tagline', desc: 'Change tagline' },
                    ] : []),
                    { cmd: '/query', desc: 'Ask a question' },
                    { cmd: '/next', desc: 'Next screen' },
                    { cmd: '/back', desc: 'Previous screen' },
                  ].map(s => (
                    <button
                      key={s.cmd}
                      onClick={() => setInputMessage(s.cmd + ' ')}
                      className={`px-2 py-1 text-xs rounded-full transition-colors ${isDarkMode
                        ? 'bg-cyan-900/30 text-cyan-400 hover:bg-cyan-800/50 border border-cyan-700/20'
                        : 'bg-cyan-50 text-cyan-600 hover:bg-cyan-100 border border-cyan-200/80'
                        }`}
                    >
                      <span className="font-mono font-semibold">{s.cmd}</span>
                      <span className="ml-1 opacity-60">{s.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Getting Started CTAs — shown on guide or questionnaire when nothing loaded */}
            {(currentView === 'guide' || currentView === 'questionnaire') && !currentQuestionnaire && !jiraFlowStep && !isLoading && !isDecomposing && (
              <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-white/10 bg-gray-900/40' : 'border-gray-200 bg-gray-50/80'}`}>
                <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  How would you like to start?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (jiraConnected) {
                        addMessage('bot', 'Paste your Jira ticket key (e.g. PROJ-123) or Atlassian URL in the chat, and I\'ll extract everything from it.', true);
                      } else {
                        handleConnectJira();
                      }
                    }}
                    className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center space-x-2 transition-all hover:scale-[1.01] active:scale-[0.99] ${isDarkMode
                      ? 'bg-purple-900/40 text-purple-200 border border-purple-700/50 hover:bg-purple-800/50 hover:border-purple-600/60'
                      : 'bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 hover:border-purple-300'
                      }`}
                  >
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24.013 12.487V1.005A1.005 1.005 0 0 0 23.013 0z" />
                    </svg>
                    <span>{jiraConnected ? 'Fetch from Jira' : 'Connect Jira'}</span>
                  </button>
                  <button
                    onClick={() => {
                      addMessage('bot', "Let's build! Describe the AI agent you want to create — what problem should it solve, what inputs does it take, and what output do you expect? I'll take it from there.", true);
                    }}
                    className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center space-x-2 transition-all hover:scale-[1.01] active:scale-[0.99] ${isDarkMode
                      ? 'bg-cyan-900/40 text-cyan-200 border border-cyan-700/50 hover:bg-cyan-800/50 hover:border-cyan-600/60'
                      : 'bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 hover:border-cyan-300'
                      }`}
                  >
                    <MessageSquare className="w-4 h-4 flex-shrink-0" />
                    <span>Describe Manually</span>
                  </button>
                </div>
              </div>
            )}

            {/* Chat Input */}
            <div className={`p-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
              {/* Auto-pilot status indicator */}
              {isAutoPilot && (
                <div className={`mb-2 flex items-center space-x-2 px-3 py-2 rounded-lg text-xs ${isDarkMode
                  ? 'bg-cyan-900/30 border border-cyan-700/30 text-cyan-300'
                  : 'bg-cyan-50 border border-cyan-200 text-cyan-700'
                  }`}>
                  <div className="flex space-x-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="font-medium">
                    {isAutoPilotPaused ? 'Byte is paused — resume to continue' : 'Byte is working on your agent...'}
                  </span>
                </div>
              )}
              {/* Slash Command Autocomplete Popup */}
              {inputMessage.startsWith('/') && (() => {
                const allCmds = [
                  ...(currentView === 'prototype' ? [
                    { cmd: '/name', desc: 'Change app name' },
                    { cmd: '/color', desc: 'Change primary color' },
                    { cmd: '/theme', desc: 'Toggle dark/light' },
                    { cmd: '/button', desc: 'Change button text' },
                    { cmd: '/tagline', desc: 'Change tagline' },
                  ] : []),
                  { cmd: '/query', desc: 'Ask a question' },
                  { cmd: '/next', desc: 'Next screen' },
                  { cmd: '/back', desc: 'Previous screen' },
                ];
                const query = inputMessage.split(' ')[0].toLowerCase();
                const filtered = allCmds.filter(s => s.cmd.startsWith(query));
                if (filtered.length === 0 || (filtered.length === 1 && filtered[0].cmd === query)) return null;
                return (
                  <div className={`mb-2 rounded-lg border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-gray-700' : 'bg-white border-gray-200 shadow-lg'}`}>
                    {filtered.map(s => (
                      <button
                        key={s.cmd}
                        onClick={() => setInputMessage(s.cmd + ' ')}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center space-x-2 transition-colors ${isDarkMode ? 'hover:bg-cyan-900/30 text-gray-200' : 'hover:bg-cyan-50 text-gray-700'
                          }`}
                      >
                        <span className={`font-mono font-semibold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{s.cmd}</span>
                        <span className="opacity-50">{s.desc}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !isLoading && !isDecomposing && !isAutoPilot && !pendingBuildMessage && handleSendMessage()}
                  placeholder={pendingBuildMessage ? 'Please choose an option above...' : isAutoPilot ? 'Byte is working... pause or take over to chat' : currentView === 'prototype' ? 'Type a message or / for commands...' : 'Type a message...'}
                  className={`flex-1 px-4 py-2.5 bg-cyan-500/15 border border-cyan-400/30 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'
                    } ${(isAutoPilot || pendingBuildMessage) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isLoading || isDecomposing || isAutoPilot || !!pendingBuildMessage}
                />
                {(isLoading || isDecomposing || isGeneratingPrompts || isGeneratingData || isGeneratingPreview) ? (
                  <button
                    onClick={handleStop}
                    className={`px-4 py-2.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors ${isDarkMode ? 'dark-tooltip' : 'light-tooltip'}`}
                    data-tooltip="Stop"
                  >
                    <Square className="w-5 h-5 fill-current" />
                  </button>
                ) : (
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputMessage.trim()}
                    className="px-4 py-2.5 bg-[#0077A8] text-white rounded-md hover:bg-[#006090] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

          {/* Hover hint bar */}
          <div
            className={`px-3 py-1.5 border-t text-center transition-all duration-300 ${
              isDarkMode ? 'bg-slate-800 border-white/10' : 'bg-gray-100 border-gray-200'
            } ${chatHovered ? 'opacity-100 max-h-10' : 'opacity-0 max-h-0 overflow-hidden py-0'}`}
          >
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {chatDocked ? 'double-click header to undock' : 'double-click header to dock'} • drag header to move
            </span>
          </div>

          {/* Resize Handle - only show when floating */}
          {!chatDocked && (
              <div
                className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
                onMouseDown={handleResizeStart}
                style={{
                  background: 'linear-gradient(135deg, transparent 50%, #4B5563 50%)',
                  borderBottomRightRadius: '8px'
                }}
              />
            )}
          </div>
        </div>
      ) : (
        /* Collapsed Chat Bubble — Orb */
        <button
          onClick={() => setChatOpen(true)}
          className={`fixed z-50 bottom-6 left-6 w-14 h-14 rounded-full shadow-lg overflow-hidden hover:scale-110 transition-transform duration-200 cursor-pointer ${isDarkMode ? 'dark-tooltip' : 'light-tooltip'}`}
          data-tooltip="Open chat"
          style={{ padding: 0, border: 'none', background: 'transparent' }}
        >
          <Orb
            hue={190}
            hoverIntensity={0.5}
            rotateOnHover={true}
            forceHoverState={false}
            backgroundColor={isDarkMode ? '#0f172a' : '#f8fafc'}
          />
        </button>
      )}

      {/* Execution History Sidebar - docks opposite to chat */}
      <div
        className={`fixed top-[60px] h-[calc(100vh-60px)] z-30 transition-all duration-300 ${chatDocked === 'left' || chatDocked === null
          ? `right-0 ${executionSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`
          : `left-0 ${executionSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
          }`}
        style={{ width: '280px' }}
      >
        <div className={`h-full backdrop-blur-xl shadow-xl ${chatDocked === 'left' || chatDocked === null ? 'border-l' : 'border-r'
          } ${isDarkMode
            ? 'bg-slate-900/95 border-white/10'
            : 'bg-white/95 border-gray-200'
          }`}>
          {/* Header */}
          <div className={`px-4 py-3 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <h3 className={`text-sm font-semibold flex items-center space-x-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              <Clock className="w-4 h-4" />
              <span>Past Runs</span>
            </h3>
            <button
              onClick={() => setExecutionSidebarOpen(false)}
              className={`p-1 rounded transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Runs list */}
          <div className="overflow-y-auto h-[calc(100%-52px)] px-3 py-2">
            {loadingExecutionHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-5 h-5 animate-spin text-cyan-500" />
              </div>
            ) : executionHistoryList.length === 0 ? (
              <p className={`text-xs py-4 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                No past executions yet
              </p>
            ) : (
              <div className="space-y-1.5">
                {executionHistoryList.map((run) => {
                  const isSelected = viewingHistoryRun === run.executionId;
                  const duration = run.completedAt && run.startedAt
                    ? Math.round((new Date(run.completedAt) - new Date(run.startedAt)) / 1000)
                    : null;
                  return (
                    <div
                      key={run.executionId}
                      className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer ${isSelected
                        ? isDarkMode
                          ? 'bg-cyan-900/30 border border-cyan-700/50'
                          : 'bg-cyan-50 border border-cyan-200'
                        : isDarkMode
                          ? 'hover:bg-gray-800/50 border border-transparent'
                          : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      onClick={() => loadExecutionRun(run.executionId)}
                    >
                      <div className="flex items-start space-x-2.5">
                        {/* Status icon */}
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${run.status === 'completed' ? 'bg-green-500/20' :
                          run.status === 'failed' ? 'bg-red-500/20' :
                            'bg-cyan-500/20'
                          }`}>
                          {run.status === 'completed' && <CheckCircle className="w-3.5 h-3.5 text-green-400" />}
                          {run.status === 'failed' && <AlertCircle className="w-3.5 h-3.5 text-red-400" />}
                          {run.status === 'running' && <Loader className="w-3.5 h-3.5 animate-spin text-cyan-400" />}
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Run label */}
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {run.sampleIndex !== null && run.sampleIndex !== undefined
                              ? `Sample ${run.sampleIndex + 1}`
                              : 'Custom Input'}
                          </p>

                          {/* Metadata row */}
                          <div className="flex items-center space-x-2 mt-0.5 flex-wrap">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${run.status === 'completed'
                              ? isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'
                              : run.status === 'failed'
                                ? isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'
                                : isDarkMode ? 'bg-cyan-900/50 text-cyan-400' : 'bg-cyan-100 text-cyan-700'
                              }`}>
                              {run.status}
                            </span>
                            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {run.nodeCount} nodes
                            </span>
                            {duration !== null && (
                              <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {duration}s
                              </span>
                            )}
                          </div>

                          {/* Timestamp */}
                          <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                            {new Date(run.startedAt).toLocaleDateString(undefined, {
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>

                          {/* Error preview */}
                          {run.error && (
                            <p className={`text-[10px] mt-0.5 truncate ${isDarkMode ? 'text-red-400/70' : 'text-red-500/70'}`}>
                              {run.error.substring(0, 60)}{run.error.length > 60 ? '...' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Panel - Adjusts based on docked chat */}
      <div
        className="flex flex-col transition-all duration-300 px-4 pb-4 z-10 overflow-hidden"
        style={getContentStyle()}
      >
        <div
          className={`${chatOpen ? 'max-w-5xl' : 'max-w-7xl'} w-full mx-auto flex flex-col flex-1 min-h-0 rounded-3xl shadow-2xl shadow-cyan-500/10 overflow-hidden p-[1px] transition-all duration-300`}
          style={{ background: 'linear-gradient(135deg, #3b82f6, #0ea5e9, #06b6d4, #0ea5e9, #3b82f6)' }}
        >
          <div className={`flex flex-col flex-1 min-h-0 backdrop-blur-2xl rounded-3xl overflow-hidden ${isDarkMode ? 'bg-slate-900/95' : 'bg-white/95'}`}>

            {/* Auto-pilot progress bar with Pause / Resume */}
            {isAutoPilot && (
              <div className={`px-4 py-2.5 flex items-center justify-between border-b ${isDarkMode ? 'bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border-cyan-700/30' : 'bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200'
                }`}>
                <div className="flex items-center space-x-3 flex-1">
                  <Sparkles className={`w-4 h-4 ${isAutoPilotPaused ? '' : 'animate-pulse'} ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                    {isAutoPilotPaused ? 'Byte paused' : 'Byte is working'}
                  </span>
                  <div className="flex-1 max-w-xs">
                    <div className={`h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-cyan-900/50' : 'bg-cyan-100'}`}>
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${isAutoPilotPaused ? 'bg-amber-400' : 'bg-gradient-to-r from-cyan-500 to-blue-500'}`}
                        style={{ width: `${(autoPilotStep / autoPilotTotal) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className={`text-xs ${isDarkMode ? 'text-cyan-400/70' : 'text-cyan-600/70'}`}>
                    {autoPilotStep}/{autoPilotTotal}
                  </span>
                </div>
                <div className="flex items-center space-x-2 ml-3">
                  {isAutoPilotPaused ? (
                    <button
                      onClick={handleResumeAutoPilot}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${isDarkMode
                        ? 'bg-cyan-600 text-white hover:bg-cyan-500 shadow-lg shadow-cyan-500/30'
                        : 'bg-cyan-600 text-white hover:bg-cyan-500 shadow-md shadow-cyan-300/50'
                        }`}
                    >
                      <span className="flex items-center space-x-1">
                        <Play className="w-3 h-3" />
                        <span>Resume</span>
                      </span>
                    </button>
                  ) : (
                    <button
                      onClick={handlePauseAutoPilot}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${isDarkMode
                        ? 'bg-amber-800/50 text-amber-300 hover:bg-amber-700/50 border border-amber-600/30'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-300'
                        }`}
                    >
                      <span className="flex items-center space-x-1">
                        <Square className="w-2.5 h-2.5" />
                        <span>Pause Byte</span>
                      </span>
                    </button>
                  )}
                  <button
                    onClick={handleTakeOver}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${isDarkMode
                      ? 'bg-gray-700/50 text-gray-400 hover:bg-gray-600/50 border border-gray-600/30'
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-300'
                      }`}
                  >
                    Take over
                  </button>
                </div>
              </div>
            )}

            <div className={`liquid-glass p-4 backdrop-blur-xl ${isDarkMode ? 'bg-slate-800/80' : 'bg-white/80'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-semibold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {currentView === 'guide' ? (
                    <>
                      <BookOpen className="w-5 h-5 mr-2 text-cyan-400" />
                      Getting Started
                    </>
                  ) : currentView === 'questionnaire' ? (
                    <>
                      <FileText className={`w-5 h-5 mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      {jiraFlowStep ? 'Jira Ticket Review' : 'Questionnaire Preview'}
                      {jiraFlowStep && jiraConsolidation?.ticket_key && (
                        <span className={`ml-2 px-2 py-1 text-xs rounded-full border ${isDarkMode ? 'bg-purple-900/50 text-purple-300 border-purple-700/50' : 'bg-purple-100 text-purple-700 border-purple-300'}`}>
                          {jiraConsolidation.ticket_key}
                        </span>
                      )}
                      {!jiraFlowStep && currentRound > 0 && (
                        <span className={`ml-2 px-2 py-1 text-xs rounded-full border ${isDarkMode ? 'bg-blue-900/50 text-blue-300 border-blue-700/50' : 'bg-blue-100 text-blue-700 border-blue-300'}`}>
                          Round {currentRound}
                        </span>
                      )}
                    </>
                  ) : currentView === 'workflow' ? (
                    <>
                      <Workflow className="w-5 h-5 mr-2 text-green-400" />
                      Generated AI Agent
                    </>
                  ) : currentView === 'prompts' ? (
                    <>
                      <Code className="w-5 h-5 mr-2 text-blue-400" />
                      Generated Prompts
                    </>
                  ) : currentView === 'data-preview' ? (
                    <>
                      <Eye className="w-5 h-5 mr-2 text-orange-400" />
                      Data Preview & Edit
                    </>
                  ) : currentView === 'map-data' ? (
                    <>
                      <Database className="w-5 h-5 mr-2 text-teal-400" />
                      Map Variables
                    </>
                  ) : currentView === 'data' ? (
                    <>
                      <Database className="w-5 h-5 mr-2 text-teal-400" />
                      Generated Test Data
                    </>
                  ) : currentView === 'execute' ? (
                    <>
                      <Rocket className="w-5 h-5 mr-2 text-cyan-400" />
                      Run Agent
                    </>
                  ) : currentView === 'present' ? (
                    <>
                      <Play className="w-5 h-5 mr-2 text-green-400" />
                      Present
                    </>
                  ) : currentView === 'prototype' ? (
                    <>
                      <Sparkles className="w-5 h-5 mr-2 text-purple-400" />
                      Prototype
                    </>
                  ) : (
                    <>
                      <Database className="w-5 h-5 mr-2 text-teal-400" />
                      Generated Test Data
                    </>
                  )}
                </h2>

                <div className="flex items-center space-x-2">
                  {currentView === 'prompts' && generatedPrompts && (
                    <button
                      onClick={handleDownloadPrompts}
                      className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200 flex items-center space-x-1 border border-blue-300"
                    >
                      <Download className="w-3 h-3" />
                      <span>Download JSON</span>
                    </button>
                  )}

                  {currentView === 'data-preview' && previewData && (
                    <>
                      {previewStep === 1 && (
                        <button
                          onClick={handleUpdatePreview}
                          disabled={isLoading}
                          className={`px-3 py-1 text-xs rounded-full flex items-center space-x-1 disabled:opacity-50 ${isDarkMode ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-800/50 border border-blue-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300'}`}
                        >
                          <Edit3 className="w-3 h-3" />
                          <span>{isLoading ? 'Learning...' : 'Learn Patterns'}</span>
                        </button>
                      )}
                      {previewStep === 2 && (
                        <button
                          onClick={handleGenerateFromPreview}
                          disabled={isGeneratingData}
                          className="px-3 py-1 bg-teal-900/50 text-teal-300 text-xs rounded-full hover:bg-teal-800/50 flex items-center space-x-1 disabled:opacity-50 border border-teal-700/50"
                        >
                          <Database className="w-3 h-3" />
                          <span>{isGeneratingData ? 'Generating...' : 'Generate Final Data'}</span>
                        </button>
                      )}
                      <button
                        onClick={() => setCurrentView('prompts')}
                        className={`px-3 py-1 text-xs rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`}
                      >
                        Back to Prompts
                      </button>
                    </>
                  )}

                  {currentView === 'data' && generatedData && (
                    <>
                      <button
                        onClick={handleDownloadData}
                        className={`px-3 py-1 text-xs rounded-full flex items-center space-x-1 ${isDarkMode ? 'bg-green-900/50 text-green-300 hover:bg-green-800/50 border border-green-700' : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'}`}
                      >
                        <Download className="w-3 h-3" />
                        <span>Download CSV</span>
                      </button>
                      <button
                        onClick={() => setCurrentView('data-preview')}
                        className={`px-3 py-1 text-xs rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'}`}
                      >
                        Back to Preview
                      </button>
                    </>
                  )}
                </div>
              </div>

              {analysisResult && currentView === 'questionnaire' && (
                <div className="mt-2 flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    {analysisResult.clarity_score > 0.8 ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-yellow-400" />
                    )}
                    <span className="text-sm text-gray-600">
                      Clarity: {(analysisResult.clarity_score * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {workflowResult && currentView === 'workflow' && (
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                  <span>Steps: {(workflowResult.flows?.[currentFlowIndex]?.workflow_steps || workflowResult.primary_workflow || []).length}</span>
                  <span>Cost: {workflowResult.flows?.[currentFlowIndex]?.total_estimated_cost || workflowResult.total_estimated_cost}</span>
                  <span>Time: {workflowResult.flows?.[currentFlowIndex]?.total_estimated_time || workflowResult.total_estimated_time}</span>
                  {getPromptdelCount() > 0 && (
                    <span className="text-blue-400">
                      LLM Nodes: {getPromptdelCount()}
                    </span>
                  )}
                </div>
              )}

              {generatedPrompts && currentView === 'prompts' && (
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                  <span>Generated Prompts: {generatedPrompts.generated_prompts.length}</span>
                  <span>Workflow ID: {generatedPrompts.workflow_id}</span>
                </div>
              )}

              {previewData && currentView === 'data-preview' && (
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                  <span>Preview ID: {previewData.previewId}</span>
                  <span>Samples: {previewData.sampleRows.length}</span>
                  <span>Final Rows: {dataGenerationSettings.numRows}</span>
                </div>
              )}

              {generatedData && currentView === 'data' && (
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                  <span>Rows: {generatedData.numRows}</span>
                  <span>Concept: {generatedData.conceptId}</span>
                  <span>Version: {generatedData.dataVersion}</span>
                </div>
              )}
            </div>
            {/* Gradient border bar */}
            <div className="h-[1px]" style={{ background: 'linear-gradient(90deg, #3b82f6, #0ea5e9, #06b6d4, #0ea5e9, #3b82f6)' }}></div>

            <div ref={contentContainerRef} className="flex-1 overflow-y-auto p-6 relative z-10">
              {/* Check loading states FIRST before content */}
              {isAnalyzingQuestionnaire || (isLoading && !currentQuestionnaire) ? (
                /* Skeleton loader for questionnaire analysis / generation */
                <QuestionnaireSkeleton thinkingMessage={thinkingMessage} isDarkMode={isDarkMode} />
              ) : isDecomposing || isSelectingFlow ? (
                /* Skeleton loader for workflow/agent generation */
                <WorkflowSkeleton thinkingMessage={thinkingMessage} isDarkMode={isDarkMode} />
              ) : currentView === 'guide' ? (
                <DocumentationView
                  isDarkMode={isDarkMode}
                  onAskByte={(question) => handleSendMessage(question)}
                  onBuildUseCase={async (prompt) => {
                    resetForNewAgent();
                    addMessage('user', prompt);
                    setCurrentView('questionnaire');
                    setIsLoading(true);
                    setThinkingMessage('Generating questionnaire...');
                    await proceedWithQuestionGeneration(prompt);
                  }}
                  onStartBuilding={() => {
                    resetForNewAgent();
                    setCurrentView('questionnaire');
                    addMessage('bot', "Let's build! Describe the AI agent you want to create — what problem should it solve, what inputs does it take, and what output do you expect? I'll take it from there.", true);
                  }}
                  jiraConnected={jiraConnected}
                  onFetchFromJira={() => {
                    setCurrentView('questionnaire');
                    addMessage('bot', 'Paste your Jira ticket key (e.g. PROJ-123) or Atlassian URL in the chat, and I\'ll extract everything from it.', true);
                  }}
                  onConnectJira={handleConnectJira}
                />
              ) : currentView === 'questionnaire' && (jiraFlowStep || currentQuestionnaire) ? (
                <QuestionnaireView
                  isDarkMode={isDarkMode}
                  currentQuestionnaire={currentQuestionnaire}
                  formData={formData}
                  otherModeFields={otherModeFields}
                  currentRound={currentRound}
                  lowClarityMode={lowClarityMode}
                  missingContextItems={missingContextItems}
                  additionalDetails={additionalDetails}
                  isViewingAnswers={isViewingAnswers}
                  isLoading={isLoading}
                  jiraFlowStep={jiraFlowStep}
                  jiraConsolidation={jiraConsolidation}
                  jiraChecklist={jiraChecklist}
                  jiraGapAnswers={jiraGapAnswers}
                  isFormValid={isFormValid()}
                  handleInputChange={handleInputChange}
                  handleCopyHint={handleCopyHint}
                  setOtherModeFields={setOtherModeFields}
                  setAdditionalDetails={setAdditionalDetails}
                  handleFormSubmit={handleFormSubmit}
                  handleProceedAnyway={handleProceedAnyway}
                  handleStartOver={handleStartOver}
                  handleBackToAgentDesign={handleBackToAgentDesign}
                  hasFormBeenEdited={hasFormBeenEdited}
                  setJiraChecklist={setJiraChecklist}
                  handleJiraChecklistConfirm={handleJiraChecklistConfirm}
                  setJiraGapAnswers={setJiraGapAnswers}
                  handleJiraGapSubmit={handleJiraGapSubmit}
                  setJiraFlowStep={setJiraFlowStep}
                  setJiraConsolidation={setJiraConsolidation}
                />
              ) : currentView === 'workflow' && workflowResult ? (
                <WorkflowView
                  isDarkMode={isDarkMode}
                  currentView={currentView}
                  workflowResult={workflowResult}
                  agentDefinition={agentDefinition}
                  agentId={agentId}
                  currentFlowIndex={currentFlowIndex}
                  currentNodeIndex={currentNodeIndex}
                  descriptionExpanded={descriptionExpanded}
                  flowExpanded={flowExpanded}
                  detailsExpanded={detailsExpanded}
                  activeDetailTab={activeDetailTab}
                  isEditingFlow={isEditingFlow}
                  editedNodes={editedNodes}
                  editingNodeIndex={editingNodeIndex}
                  nodeValidations={nodeValidations}
                  isValidatingNode={isValidatingNode}
                  draftNodeValues={draftNodeValues}
                  isSelectingFlow={isSelectingFlow}
                  allEditedNodesValidated={allEditedNodesValidated}
                  flowPillStyle={flowPillStyle}
                  detailPillStyle={detailPillStyle}
                  flowTabContainerRef={flowTabContainerRef}
                  flowTabRefs={flowTabRefs}
                  detailTabContainerRef={detailTabContainerRef}
                  detailTabRefs={detailTabRefs}
                  isAutoPilot={isAutoPilot}
                  setCurrentFlowIndex={setCurrentFlowIndex}
                  setCurrentNodeIndex={setCurrentNodeIndex}
                  setDescriptionExpanded={setDescriptionExpanded}
                  setFlowExpanded={setFlowExpanded}
                  setDetailsExpanded={setDetailsExpanded}
                  setActiveDetailTab={setActiveDetailTab}
                  setEditingNodeIndex={setEditingNodeIndex}
                  setDraftNodeValues={setDraftNodeValues}
                  handleEditFlow={handleEditFlow}
                  handleCancelEdit={handleCancelEdit}
                  handleNodeEdit={handleNodeEdit}
                  handleApplySuggestion={handleApplySuggestion}
                  handleAddNode={handleAddNode}
                  handleRemoveNode={handleRemoveNode}
                  handleValidateNode={handleValidateNode}
                  handleFinalizeEdits={handleFinalizeEdits}
                  handleProceedToData={handleProceedToData}
                  runAutoPilot={runAutoPilot}
                  addMessage={addMessage}
                />
              ) : currentView === 'prompts' && (isGeneratingPrompts || generatedPrompts) ? (
                <PromptsView
                  isDarkMode={isDarkMode}
                  generatedPrompts={generatedPrompts}
                  isGeneratingPrompts={isGeneratingPrompts}
                  selectedPromptNodeIndex={selectedPromptNodeIndex}
                  isPromptFlowExpanded={isPromptFlowExpanded}
                  promptTestResults={promptTestResults}
                  isRunningPromptTest={isRunningPromptTest}
                  requestJsonOutput={requestJsonOutput}
                  workflowResult={workflowResult}
                  currentFlowIndex={currentFlowIndex}
                  agentDefinition={agentDefinition}
                  setSelectedPromptNodeIndex={setSelectedPromptNodeIndex}
                  setIsPromptFlowExpanded={setIsPromptFlowExpanded}
                  setRequestJsonOutput={setRequestJsonOutput}
                  setPromptTestResults={setPromptTestResults}
                  setExecutionNodes={setExecutionNodes}
                  setExecutionStatus={setExecutionStatus}
                  setCurrentExecutingNodeIndex={setCurrentExecutingNodeIndex}
                  setCurrentView={setCurrentView}
                  handleTestPrompt={handleTestPrompt}
                  handleCopyPrompt={handleCopyPrompt}
                />
              ) : currentView === 'execute' ? (
                <ExecuteView
                  isDarkMode={isDarkMode}
                  agentId={agentId}
                  workflowResult={workflowResult}
                  currentFlowIndex={currentFlowIndex}
                  agentDefinition={agentDefinition}
                  generatedData={generatedData}
                  editedSamples={editedSamples}
                  isExecuting={isExecuting}
                  executionStatus={executionStatus}
                  executionNodes={executionNodes}
                  currentExecutingNodeIndex={currentExecutingNodeIndex}
                  executionCarouselIndex={executionCarouselIndex}
                  switchingToNode={switchingToNode}
                  totalExecutionNodes={totalExecutionNodes}
                  selectedExecutionSample={selectedExecutionSample}
                  executionDisplayMode={executionDisplayMode}
                  executionDetailsExpanded={executionDetailsExpanded}
                  copiedSection={copiedSection}
                  executionSidebarOpen={executionSidebarOpen}
                  viewingHistoryRun={viewingHistoryRun}
                  isPromptFlowExpanded={isPromptFlowExpanded}
                  isExportingCode={isExportingCode}
                  exportProgress={exportProgress}
                  setSelectedExecutionSample={setSelectedExecutionSample}
                  setExecutionCarouselIndex={setExecutionCarouselIndex}
                  setIsPromptFlowExpanded={setIsPromptFlowExpanded}
                  setExecutionDisplayMode={setExecutionDisplayMode}
                  setExecutionDetailsExpanded={setExecutionDetailsExpanded}
                  setCopiedSection={setCopiedSection}
                  setSwitchingToNode={setSwitchingToNode}
                  setCurrentView={setCurrentView}
                  onRunExecution={handleRunExecution}
                  toggleExecutionSidebar={toggleExecutionSidebar}
                  handleDownloadExecutionResults={handleDownloadExecutionResults}
                  handleExportCode={handleExportCode}
                  switchTimerRef={switchTimerRef}
                />

              ) : currentView === 'prototype' ? (
                <PrototypeView
                  agentId={agentId}
                  agentDefinition={agentDefinition}
                  isDarkMode={isDarkMode}
                  token={token}
                  executionApi={EXECUTION_API}
                  externalConfig={prototypeConfig}
                  configUpdate={prototypeConfigUpdate}
                  onConfigChange={(config) => setPrototypeConfig(config)}
                  autoExecute={protoAutoExecute}
                  onInsufficientCredits={handleInsufficientCredits}
                  userId={userId}
                  onAutoExecuteComplete={() => {
                    setProtoAutoExecute(false);
                    if (protoAutoExecuteResolveRef.current) {
                      protoAutoExecuteResolveRef.current();
                      protoAutoExecuteResolveRef.current = null;
                    }
                  }}
                />
              ) : (currentView === 'data-preview' || currentView === 'map-data' || currentView === 'data') ? (
                <MapDataView
                  isDarkMode={isDarkMode}
                  currentView={currentView}
                  agentDefinition={agentDefinition}
                  workflowResult={workflowResult}
                  currentFlowIndex={currentFlowIndex}
                  currentNodeIndex={currentNodeIndex}
                  isAutoPilot={isAutoPilot}
                  thinkingMessage={thinkingMessage}
                  mapDataSubTab={mapDataSubTab}
                  variableDictExpanded={variableDictExpanded}
                  hoveredVarExample={hoveredVarExample}
                  generatedData={generatedData}
                  generatedSchema={generatedSchema}
                  previewData={previewData}
                  editedSamples={editedSamples}
                  previewStep={previewStep}
                  learnedPatterns={learnedPatterns}
                  isGeneratingPrompts={isGeneratingPrompts}
                  isSelectingFlow={isSelectingFlow}
                  agentId={agentId}
                  setCurrentNodeIndex={setCurrentNodeIndex}
                  setMapDataSubTab={setMapDataSubTab}
                  setVariableDictExpanded={setVariableDictExpanded}
                  setHoveredVarExample={setHoveredVarExample}
                  setCurrentView={setCurrentView}
                  handleAddColumn={handleAddColumn}
                  handleRemoveColumn={handleRemoveColumn}
                  handleAddRow={handleAddRow}
                  handleSampleEdit={handleSampleEdit}
                  handleProceedToData={handleProceedToData}
                  onGeneratePrompts={handleGeneratePrompts}
                  shouldScrollToTestData={shouldScrollToTestData}
                  onTestDataScrolled={() => setShouldScrollToTestData(false)}
                />

              ) : (
                <div className={`flex flex-col items-center justify-center h-full ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FileText className={`w-16 h-16 mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`} />
                  <h3 className="text-lg font-medium mb-2">
                    {currentView === 'questionnaire'
                      ? 'No questionnaire generated yet'
                      : currentView === 'workflow'
                        ? 'No AI feature generated yet'
                        : currentView === 'prompts'
                          ? 'No prompts generated yet'
                          : currentView === 'data-preview'
                            ? 'No preview data generated yet'
                            : currentView === 'data'
                              ? 'No test data generated yet'
                              : 'No content yet'
                    }
                  </h3>
                  <p className="text-center">
                    {currentView === 'data'
                      ? 'Map variables first in the Map Data tab, then generate test data here.'
                      : 'Describe your problem in the chat to get started'
                    }
                  </p>
                </div>
              )}

              {/* ── Auto-pilot 3-2-1 countdown overlay ── */}
              {isAutoPilot && autoPilotCountdownValue !== null && (
                <div className="sticky bottom-4 left-0 right-0 z-30 pointer-events-none">
                  <div className={`mx-auto max-w-sm pointer-events-auto rounded-2xl shadow-2xl border backdrop-blur-xl p-5 ${isDarkMode
                    ? 'bg-slate-900/95 border-cyan-700/40 shadow-cyan-900/30'
                    : 'bg-white/95 border-cyan-200 shadow-cyan-100/50'
                    }`}>
                    <div className="flex items-center space-x-4">
                      {/* Countdown circle */}
                      <div className="relative w-14 h-14 flex-shrink-0">
                        <svg className="w-14 h-14 -rotate-90" viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="45" fill="none" stroke={isDarkMode ? 'rgba(6,182,212,0.15)' : 'rgba(6,182,212,0.2)'} strokeWidth="6" />
                          <circle cx="50" cy="50" r="45" fill="none" stroke="rgb(6,182,212)" strokeWidth="6"
                            strokeDasharray="283" strokeLinecap="round"
                            style={{ strokeDashoffset: 283 - (283 * (1 - autoPilotCountdownValue / 3)), animation: 'countdown-ring 1s linear', transition: 'stroke-dashoffset 0.3s ease' }}
                          />
                        </svg>
                        <span className={`absolute inset-0 flex items-center justify-center text-2xl font-bold ${isDarkMode ? 'text-cyan-300' : 'text-cyan-600'}`}
                          style={{ animation: 'countdown-pop 0.4s ease-out' }}
                          key={autoPilotCountdownValue}
                        >
                          {autoPilotCountdownValue}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Switching to {autoPilotNextLabel}
                        </div>
                        <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Byte is moving to the next step
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Auto-pilot paused overlay ── */}
              {isAutoPilot && isAutoPilotPaused && !autoPilotCountdownValue && (
                <div className="sticky bottom-4 left-0 right-0 z-30 pointer-events-none">
                  <div className={`mx-auto max-w-md pointer-events-auto rounded-2xl shadow-2xl border backdrop-blur-xl p-4 ${isDarkMode
                    ? 'bg-slate-900/95 border-amber-700/40 shadow-amber-900/20'
                    : 'bg-white/95 border-amber-200 shadow-amber-100/50'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-amber-900/50' : 'bg-amber-100'
                          }`}>
                          <Square className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                        </div>
                        <div>
                          <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Byte is paused
                          </div>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Take your time to explore. Resume when ready.
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleResumeAutoPilot}
                        className="px-4 py-2 text-sm font-semibold rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 shadow-lg shadow-cyan-500/30 transition-all hover:scale-105 flex items-center space-x-1.5"
                      >
                        <Play className="w-3.5 h-3.5" />
                        <span>Resume</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Handback announcement — Byte finished ── */}
              {showHandbackBanner && !isAutoPilot && (
                <div className="sticky bottom-0 left-0 right-0 z-20 pointer-events-none">
                  <div className={`mx-auto max-w-md pointer-events-auto rounded-2xl shadow-2xl border backdrop-blur-xl p-4 mb-2 ${isDarkMode
                    ? 'bg-gradient-to-r from-cyan-900/90 to-blue-900/90 border-cyan-600/40 shadow-cyan-900/30'
                    : 'bg-gradient-to-r from-cyan-50/95 to-blue-50/95 border-cyan-300 shadow-cyan-100/50'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-cyan-500/20' : 'bg-cyan-100'
                          }`}>
                          <CheckCircle className={`w-5 h-5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                        </div>
                        <div>
                          <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Byte is done! You're in control.
                          </div>
                          <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-cyan-300/70' : 'text-cyan-600/70'}`}>
                            Everything is set up — explore and customize as you like.
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowHandbackBanner(false)}
                        className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200/50 text-gray-500'
                          }`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {insufficientCreditsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">&#9981;</span>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Out of Credits
              </h3>
            </div>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              You don't have enough credits to perform this action. Add more credits to continue building.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setInsufficientCreditsDialog(false)}
                className={`px-4 py-2 rounded-md ${isDarkMode ? 'text-gray-300 border border-gray-600 bg-gray-700 hover:bg-gray-600' : 'text-gray-600 border border-gray-300 bg-gray-100 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => { setInsufficientCreditsDialog(false); localStorage.setItem('bytesized_billing_return', `/?view=${currentView}`); navigate('/billing?recharge=true'); }}
                className="px-4 py-2 text-white rounded-md bg-[#0077A8] hover:bg-[#006090]"
              >
                Add Credits
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              {confirmDialog.title}
            </h3>
            <p className={`mb-6 whitespace-pre-line ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {confirmDialog.message}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  if (confirmDialog.onCancel) {
                    confirmDialog.onCancel();
                  } else {
                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null, confirmText: 'Confirm', cancelText: 'Cancel' });
                  }
                }}
                className={`px-4 py-2 rounded-md ${confirmDialog.onCancel
                  ? 'bg-[#0077A8] hover:bg-[#006090] text-white'
                  : isDarkMode ? 'text-gray-300 border border-gray-600 bg-gray-700 hover:bg-gray-600' : 'text-gray-600 border border-gray-300 bg-gray-100 hover:bg-gray-200'
                  }`}
              >
                {confirmDialog.cancelText || 'Cancel'}
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className={`px-4 py-2 text-white rounded-md ${confirmDialog.confirmText === 'Delete' || confirmDialog.confirmText === 'Remove'
                  ? 'bg-red-600 hover:bg-red-700'
                  : confirmDialog.onCancel
                    ? isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-500 hover:bg-gray-600'
                    : 'bg-[#0077A8] hover:bg-[#006090]'
                  }`}
              >
                {confirmDialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Going Back to Questionnaire */}
      {showBackConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={cancelGoBackToQuestionnaire}
          />
          {/* Dialog */}
          <div className={`relative z-10 w-full max-w-md mx-4 rounded-xl shadow-2xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
            {/* Header */}
            <div
              className="px-6 py-4 rounded-t-xl"
              style={{
                background: isDarkMode
                  ? 'rgba(0, 119, 168, 0.2)'
                  : 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)'
              }}
            >
              <h3 className={`text-lg font-semibold flex items-center ${isDarkMode ? 'text-[#0099CC]' : 'text-white'}`}>
                <AlertTriangle className={`w-5 h-5 mr-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-200'}`} />
                Edit Questionnaire?
              </h3>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Going back will allow you to review and edit your answers.
              </p>
              <p className={`text-sm leading-relaxed mt-3 ${isDarkMode ? 'text-yellow-400' : 'text-amber-600'}`}>
                <strong>Note:</strong> If you modify any answers and resubmit, your current AI agent design will be regenerated from scratch.
              </p>
            </div>

            {/* Footer */}
            <div className={`px-6 py-4 flex justify-end space-x-3 rounded-b-xl ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
              <button
                onClick={cancelGoBackToQuestionnaire}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmGoBackToQuestionnaire}
                disabled={isLoadingSession}
                className="px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors bg-[#0077A8] hover:bg-[#006090] disabled:opacity-50"
              >
                {isLoadingSession ? 'Loading...' : 'Continue to Questionnaire'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextGeneratorUI;